import { FormEvent, MouseEvent, ReactNode, SVGProps, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { QRCodeSVG } from "qrcode.react";
import ShareArrowIcon from "../vendor/fanxing-universe/ShareArrowIcon";
import { StatusDrawerIcon } from "../vendor/fanxing-os/src/fanxingOSUI/lightModeUI/icons/StatusDrawerIcon";
import FanxingActionConfirmDialog from "../vendor/fanxing-os/src/fanxingOSUI/sharedUI/actionConfirmDialog/FanxingActionConfirmDialog";
import PublicActivityCalendar from "./PublicActivityCalendar";
import HeroTagline from "./HeroTagline";
import { TeacherDemonstrations, workflowStatusEvent, workflowToggleEvent, type DemonstrationWorkflowStatus } from "./TeacherDemonstrations";
import AnnualReportShare, { type AnnualReport } from "./AnnualReportShare";
import "./styles.css";
import "./TeacherLessonDetails.css";
import projectResultsCatalog from "./projectResults";
import portalVersions from "./data/portal-versions.generated.json";
import { cachedCourse, clearCourseCache, getCourseCacheInfo } from "./courseCache";
import { clearTrainingCache, getTrainingCacheInfo, pickTrainingCacheDirectory, restoreTrainingCacheDirectory } from "./trainingCache";
import VisitorChatWidget from "./VisitorChatWidget";

type Role = "admissions" | "teacher" | "student";
type IntegrationState = "ready_verified" | "configured_unverified" | "demo_only" | "not_configured" | "blocked";
type Integration = { status: IntegrationState; label: string; note: string };
type StatusPayload = { mode: string; release: string; integrations: Record<string, Integration> };
type CourseDay = {
  day: number;
  code: string;
  week: number;
  productionHours: number;
  operationsHours: number;
  title: string;
  deliverable: string;
  status: "expanded" | "outline";
  objective: string;
  prerequisite?: string;
  milestone?: string;
  sessions?: [string, string][];
  hourSpecs?: TeacherHourSpec[];
  acceptance: string;
  homework: string;
};
type TeacherHourSpec = {
  goal: string; content: string; result: string; check: string;
  demo: string; mistake: string; durationMinutes: number;
  agenda: { minutes: number; label: string; detail: string }[];
  resource: { title: string; fields: string[]; example: string; kind: "inline-template"; steps?: string[]; wrongExample?: string };
  references: { title: string; url: string }[];
};
type CoursePayload = {
  version: string;
  totals: { days: number; productionHours: number; operationsHours: number; hours: number };
  days: CourseDay[];
  caseId?: TeachingCaseId;
  weeks?: { week: number; title: string; summary: string }[];
  teachingCase?: TeachingCaseSummary & {
    promise: string; ending: string; status: string;
    characters: [string, string][]; locations: string[]; props: string[];
    episodes: { id: string; title: string; action: string; hook: string; plannedMinutes: number }[];
  };
};
type TeachingCaseId = "a" | "b";
type TeachingCaseSummary = { id: TeachingCaseId; label: string; title: string; logline: string; format: string };
type PublicCourseDay = Pick<CourseDay, "day" | "code" | "week" | "title" | "productionHours" | "operationsHours"> & { summary: string };
type PublicCoursePayload = Omit<CoursePayload, "days"> & { days: PublicCourseDay[] };
type Lead = {
  id: string;
  name: string;
  contactType: string;
  maskedContact: string;
  schedule: string;
  convenientTime: string;
  question: string;
  source: string;
  contactStatus: string;
  enrollmentStatus: string;
  fundStatus: string;
  interactionCount: number;
  demo: boolean;
  createdAt: string;
};
type AdmissionsData = {
  user: PublicProfile;
  leads: Lead[];
  metrics: { leads: number; pending: number; enrolled: number; paidFen: number; refundedFen: number };
};
type CourseMemberRole = "teacher" | "student";
type CourseMember = { user: PublicProfile; roles: CourseMemberRole[]; superAdmin: boolean; cohortNo: number | null; updatedAt: string };
type MemberFilter = "all" | "superAdmin" | CourseMemberRole | "cohorts" | "unassigned" | `cohort:${number}`;
type Assignment = { id: string; lesson_code: string; title: string; requirements: string; created_at: string };
type Review = { id: string; decision: string; timecode: string; comment: string; createdAt: string };
type Submission = {
  id: string;
  assignmentId: string;
  assignmentTitle: string;
  lessonCode: string;
  version: number;
  note: string;
  attachmentLabel: string;
  status: string;
  createdAt: string;
  review: Review | null;
};
type Cost = { id: string; category: string; tool: string; amount_fen: number | null; currency: string; note: string; created_at: string };
type CreatorCategory = "project" | "activity" | "timeline" | "works" | "highlights" | "reviews" | "capabilities";
type CreatorVisibility = Record<CreatorCategory, "private" | "teacher" | "public">;
type StudentDashboard = { user: PublicProfile; project: { title: string; stage: string; targetDurationMs: number; acceptedDurationMs: number }; assignments: Assignment[]; submissions: Submission[]; costs: Cost[]; knownCostFen: number; creatorVisibility: CreatorVisibility };
type PublicCreatorData = {
  user: PublicProfile;
  visibility: CreatorVisibility;
  project: { title: string; stage: string; targetDurationMs: number; acceptedDurationMs: number } | null;
  activity: { date: string; count: number }[];
  timeline: { date: string; lessonCode: string; title: string; version: number; status: "approved" }[];
  summary: { submissions: number | null; completedMilestones: number | null; acceptedDurationMinutes: number | null };
  capabilities: {
    script: { approvedTasks: number; approvedVersions: number; participatingEpisodes: number | null };
    storyboard: { acceptedShots: number | null };
    image: { adoptedAssets: number | null };
    video: { acceptedShots: number | null };
    editing: { acceptedDurationMinutes: number | null };
    project: { submissionVersions: number; approvedTasks: number; completedProjects: number | null; activeProjects: number | null };
  } | null;
  annualReport: AnnualReport | null;
};
type PortalView = "overview" | "outline" | "today" | "course" | "tasks" | "pending" | "feedback" | "projects" | "files" | "costs" | "materials" | "scripts" | "dramas" | "members" | "cache";
type TeachingCohortId = "morning" | "afternoon" | "evening";
type PortalNavGroup = { label: string; items: { id: PortalView; label: string }[] };
type PublicProfile = {
  id: string;
  username: string;
  displayName: string;
  displayId: string;
  avatarUrl: string;
  bio: string;
  profileVersion: string;
};
type ProjectResult = {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  durationMinutes: number;
  coverUrl: string;
};
type QrStatus = "loading" | "pending" | "expired" | "error";
type QrChallenge = {
  challengeId: string;
  pollToken: string;
  qrPayload: string;
  expiresAt: string;
  pollIntervalMs: number;
};
type LoginUser = { id: string; displayName?: string; username?: string; displayId?: string; avatarUrl?: string };
type LoginSession = { accessToken: string; refreshToken?: string; refreshTokenExpiresAt?: string; user: LoginUser; roles: Role[]; availablePortals: Role[]; primaryRole: Role };
type SystemMetrics = { cpu: number; ram: number; gpu: number; vram: number; temp: number; sampledAt?: string };

const roleNames: Record<Role, string> = { admissions: "管理端", teacher: "教师端", student: "学生端" };
const developmentUserId = "696e29a9-8831-47b6-b116-98e46caf9171";
const teacherPath = `/teacher/${developmentUserId}`;
const studentPath = `/student/${developmentUserId}`;
const publicStudentPath = `/u/${developmentUserId}`;
const portalViewSlugs: Record<"teacher" | "student", Partial<Record<PortalView, string>>> = {
  teacher: { overview: "", outline: "outline", course: "plan", today: "today", tasks: "tasks", pending: "pending", feedback: "feedback", scripts: "scripts", dramas: "dramas", projects: "projects", files: "files", costs: "costs", cache: "cache" },
  student: { overview: "", scripts: "scripts", dramas: "dramas", today: "today", tasks: "tasks", feedback: "feedback", files: "files", costs: "costs", materials: "docs", cache: "cache" },
};

function portalViewPath(role: "teacher" | "student", userId: string, view: PortalView) {
  const slug = portalViewSlugs[role][view];
  if (slug === undefined) throw new Error(`Unsupported ${role} view: ${view}`);
  return `/${role}/${userId}${slug ? `/${slug}` : ""}`;
}

function portalViewFromSlug(role: "teacher" | "student", slug: string | undefined): PortalView | undefined {
  return (Object.entries(portalViewSlugs[role]).find(([, value]) => value === (slug || ""))?.[0]) as PortalView | undefined;
}

function navigatePortalView(role: "teacher" | "student", userId: string, view: PortalView) {
  const href = portalViewPath(role, userId, view);
  if (window.location.pathname === href) return;
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateTeacherToday(userId: string, cohort: TeachingCohortId, session: number) {
  const href = `${portalViewPath("teacher", userId, "today")}/${cohort}/${session}`;
  if (window.location.pathname === href) return;
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateTeacherPlan(userId: string, cohort: TeachingCohortId, week?: number, day?: number) {
  const href = `${portalViewPath("teacher", userId, "course")}/${cohort}${week ? `/w${week}` : ""}${day ? `/d${String(day).padStart(2, "0")}` : ""}`;
  if (window.location.pathname === href) return;
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateTeacherOutline(userId: string, caseId: TeachingCaseId, week: number, day?: number, hour?: number) {
  const href = `${portalViewPath("teacher", userId, "outline")}/${caseId}/w${week}${day ? `/d${String(day).padStart(2, "0")}` : ""}${hour ? `/h${hour}` : ""}`;
  if (window.location.pathname === href) return;
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
const statusNames: Record<IntegrationState, string> = {
  ready_verified: "已验证",
  configured_unverified: "配置未验证",
  demo_only: "演示适配",
  not_configured: "未配置",
  blocked: "阻塞",
};
const contactStatusNames: Record<string, string> = {
  pending: "待联系",
  contacting: "沟通中",
  waiting: "等待回复",
  callback: "约定回访",
  paused: "暂缓",
  closed: "不适合 / 明确不报",
};

class ApiRequestError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

let refreshSessionRequest: Promise<LoginSession | null> | null = null;

async function refreshStoredLoginSession(): Promise<LoginSession | null> {
  if (refreshSessionRequest) return refreshSessionRequest;
  const session = readLoginSession();
  if (!session?.refreshToken) return null;
  refreshSessionRequest = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) return null;
      const refreshed = normalizeLoginSession({ ...session, ...payload });
      if (!refreshed || readLoginSession()?.user.id !== session.user.id) return null;
      persistLoginSession(refreshed);
      return refreshed;
    } catch {
      return null;
    } finally {
      refreshSessionRequest = null;
    }
  })();
  return refreshSessionRequest;
}

async function authenticatedFetch(path: string, options: RequestInit = {}, retryAfterRefresh = true): Promise<Response> {
  const headers = new Headers(options.headers || {});
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const session = readLoginSession();
  if (session?.accessToken && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${session.accessToken}`);
  const response = await fetch(path, {
    ...options,
    headers,
  });
  if (session?.user.id && readLoginSession()?.user.id !== session.user.id) throw new DOMException("账号已变更，请重新打开页面。", "AbortError");
  if (response.status === 401 && retryAfterRefresh && path !== "/api/auth/refresh") {
    const refreshed = await refreshStoredLoginSession();
    if (refreshed) return authenticatedFetch(path, options, false);
  }
  return response;
}

async function api<T>(path: string, options: RequestInit = {}, retryAfterRefresh = true): Promise<T> {
  const accountId = readLoginSession()?.user.id;
  const response = await authenticatedFetch(path, options, retryAfterRefresh);
  const payload = await response.json().catch(() => ({}));
  if (response.status === 403 && path === "/api/auth/session" && accountId) {
    await clearCourseCache(accountId).catch(() => undefined);
    await clearTrainingCache(accountId, "teacher").catch(() => undefined);
  }
  if (!response.ok) throw new ApiRequestError(response.status, payload.code || `HTTP_${response.status}`, payload.message || payload.code || `HTTP_${response.status}`);
  return payload as T;
}

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    // A child access gate can redirect before this parent effect subscribes.
    update();
    return () => window.removeEventListener("popstate", update);
  }, []);
  return pathname;
}

function Link({ href, className = "", children, onClick, ariaCurrent }: { href: string; className?: string; children: ReactNode; onClick?: () => void; ariaCurrent?: "page" }) {
  return <a className={className} href={href} aria-current={ariaCurrent} onClick={(event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
    const hash = new URL(href, window.location.href).hash;
    if (hash) window.requestAnimationFrame(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }));
    else window.scrollTo({ top: 0, behavior: "smooth" });
    onClick?.();
  }}>{children}</a>;
}

function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function money(fen: number | null, currency = "CNY") {
  if (fen === null) return "费用待核";
  return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(fen / 100);
}

function BrandLockup() {
  return <span className="brand-lockup">
    <img className="brand-symbol" src="/fanxing.svg" alt="" aria-hidden="true" />
    <strong>繁星短剧训练营</strong>
  </span>;
}

const loginSessionKey = "duanju-school:auth:v1";
const loginDeviceKey = "duanju-school:qr-device-id:v1";
let verifiedLoginSession: LoginSession | null = null;
let pendingProtectedPath: string | null = null;

function createLoginDeviceId() {
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof window.crypto?.getRandomValues === "function") window.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function readLoginSession(): LoginSession | null {
  try {
    return normalizeLoginSession(JSON.parse(window.localStorage.getItem(loginSessionKey) || "null"));
  } catch {
    return null;
  }
}

function normalizeLoginSession(value: unknown): LoginSession | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<LoginSession>;
  const roles = Array.isArray(candidate.roles) ? candidate.roles.filter((role): role is Role => role === "admissions" || role === "teacher" || role === "student") : [];
  const availablePortals = Array.isArray(candidate.availablePortals) ? candidate.availablePortals.filter((role): role is Role => role === "admissions" || role === "teacher" || role === "student") : roles;
  const primaryRole = candidate.primaryRole && roles.includes(candidate.primaryRole) ? candidate.primaryRole : roles[0];
  if (!candidate.accessToken || !candidate.user?.id || !primaryRole || roles.length === 0) return null;
  return { ...candidate, accessToken: candidate.accessToken, user: candidate.user, roles, availablePortals, primaryRole };
}

function persistLoginSession(session: LoginSession) {
  const previousAccount = readLoginSession()?.user.id;
  if (previousAccount !== session.user.id) void clearCourseCache(previousAccount).catch(() => undefined);
  verifiedLoginSession = session;
  window.localStorage.setItem(loginSessionKey, JSON.stringify(session));
}

function clearLoginSession() {
  const previousAccount = readLoginSession()?.user.id;
  verifiedLoginSession = null;
  void clearCourseCache(previousAccount).catch(() => undefined);
  window.localStorage.removeItem(loginSessionKey);
}

window.addEventListener("storage", (event) => {
  if (event.key !== loginSessionKey && event.key !== null) return;
  let transition: LoginSession | null = null;
  try { transition = normalizeLoginSession(JSON.parse(event.newValue || "null")); } catch { /* reload below */ }
  const current = readLoginSession();
  if (!transition || verifiedLoginSession?.user.id !== transition.user.id || verifiedLoginSession?.user.id !== current?.user.id
    || JSON.stringify(verifiedLoginSession?.availablePortals) !== JSON.stringify(transition.availablePortals)) window.location.reload();
});

function sessionCanEnter(session: LoginSession | null, role: Role, userId?: string) {
  return Boolean(session?.availablePortals.includes(role) && (!userId || session.user.id.toLowerCase() === userId.toLowerCase()));
}

function loginPortal(session: LoginSession) {
  const role = session.primaryRole;
  const href = role === "admissions" ? "/admissions" : portalViewPath(role, session.user.id, "overview");
  return { label: roleNames[role], href };
}

function readLoginDeviceId() {
  try {
    const existing = window.localStorage.getItem(loginDeviceKey)?.trim() || "";
    if (/^[0-9a-f-]{36}$/i.test(existing)) return existing;
    const created = createLoginDeviceId();
    window.localStorage.setItem(loginDeviceKey, created);
    return created;
  } catch {
    return createLoginDeviceId();
  }
}

function XingxunLoginDialog({ onClose, onAuthenticated }: { onClose: () => void; onAuthenticated: (session: LoginSession) => void }) {
  const [challenge, setChallenge] = useState<QrChallenge | null>(null);
  const [status, setStatus] = useState<QrStatus>("loading");
  const [error, setError] = useState("");

  const createChallenge = useCallback(async () => {
    setChallenge(null);
    setStatus("loading");
    setError("");
    try {
      const payload = await api<{ ok: true } & QrChallenge>("/api/auth/qr/challenges", {
        method: "POST",
        body: JSON.stringify({ deviceId: readLoginDeviceId() }),
      });
      setChallenge(payload);
      setStatus("pending");
    } catch {
      setStatus("error");
      setError("二维码获取失败，请检查网络后重试");
    }
  }, []);

  useEffect(() => { void createChallenge(); }, [createChallenge]);
  useEffect(() => {
    document.body.classList.add("overlay-open");
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("overlay-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);
  useEffect(() => {
    if (!challenge || status !== "pending") return;
    let disposed = false;
    let timer = 0;
    const poll = async () => {
      try {
        const result = await api<{ ok: true; status: string; accessToken?: string; refreshToken?: string; refreshTokenExpiresAt?: string; user?: LoginUser; roles?: Role[]; primaryRole?: Role }>("/api/auth/qr/poll", {
          method: "POST",
          body: JSON.stringify({ challengeId: challenge.challengeId, pollToken: challenge.pollToken }),
        });
        if (disposed) return;
        if (result.status === "authenticated") {
          const session = normalizeLoginSession(result);
          if (!session) throw new Error("登录响应缺少课程权限");
          persistLoginSession(session);
          onAuthenticated(session);
          return;
        }
        if (result.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (pollError) {
        if (disposed) return;
        const message = pollError instanceof Error ? pollError.message : "";
        if (message.includes("410") || message.includes("EXPIRED")) {
          setStatus("expired");
          return;
        }
        if ((pollError instanceof ApiRequestError && pollError.status === 403) || message.includes("NOT_ALLOWED") || message.includes("ACCESS_REQUIRED")) {
          setStatus("error");
          setError("该星讯账号尚未获得短剧训练营权限");
          return;
        }
      }
      if (!disposed && Date.parse(challenge.expiresAt) <= Date.now()) {
        setStatus("expired");
        return;
      }
      if (!disposed) timer = window.setTimeout(poll, challenge.pollIntervalMs);
    };
    timer = window.setTimeout(poll, Math.min(challenge.pollIntervalMs, 150));
    return () => { disposed = true; window.clearTimeout(timer); };
  }, [challenge, onAuthenticated, status]);

  return <div className="login-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="fanxing-admin-qr-login" role="dialog" aria-modal="true" aria-labelledby="duanju-qr-login-title">
      <button className="login-modal-close" type="button" onClick={onClose} aria-label="关闭登录窗口">
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
      </button>
      <div className="fanxing-admin-qr-login__layout">
        <div className="fanxing-admin-qr-login__copy">
          <div className="fanxing-admin-qr-login__heading">
            <span className="fanxing-admin-qr-login__eyebrow">繁星短剧训练营</span>
            <h1 id="duanju-qr-login-title">登录工作台</h1>
            <p>使用手机星讯扫描右侧二维码</p>
          </div>
          <ol className="fanxing-admin-qr-login__steps" aria-label="扫码登录步骤">
            <li><span>1</span><div><strong>打开手机星讯</strong><small>确保手机端已登录</small></div></li>
            <li><span>2</span><div><strong>扫描右侧二维码</strong><small>使用星讯内“扫一扫”</small></div></li>
            <li><span>3</span><div><strong>在手机端确认</strong><small>通过验证后自动进入</small></div></li>
          </ol>
        </div>
        <div className="fanxing-admin-qr-login__qr-column">
          <div className={`fanxing-admin-qr-login__code${status === "pending" ? "" : " is-muted"}`}>
            {challenge ? <QRCodeSVG value={challenge.qrPayload} size={152} level="M" marginSize={1} bgColor="transparent" fgColor="#111111" /> : <span className="fanxing-admin-qr-login__spinner" aria-label="正在获取二维码" />}
            {status === "expired" ? <span className="fanxing-admin-qr-login__overlay">二维码已过期</span> : null}
          </div>
          <div className={`fanxing-admin-qr-login__status is-${status}`} aria-live="polite"><span aria-hidden />{status === "pending" ? "等待手机确认" : status === "loading" ? "正在生成二维码" : "请刷新二维码"}</div>
          {status === "error" ? <span className="fanxing-admin-qr-login__error">{error}</span> : null}
          {status === "expired" || status === "error" ? <button type="button" onClick={() => void createChallenge()}>刷新二维码</button> : null}
        </div>
        <span className="fanxing-admin-qr-login__secure"><i aria-hidden /> 手机确认 · 安全登录</span>
      </div>
    </section>
  </div>;
}

function Header({ internal = false, promotion = true, loginSession = null, onLogin }: { internal?: boolean; promotion?: boolean; loginSession?: LoginSession | null; onLogin?: () => void }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const portal = loginSession ? loginPortal(loginSession) : null;
  const navState = (href: string) => {
    const active = href === "/" ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);
    return { className: active ? "is-active" : "", ariaCurrent: active ? "page" as const : undefined };
  };
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 24);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (!menuRef.current?.contains(event.target) && !navRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setOpen(false); menuRef.current?.focus(); }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);
  return <header className={`${scrolled ? "site-header is-scrolled" : "site-header"}${promotion ? "" : " no-promotion"}`}>
    <div className="header-inner">
      <Link href="/" className="brand" onClick={() => setOpen(false)}>
        <BrandLockup />
      </Link>
      <div className="header-actions">
        <nav ref={navRef} className={open ? "main-nav is-open" : "main-nav"} aria-label="主导航">
          {internal ? <>
            <Link href="/admissions" {...navState("/admissions")} onClick={() => setOpen(false)}>招生</Link>
            <Link href={teacherPath} {...navState("/teacher")} onClick={() => setOpen(false)}>教师</Link>
            <Link href={studentPath} {...navState("/student")} onClick={() => setOpen(false)}>学生</Link>
            <Link href={publicStudentPath} {...navState("/u")} onClick={() => setOpen(false)}>公开空间</Link>
          </> : <>
            <Link href="/" {...navState("/")} onClick={() => setOpen(false)}>首页</Link>
            <Link href="/plan" {...navState("/plan")} onClick={() => setOpen(false)}>课程计划</Link>
            <Link href="/teach" {...navState("/teach")} onClick={() => setOpen(false)}>教学方式</Link>
            <Link href="/work" {...navState("/work")} onClick={() => setOpen(false)}>项目成果</Link>
            <Link href="/me" {...navState("/me")} onClick={() => setOpen(false)}>关于老师</Link>
            <Link href="/faq" {...navState("/faq")} onClick={() => setOpen(false)}>常见问题</Link>
            <Link href="/ask" className="header-mobile-consult" onClick={() => setOpen(false)}>咨询老师</Link>
          </>}
        </nav>
        <div className="header-utility-actions">
          <Link href="/ask" className="header-contact" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 9.4a6.5 6.5 0 0 1 13 0v4.1a2 2 0 0 1-2 2H13v-5h3.5M3.5 10.5H7v5H5.5a2 2 0 0 1-2-2v-3Z" /><path d="M13 15.5c-.7 1-1.7 1.5-3 1.5" /></svg>
            <span>咨询老师</span>
          </Link>
          {portal ? <Link href={portal.href} className="header-login" onClick={() => setOpen(false)}>{portal.label}</Link> : onLogin ? <button className="header-login" type="button" onClick={() => { setOpen(false); onLogin(); }}>登录工作台</button> : null}
        </div>
        <button ref={menuRef} className="menu-button" type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "关闭菜单" : "打开菜单"}><span /><span /><span /></button>
      </div>
    </div>
  </header>;
}

const founderPriceDeadline = new Date("2026-11-01T00:00:00+08:00").getTime();

function founderCountdown() {
  const remaining = Math.max(0, founderPriceDeadline - Date.now());
  const seconds = Math.floor(remaining / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const restSeconds = seconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${days}天 ${pad(hours)}:${pad(minutes)}:${pad(restSeconds)}`;
}

function DemoBanner() {
  const [countdown, setCountdown] = useState(founderCountdown);
  useEffect(() => {
    const timer = window.setInterval(() => setCountdown(founderCountdown()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return <div className="demo-banner" role="status">
    <span className="demo-banner-message"><span className="banner-copy-desktop">首期创始班 <b>¥3,980</b> · 11月1日起恢复 ¥4,999</span><span className="banner-copy-mobile">创始班 <b>¥3,980</b> · 11月起 ¥4,999</span></span>
    <strong className="demo-banner-countdown" aria-label={`创始价结束倒计时 ${countdown}`}><span className="banner-countdown-label">创始价结束</span><time dateTime="2026-11-01T00:00:00+08:00">{countdown}</time></strong>
  </div>;
}

function Footer({ onLogin, loginSession }: { onLogin: () => void; loginSession: LoginSession | null }) {
  const portal = loginSession ? loginPortal(loginSession) : null;
  return <footer className="footer">
    <div className="footer-inner">
      <div className="footer-main">
        <div className="footer-brand">
          <Link href="/" className="footer-logo"><BrandLockup /></Link>
          <p className="footer-tagline">一人一剧，用自己的项目完成学习闭环。</p>
          <div className="footer-locale"><span>/ 访问范围</span><strong>内网</strong></div>
        </div>
        <nav className="footer-nav" aria-label="页尾导航">
          <div className="footer-nav-col"><span>/ 课程</span><Link href="/plan">40 天课程路线</Link><Link href="/">训练营首页</Link></div>
          <div className="footer-nav-col"><span>/ 咨询</span><Link href="/ask">直接问老师</Link><Link href="/ask">留下联系方式</Link></div>
          <div className="footer-nav-col"><span>/ 空间</span>{portal ? <Link href={portal.href} className="footer-login">{portal.label}</Link> : <button className="footer-login" type="button" onClick={onLogin}>登录</button>}</div>
        </nav>
      </div>
      <div className="footer-bottom"><span>© 2026 繁星短剧训练营</span><span>DUANJU.SCHOOL · 内网 P1</span><span>未开放公开招生与付款</span></div>
    </div>
  </footer>;
}

function Shell({ children, internal = false, promotion = true }: { children: ReactNode; internal?: boolean; promotion?: boolean }) {
  const [loginOpen, setLoginOpen] = useState(() => Boolean(pendingProtectedPath));
  const [loginSession, setLoginSession] = useState<LoginSession | null>(() => readLoginSession());
  useEffect(() => {
    delete document.documentElement.dataset.internalBoot;
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeColor) themeColor.content = "#f5f0e6";
  }, []);
  useEffect(() => {
    if (!loginSession) return;
    const controller = new AbortController();
    api<LoginSession>("/api/auth/session", { signal: controller.signal }).then((payload) => {
      const verified = normalizeLoginSession({ ...loginSession, ...payload });
      if (!controller.signal.aborted && verified) {
        persistLoginSession(verified);
        setLoginSession(verified);
      }
    }).catch((error) => {
      if (!controller.signal.aborted && error instanceof ApiRequestError && error.status === 403) {
        clearLoginSession();
        setLoginSession(null);
      }
    });
    return () => controller.abort();
  }, []);
  const onAuthenticated = (session: LoginSession) => {
    setLoginSession(session);
    setLoginOpen(false);
    const requested = pendingProtectedPath;
    pendingProtectedPath = null;
    const scoped = requested?.match(/^\/(teacher|student)\/([0-9a-f-]{36})(?:\/|$)/i);
    const returnPath = scoped && sessionCanEnter(session, scoped[1] as Role, scoped[2])
      ? requested
      : requested?.startsWith("/admissions") && sessionCanEnter(session, "admissions")
        ? requested : loginPortal(session).href;
    window.history.pushState({}, "", returnPath);
    window.dispatchEvent(new PopStateEvent("popstate"));
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  return <div className={promotion ? "site-shell" : "site-shell no-promotion"}>{promotion ? <DemoBanner /> : null}<Header internal={internal} promotion={promotion} loginSession={loginSession} onLogin={() => { if (!loginSession) setLoginOpen(true); }} /><main>{children}</main><Footer loginSession={loginSession} onLogin={() => { if (!loginSession) setLoginOpen(true); }} />{!internal ? <VisitorChatWidget hidden={loginOpen} /> : null}{loginOpen ? <XingxunLoginDialog onClose={() => { pendingProtectedPath = null; setLoginOpen(false); }} onAuthenticated={onAuthenticated} /> : null}</div>;
}

const showcaseAssets = [...Array.from({ length: 18 }, (_, index) => ({
  src: `/showcase/orbit-${String(index + 1).padStart(2, "0")}.webp?v=20260920-hq`,
  label: `短剧项目 ${String(index + 1).padStart(2, "0")}`,
})), ...projectResultsCatalog.projects.slice(0, 6).map(project => ({ src: project.coverUrl, label: project.title }))];

const trainingStages = [
  ["01", "立项与总纲", "确定题材、受众、人物与冲突，把 100 分钟分集结构变成可执行方案。", "项目定位", "分集结构", "PROJECT BRIEF"],
  ["02", "剧本与分镜", "推进剧本、影视语言、分镜与镜头表，并完成生产批次和成本分级。", "剧本拆解", "镜头规划", "SCRIPT & SHOTS"],
  ["03", "视觉资产与样片制作", "建立人物、场景与道具资产，完成图像、视频、声音和样片粗剪。", "资产一致性", "样片制作", "ASSET & SAMPLE"],
  ["04", "样片关卡与首批正片", "用 2—4 分钟样片验证质量、耗时与成本，返修后推进首批 25 分钟。", "样片审阅", "25分钟", "QUALITY GATE"],
  ["05", "批量制作与发行验证", "沿 25、50、75、100 分钟里程碑持续生产，同步包装、提交与真实反馈。", "批量生产", "发行验证", "PRODUCTION & RELEASE"],
  ["06", "完整交付与项目复盘", "完成最终返修、项目交付包、成本台账、作品整理、渠道状态与数据复盘。", "项目交付", "成本复盘", "DELIVERY & REVIEW"],
] as const;

const productionMilestones = ["立项", "剧本", "人物 / 场景资产", "分镜与镜头表", "样片", "25分钟", "50分钟", "75分钟", "100分钟", "发行运营"];

const courseWeeks = [
  ["WEEK 01", "100分钟项目立项", "题材、目标受众、故事核心、人物、冲突、分集结构。"],
  ["WEEK 02", "剧本与影视语言", "剧本、短剧节奏、景别、构图、轴线、镜头语言、分镜。"],
  ["WEEK 03", "人物场景与视觉资产", "人物一致性、场景、服装、道具、视觉风格、资产库。"],
  ["WEEK 04", "样片与生产线", "图片、视频、声音、剪辑，跑通首集完整流程。"],
  ["WEEK 05", "批量制作", "进入25分钟、50分钟里程碑。"],
  ["WEEK 06", "制作 + 首轮发行", "继续成片，同时开始包装和实际发布。"],
  ["WEEK 07", "75—100分钟 + 数据优化", "持续生产、审片返修、分析真实反馈。"],
  ["WEEK 08", "完整成片 + 发行复盘", "完成项目、发行、数据、作品集和最终复盘。"],
] as const;

const projectAssets = ["完整剧本", "分集大纲", "人物与场景资产", "分镜 / 镜头表", "图像与视频素材", "声音资产", "剪辑工程", "海报 / 封面 / 预告", "发行记录", "运营数据", "成本台账", "完整项目复盘"];

const homepageFaqs = [
  ["零基础能学吗？", "可以。课程从项目立项和基础影视语言开始，但需要持续投入课后制作时间。"],
  ["一定能完成100分钟吗？", "100分钟是完整课程围绕的项目目标，不是“付款后繁星代为交付100分钟”。最终完成情况与个人出勤、投入、项目难度和执行有关。"],
  ["每天除上课外还需要投入多少时间？", "会因题材、熟练度和返修量不同而变化。报名沟通时会结合个人时间给出制作安排建议。"],
  ["电脑需要什么配置？", "开班前提供配置清单与替代路线；不同生产方案对本地设备要求不同。"],
  ["AI工具大概要花多少钱？", "当前仍在实测100分钟低成本制作预算，开班前会提供推荐工具、费用区间与低成本路线。"],
  ["缺课怎么办？", "课程以个人项目进度为核心，具体补课、资料和里程碑调整规则将在开班方案中说明。"],
  ["作品版权归谁？", "作品归属与素材、模型服务条款相关；开班前会明确项目资料、公开展示与第三方工具的边界。"],
  ["学完以后能赚钱吗 / 能接单吗？", "课程帮助你完成作品与生产流程，不承诺收入、接单结果或平台流量。"],
] as const;

const faqGroups = [
  {
    id: "faq-starting",
    index: "01",
    title: "入学与基础",
    summary: "零基础、学习准备与电脑配置。",
    items: [homepageFaqs[0], homepageFaqs[3]],
  },
  {
    id: "faq-schedule",
    index: "02",
    title: "课程与进度",
    summary: "每日投入、缺课与项目推进节奏。",
    items: [homepageFaqs[2], homepageFaqs[5]],
  },
  {
    id: "faq-project",
    index: "03",
    title: "项目与成果",
    summary: "100分钟目标、作品完成与发展边界。",
    items: [homepageFaqs[1], homepageFaqs[7]],
  },
  {
    id: "faq-cost",
    index: "04",
    title: "工具、费用与版权",
    summary: "生成工具、制作成本与作品归属。",
    items: [homepageFaqs[4], homepageFaqs[6]],
  },
] as const;

function smoothStep(start: number, end: number, value: number) {
  const progress = Math.min(1, Math.max(0, (value - start) / (end - start)));
  return progress * progress * (3 - 2 * progress);
}

function StudentWorkbenchPreview() {
  return <div className="workbench-window" role="img" aria-label="繁星短剧训练营学生工作台界面演示">
    <aside className="workbench-sidebar">
      <div className="workbench-brand"><img src="/fanxing.svg" alt="" /><strong>繁星短剧训练营</strong></div>
      <nav aria-label="工作台演示导航">
        <span className="active"><b>＋</b> 开始创作</span>
        <span><b>▥</b> 我的课程</span>
        <span><b>◇</b> 项目资产</span>
        <span><b>□</b> 提交记录</span>
      </nav>
      <div className="workbench-recent"><small>最近项目</small><span className="active"><i className="thumb thumb-night" />雾港回声</span><span><i className="thumb thumb-warm" />重返第七码头</span><span><i className="thumb thumb-future" />明日来信</span></div>
      <div className="workbench-user"><span>周</span><div><strong>周晨</strong><small>学生空间 · 演示</small></div></div>
    </aside>
    <section className="workbench-canvas">
      <div className="canvas-toolbar"><span>选择</span><span>连接</span><span>批注</span><span>审片</span><strong>78%</strong></div>
      <svg className="canvas-lines" viewBox="0 0 720 540" aria-hidden="true"><path d="M118 278 C 192 278, 176 132, 284 132" /><path d="M118 278 C 210 278, 205 385, 328 385" /><path d="M438 132 C 522 132, 502 268, 595 268" /></svg>
      <article className="canvas-node node-script"><small>EP01 · 剧本卡</small><strong>雨夜里的失踪电话</strong><p>人物目标、冲突和结尾钩子已完成第一版。</p><span>v1.3</span></article>
      <article className="canvas-node node-character"><img src="/showcase/night.webp" alt="" /><div><small>角色资产</small><strong>林澈 / 调查记者</strong></div></article>
      <article className="canvas-node node-shot"><img src="/showcase/future.webp" alt="" /><div><small>镜头 08</small><strong>码头追踪 · 夜 / 外</strong></div></article>
      <article className="canvas-node node-audio"><small>对白与环境声</small><div className="waveform">{Array.from({ length: 28 }, (_, index) => <i key={index} style={{ height: `${18 + ((index * 17) % 38)}%` }} />)}</div><span>00:08 / 00:32</span></article>
      <div className="canvas-progress"><span>本周项目进度</span><strong>剧本 → 角色 → 样片</strong><i><b /></i></div>
    </section>
    <aside className="workbench-coach">
      <header><div><small>项目导师</small><strong>雾港回声 · 第 2 周</strong></div><span>演示界面</span></header>
      <div className="coach-message student">我把第一集的雨夜追踪改完了，人物动机还需要补吗？</div>
      <div className="coach-checks"><span>◌ 结构检查</span><span>◇ 角色一致性</span><span>✦ 审片等待</span></div>
      <div className="coach-note"><p>先保留记者主动追查的选择，再把电话来源提前埋进第 2 场。这样第 8 个镜头的追踪动作会更自然。</p><button type="button">查看批注点 3</button></div>
      <div className="coach-input"><span>输入问题，或上传新版本</span><div><b>＋</b><small>课程资料</small><small>项目资产</small><button type="button" aria-label="发送演示消息">↑</button></div></div>
    </aside>
  </div>;
}

function HeroExperience() {
  const rootRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const orbitItems = Array.from(root.querySelectorAll<HTMLElement>(".orbit-card"));
    const experienceFrame = root.querySelector<HTMLElement>(".experience-frame");
    const intro = root.querySelector<HTMLElement>(".experience-intro");
    let backScale = .64;
    let backRadiusY = 285;
    const measureOrbit = () => {
      if (!experienceFrame || !intro || window.innerWidth <= 780) return;
      const frameTop = experienceFrame.getBoundingClientRect().top;
      const height = experienceFrame.clientHeight;
      const introTop = frameTop + height * .42 - intro.offsetHeight / 2;
      const headerBottom = document.querySelector('.site-header')?.getBoundingClientRect().bottom ?? 108;
      const cardHeight = Math.max(...orbitItems.map(item => item.offsetHeight));
      // Fit the distant arc between navigation and copy on shorter desktops.
      backScale = Math.min(.64, Math.max(.4, (introTop - headerBottom - 48) / cardHeight));
      const centerY = frameTop + height / 2 - Math.min(56, Math.max(44, innerHeight * .065));
      backRadiusY = Math.max(0, (centerY - (headerBottom + introTop) / 2 - 16) / .89);
    };
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let scrollFrame = 0;
    let orbitFrame = 0;
    let lastOrbitPaint = 0;
    let scrollProgress = 0;
    const update = () => {
      scrollFrame = 0;
      if (window.innerWidth <= 780) { scrollProgress = 0; return; }
      const rect = root.getBoundingClientRect();
      const bannerHeight = document.querySelector('.demo-banner')?.getBoundingClientRect().height ?? 0;
      const viewportHeight = window.innerHeight - bannerHeight;
      const travel = Math.max(1, root.offsetHeight - viewportHeight);
      const progress = Math.min(1, Math.max(0, (bannerHeight - rect.top) / travel));
      scrollProgress = progress;
      const introExit = smoothStep(.04, .24, progress);
      const orbitExit = smoothStep(.18, .28, progress);
      const productEnter = smoothStep(.22, .28, progress);
      const productPhase = Math.min(1, Math.max(0, (progress - .22) / .24));
      const productSettlePhase = Math.min(1, productPhase / .6167);
      const productSettle = 1 - Math.pow(1 - productSettlePhase, 2);
      root.style.setProperty("--intro-opacity", (1 - introExit).toFixed(4));
      root.style.setProperty("--intro-y", `${(-22 * introExit).toFixed(2)}px`);
      root.style.setProperty("--intro-scale", (1 - .055 * introExit).toFixed(4));
      root.style.setProperty("--orbit-opacity", (1 - orbitExit).toFixed(4));
      root.style.setProperty("--product-opacity", productEnter.toFixed(4));
      root.style.setProperty("--product-scale", (1.5 - .5 * productSettle).toFixed(4));
      root.style.setProperty("--product-y", "0px");
      root.style.setProperty("--grid-opacity", (.58 * (1 - smoothStep(.36, .68, progress))).toFixed(4));
    };
    const renderOrbit = (time: number) => {
      orbitFrame = window.requestAnimationFrame(renderOrbit);
      if (!experienceFrame || document.hidden) return;
      const mobile = window.innerWidth <= 780;
      const targetFps = scrollProgress > .015 && scrollProgress < .52 ? 60 : 30;
      if (lastOrbitPaint && time - lastOrbitPaint < 1000 / targetFps - 1) return;
      lastOrbitPaint = time;
      const orbit = root.querySelector<HTMLElement>('.experience-orbit');
      const bounds = mobile && orbit ? orbit.getBoundingClientRect() : root.getBoundingClientRect();
      if (bounds.bottom < -160 || bounds.top > window.innerHeight + 160) return;
      const phase = (reducedMotion.matches ? 0 : 72e-6 * time) + scrollProgress * Math.PI * .76;
      const radiusX = mobile ? .54 * window.innerWidth : Math.min(.56 * window.innerWidth, 1420);
      const radiusY = mobile ? (orbit?.clientHeight ?? 540) * .36 : Math.min(.38 * experienceFrame.clientHeight, 285);
      const activeItems = mobile ? orbitItems.slice(0, 16) : orbitItems;
      activeItems.forEach((item, index) => {
        const angle = phase + index / activeItems.length * Math.PI * 2;
        const depth = (Math.sin(angle) + 1) / 2;
        const frontBoost = .1 * smoothStep(.92, 1, depth);
        const x = Math.cos(angle) * radiusX * (.82 + .18 * depth) * (1.2 + .06 * smoothStep(.72, .96, depth));
        const lift = 16 * Math.pow(1 - depth, 1.7);
        const rawY = Math.sin(angle) * (!mobile && depth < .5 ? backRadiusY : radiusY) * .89 - lift;
        const frontY = .89 * radiusY;
        const frontTrack = smoothStep(.68, .9, depth);
        const y = rawY + (frontY - rawY) * frontTrack + (mobile ? 10 : 52) * frontTrack;
        const distantScale = mobile ? .64 : backScale;
        const scale = distantScale + ((mobile ? 1 : 1.14) - distantScale) * depth + frontBoost;
        const rotation = 5.2 * Math.sin(1.6 * angle + .8 * index) * Math.pow(1 - depth, .72);
        const blur = depth < .5 ? 5.2 * Math.pow(.5 - depth, 1.3) : 0;
        const opacity = depth >= .5 ? 1 : .34 + depth / .5 * .66;
        item.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) translate(-50%, -50%) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
        item.style.filter = blur > .08 ? `blur(${blur.toFixed(2)}px)` : "none";
        item.style.opacity = opacity.toFixed(4);
        item.style.zIndex = String(Math.round(10 + 80 * depth));
      });
    };
    const requestUpdate = () => { if (!scrollFrame) scrollFrame = window.requestAnimationFrame(update); };
    const resize = () => { measureOrbit(); requestUpdate(); };
    const layoutObserver = new ResizeObserver(measureOrbit);
    if (intro) layoutObserver.observe(intro);
    measureOrbit();
    update();
    orbitFrame = window.requestAnimationFrame(renderOrbit);
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", resize);
    return () => {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      if (orbitFrame) window.cancelAnimationFrame(orbitFrame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", resize);
      layoutObserver.disconnect();
    };
  }, []);

  return <section className="hero-experience" id="home" ref={rootRef}>
    <div className="experience-frame">
      <div className="experience-grid" aria-hidden="true" />
      <div className="experience-orbit" aria-hidden="true">
        {showcaseAssets.map(({ src, label }) => <figure className="orbit-card" key={src}><img src={src} alt="" /><figcaption>{label}</figcaption></figure>)}
      </div>
      <div className="experience-intro">
        <h1 className="experience-statement"><span>两个月，完成你的第一部</span><strong><span className="hero-latin">100</span>分钟 <span className="hero-latin">AI</span> 短剧</strong></h1>
        <HeroTagline />
      </div>
      <div className="experience-product"><StudentWorkbenchPreview /></div>
      <div className="scroll-cue" aria-hidden="true"><span>向下滚动进入学生工作台</span><i /></div>
    </div>
  </section>;
}

function FlowVisual({ index }: { index: number }) {
  if (index === 0) return <div className="flow-visual flow-visual-brief"><div className="brief-sheet"><span>PROJECT BRIEF</span><h4>雾港回声</h4><p>一通来自失踪者的电话，把记者带回十年前的码头。</p><div><i>悬疑</i><i>都市</i><i>12 集</i></div></div><img src="/showcase/night.webp" alt="" /></div>;
  if (index === 1) return <div className="flow-visual flow-visual-board"><img src="/showcase/future.webp" alt="" /><div className="shot-strip"><span>01</span><span>02</span><span>03</span></div><div className="timeline"><i /><b /><i /></div><strong>00:26:03</strong></div>;
  if (index === 2) return <div className="flow-visual flow-visual-assets"><div className="asset-frame"><img src="/showcase/romance.webp" alt="" /></div><div className="asset-frame"><img src="/showcase/fantasy.webp" alt="" /></div><div className="asset-note"><small>一致性参考</small><strong>人物 / 服装 / 场景</strong><span>已绑定 18 个镜头</span></div></div>;
  if (index === 3) return <div className="flow-visual flow-visual-review"><img src="/showcase/night.webp" alt="" /><div className="review-marker marker-a">03</div><div className="review-marker marker-b">07</div><div className="review-panel-mini"><span>样片 v03</span><strong>3 条修改意见</strong><i><b /></i></div></div>;
  if (index === 4) return <div className="flow-visual flow-visual-release"><div className="release-poster"><img src="/showcase/future.webp" alt="" /></div><div className="release-data"><span>成片进度</span><strong>75:24</strong><i><b /></i><div><em>25 / 50 / 75</em><em>发行验证</em></div></div></div>;
  return <div className="flow-visual flow-visual-delivery"><div className="delivery-package"><header><span>PROJECT PACKAGE</span><strong>雾港回声 · FINAL</strong></header><ul><li><span>完整成片与分集母版</span><b>✓</b></li><li><span>剧本 / 分镜 / 资产</span><b>✓</b></li><li><span>发行记录与成本台账</span><b>✓</b></li></ul></div><div className="delivery-summary"><span>FINAL REVIEW</span><strong>100 min</strong><small>交付包 · 渠道状态 · 项目复盘</small></div></div>;
}

function TrainingFlow() {
  const trackRef = useRef<HTMLDivElement>(null);
  const move = (direction: number) => trackRef.current?.scrollBy({ left: direction * Math.min(window.innerWidth * .72, 720), behavior: "smooth" });
  return <section className="training-flow" id="teaching-method">
    <header className="training-flow-header"><div><p className="story-index">04 / PRODUCTION WORKFLOW</p><h2>把短剧，真正做出来</h2><p>六个生产阶段对应八周课程，从立项、样片到批量制作、发行验证与最终复盘。</p></div><div className="flow-arrows"><button type="button" onClick={() => move(-1)} aria-label="上一组">‹‹</button><button type="button" onClick={() => move(1)} aria-label="下一组">››</button></div></header>
    <div className="training-flow-track" ref={trackRef}>
      {trainingStages.map(([number, title, copy, tagA, tagB, stageLabel], index) => <article className="flow-card" key={number}>
        <FlowVisual index={index} />
        <div className="flow-card-copy"><div><span>{number} / 06</span><small>/ {stageLabel}</small></div><h3>{title}</h3><p>{copy}</p><footer><span>{tagA}</span><span>{tagB}</span><Link href="/plan">查看课程 <b>→</b></Link></footer></div>
      </article>)}
    </div>
  </section>;
}

function CourseDifference() {
  return <section className="course-difference section-pad" id="course-difference" aria-labelledby="course-difference-title">
    <div className="course-difference-inner">
      <div className="course-difference-copy">
        <p className="difference-kicker">02 / PROJECT-BASED TRAINING</p>
        <h2 id="course-difference-title"><span>不是学几个 AI 工具。</span><strong>是真正做完一部短剧。</strong></h2>
        <p className="difference-belief">所有知识点，都为你的这部<span className="keep-phrase">100分钟短剧</span>服务。</p>
      </div>
      <div className="difference-orbit" aria-label="普通工具培训与 DUANJU.SCHOOL 项目制训练对比">
        <article className="difference-ordinary">
          <header><span>01</span><h3>普通工具培训</h3></header>
          <ul>
            <li>学提示词</li>
            <li>学几个软件</li>
            <li>做几个练习镜头</li>
            <li>课程结束</li>
          </ul>
        </article>
        <article className="difference-duanju">
          <header><span>02</span><h3>DUANJU.SCHOOL</h3></header>
          <ul>
            <li>Day 1 开始自己的短剧</li>
            <li>一人一个完整项目</li>
            <li>每天推进自己的成片</li>
            <li>老师审片与返修</li>
            <li>最终目标约100分钟</li>
            <li>继续进入发行与运营</li>
          </ul>
        </article>
      </div>
    </div>
  </section>;
}

function LandingStory() {
  return <div className="landing-story">
    <section className="story-chapter production-roadmap" id="story-production" aria-labelledby="production-roadmap-title">
      <header className="story-chapter-head">
        <p className="story-index">03 / PRODUCTION ROADMAP</p>
        <h2 id="production-roadmap-title">你的100分钟，<br />是怎样做出来的</h2>
        <p>不靠最后几天冲刺，把目标拆成从立项到发行的十个真实节点。</p>
      </header>
      <ol className="milestone-line">
        {productionMilestones.map((item, index) => <li className={index >= 5 ? "is-runtime" : ""} key={item}><span>{String(index + 1).padStart(2, "0")}</span><i /><strong>{item}</strong></li>)}
      </ol>
      <p className="story-conclusion">100分钟不是最后几天突然冲出来的，而是从第一天开始，通过明确的项目里程碑逐步完成。</p>
    </section>

    <TrainingFlow />

    <section className="story-chapter one-project" id="story-one-project" aria-labelledby="one-project-title">
      <div className="story-copy">
        <p className="story-index">05 / ONE PERSON, ONE PROJECT</p>
        <h2 id="one-project-title"><span>不组队等别人。</span><strong>每个人都有自己的<span>短剧。</span></strong></h2>
        <p>每位学员独立选择题材、建立人物、完成剧本、制作镜头并推进自己的项目，<br />不因其他学员进度不同而互相等待。</p>
      </div>
      <article className="student-space-model" aria-label="学生个人空间概念界面">
        <header><div><span>我的短剧</span><h3>《雾港回声》</h3></div><small>学生空间 · 概念界面</small></header>
        <div className="student-model-progress"><div><strong>27.4</strong><span>/ 100 min</span></div><i><b style={{ width: "27.4%" }} /></i></div>
        <dl><div><dt>总目标</dt><dd>100分钟</dd></div><div><dt>已完成</dt><dd>27.4分钟</dd></div><div><dt>当前阶段</dt><dd>批量生产</dd></div><div><dt>下一里程碑</dt><dd>50分钟</dd></div><div><dt>本周待返修</dt><dd>3项</dd></div></dl>
        <footer><span>DAY 18</span><p>今天继续推进自己的成片</p><b>→</b></footer>
      </article>
    </section>

    <section className="story-chapter cost-control" id="story-cost" aria-labelledby="cost-control-title">
      <header className="story-chapter-head compact-head">
        <p className="story-index">06 / COST CONTROL</p>
        <h2 id="cost-control-title"><span>不靠无限抽卡。</span><strong>在预算内完成100分钟。</strong></h2>
      </header>
      <div className="cost-layout">
        <div className="cost-process"><p>先剧本和分镜，再把生成费用花在真正值得的视频镜头上。</p><ol><li>先剧本和分镜</li><li>先锁人物与画面</li><li>低成本验证镜头</li><li>投入正式视频生成</li></ol></div>
        <div className="shot-classes">
          <article><span>A 类</span><h3>关键表演 / 动作 / 高潮</h3><p>高质量视频生成</p></article>
          <article><span>B 类</span><h3>普通叙事镜头</h3><p>标准视频生成</p></article>
          <article><span>C 类</span><h3>对白 / 情绪 / 信息镜头</h3><p>图片资产 + 动态化 + 剪辑</p></article>
        </div>
      </div>
      <div className="cost-ledger"><div><span>已完成</span><strong>31.8分钟</strong></div><div><span>累计生成成本</span><strong>待实测</strong></div><div><span>单位成片成本</span><strong>待实测</strong></div><div><span>预计剩余预算</span><strong>待实测</strong></div><p>开班前公布实测的100分钟低成本制作预算。</p></div>
    </section>

    <section className="story-chapter curriculum-story" id="story-curriculum" aria-labelledby="curriculum-story-title">
      <header className="story-chapter-head inline-head"><div><p className="story-index">07 / 40 DAYS · 160 LESSONS</p><h2 id="curriculum-story-title">40天 / 160课时<br />到底学什么</h2></div><p>制作112课时、发行运营48课时，7∶3交叉推进，<br />八周围绕同一部短剧完成从立项、生产到发行复盘。</p></header>
      <div className="week-timeline desktop-weeks">{courseWeeks.map(([week, title, copy], index) => <article key={week}><span>{week}</span><i>{String(index + 1).padStart(2, "0")}</i><h3>{title}</h3><p>{copy}</p></article>)}</div>
      <div className="mobile-weeks">{courseWeeks.map(([week, title, copy]) => <details key={week}><summary><span>{week}</span><strong>{title}</strong><i>＋</i></summary><p>{copy}</p></details>)}</div>
      <Link href="/plan" className="story-button">查看完整40天课程 <span>→</span></Link>
    </section>

    <section className="story-chapter takeaways" id="story-takeaways" aria-labelledby="takeaways-title">
      <div className="story-copy"><p className="story-index">08 / WHAT YOU KEEP</p><h2 id="takeaways-title"><span>两个月以后，</span><strong>留下的，不只是</strong><span>“学过AI”。</span></h2><p>课程结束以后，真正留下的是一套可继续生产、展示和复盘的<span className="keep-phrase">个人项目</span>。</p></div>
      <div className="takeaway-system"><div className="takeaway-core"><span>核心成果</span><strong>一部自己的<br />约100分钟<br />完整短剧</strong><small>个人项目 · 持续迭代</small></div><ul>{projectAssets.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>)}</ul></div>
    </section>

    <section className="story-chapter teacher-story" id="story-teacher" aria-labelledby="teacher-story-title">
      <header className="story-chapter-head inline-head"><div><p className="story-index">09 / YOUR TEACHER</p><h2 id="teacher-story-title">从影视制作，到教学，<br />再到AI短剧。</h2></div><p>不是把软件菜单重新讲一遍，<br />而是把完整影视制作经验改造成个人可以执行的生产路线。</p></header>
      <div className="teacher-path"><article><span>01</span><div><small>影视制作</small><h3>真实项目里的制作判断</h3><p>真人短剧、影视后期、商业影像项目经验。</p></div></article><article><span>02</span><div><small>教学与专业建设</small><h3>把复杂流程变成可学习的方法</h3><p>多年影视动画教学与课程体系建设经验。</p></div></article><article><span>03</span><div><small>AI内容生产</small><h3>持续在真实生产中验证</h3><p>AI漫剧承制、自主项目、繁星OS及短剧生产体系实践。</p></div></article></div>
      <blockquote>这门课不是把AI软件操作重新录一遍，而是把真实影视制作流程改造成适合个人完成的AI短剧生产方法。</blockquote>
    </section>

    <section className="story-chapter teaching-system" id="story-teaching" aria-labelledby="teaching-system-title">
      <header className="story-chapter-head"><p className="story-index">10 / HOW WE TEACH</p><h2 id="teaching-system-title"><span>不是进群听课。</span><strong>进入自己的短剧项目。</strong></h2></header>
      <div className="interface-concepts">
        <article><div className="concept-ui student-ui"><header><i>周</i><span>我的短剧</span><b>27.4%</b></header><div><span /><span /><span /></div><footer><i style={{ width: "42%" }} /></footer></div><span>01 / 学生空间</span><h3>我的剧、我的进度、我的成本、我的反馈。</h3></article>
        <article><div className="concept-ui review-ui"><header><span>镜头 08 · v03</span><b>3 条批注</b></header><div><i>00:12</i><i>00:27</i><i>00:41</i></div><footer><span>返修后再提交</span><b>→</b></footer></div><span>02 / 教师审片</span><h3>镜头点评、返修、里程碑验收。</h3></article>
        <article><div className="concept-ui timeline-ui"><header>DAY 1 — DAY 40</header><div>{[1, 2, 3, 4, 5].map((item) => <i key={item}><b /></i>)}</div><footer><span>立项</span><span>样片</span><span>100 min</span></footer></div><span>03 / 项目时间线</span><h3>从 Day 1 到100分钟完整保存。</h3></article>
      </div>
    </section>

    <section className="story-chapter audience-fit" id="story-fit" aria-labelledby="audience-fit-title">
      <header className="story-chapter-head compact-head"><p className="story-index">11 / WHO IT IS FOR</p><h2 id="audience-fit-title"><span>适合谁，</span><strong>也提前说清楚</strong><span>谁不适合。</span></h2></header>
      <div className="fit-columns"><article><span>适合</span><ul><li>零基础但真正想进入AI短剧制作的人</li><li>已经在做AI图片/视频但缺完整流程的人</li><li>影视、剪辑、编剧转AI制作的人</li><li>想通过一个完整项目建立作品集的人</li></ul></article><article><span>不适合</span><ul><li>只想买一部别人替你做好的短剧</li><li>不愿意投入课后制作时间</li><li>只想听课、不想完成项目</li><li>希望3980包含无限AI生成费用的人</li></ul></article></div>
    </section>

    <section className="story-chapter pricing-story" id="story-pricing" aria-labelledby="pricing-story-title">
      <div className="pricing-copy"><p className="story-index">12 / FIRST COHORT</p><h2 id="pricing-story-title">首期创始班</h2><p>首期同时设置三个平行小班，教学内容与课程进度相同，<br />报名时选择一个适合自己的时段，全程跟随该班学习。</p><div className="price-lockup"><del>¥4,999</del><strong>¥3,980</strong><span>首期创始班价格<br />后续标准价 ¥4,999</span></div></div>
      <div className="class-schedules">
        <header><span>三个平行班次 · 任选其一</span><p>每位学员只需选择一个上课时段，不需要一天参加三场。</p></header>
        <article><span>上午班</span><strong>09:00—12:00</strong><small>本班 · 3小时 / 日 · 4课时 / 日</small></article>
        <article><span>下午班</span><strong>14:00—17:00</strong><small>本班 · 3小时 / 日 · 4课时 / 日</small></article>
        <article><span>晚间班</span><strong>19:00—22:00</strong><small>本班 · 3小时 / 日 · 4课时 / 日</small></article>
        <footer><span>任选一个班次 · 约40教学日</span><strong>每班160课时</strong></footer>
      </div>
    </section>

    <section className="story-chapter fee-boundary" id="story-fees" aria-labelledby="fee-boundary-title">
      <header className="story-chapter-head inline-head"><div><p className="story-index">13 / COST BOUNDARY</p><h2 id="fee-boundary-title">学费不是<br />全部制作费用。</h2></div><p>报名之前把费用边界说明白，<br />第三方生成服务由学员按自己的设备、预算和生产路线选择。</p></header>
      <div className="fee-columns"><article><span>学费包含</span><ul>{["课程", "教师指导", "项目点评", "学生空间", "项目存储", "教学资料", "项目验收"].map((item) => <li key={item}>{item}</li>)}</ul></article><article><span>学费不包含</span><strong>学员自行使用的第三方AI生成服务实际费用。</strong><p>报名前提供当前课程推荐工具与预计制作预算。学员可以根据自己的设备和预算选择不同生产路线。</p></article></div>
    </section>

    <section className="story-chapter faq-story" id="story-home-faq" aria-labelledby="faq-story-title">
      <header className="story-chapter-head inline-head"><div><p className="story-index">14 / FAQ</p><h2 id="faq-story-title">报名前，<br />先把重要问题说清楚</h2></div><p>不回避100分钟、设备、时间、第三方费用和结果边界。</p></header>
      <div className="faq-list">{homepageFaqs.map(([question, answer], index) => <details key={question} open={index === 1}><summary><span>{String(index + 1).padStart(2, "0")}</span><strong>{question}</strong><i>＋</i></summary><p>{answer}</p></details>)}</div>
    </section>

    <section className="story-chapter final-consult" id="story-consult" aria-labelledby="final-consult-title">
      <header><p className="story-index">15 / TALK TO US</p><h2 id="final-consult-title">还有问题？直接问我。</h2><p>选择网页咨询、微信或留下联系方式，用你方便的方式直接联系老师。</p></header>
      <div className="consult-options"><article><span>💬</span><h3>网页直接聊</h3><p>无需注册，直接咨询老师。</p><small>星讯独立站点待接通</small><Link href="/ask?source=home-webchat">查看接入状态 →</Link></article><article><span>📱</span><h3>微信咨询</h3><p>扫码加微信详细聊。</p><small>项目二维码待提供</small><Link href="/ask?source=home-wechat">查看微信入口 →</Link></article><article><span>📝</span><h3>留下联系方式</h3><p>现在不方便聊？留下微信或手机，我联系你。</p><small>提交后进入管理端</small><Link href="/ask?source=home-lead">填写咨询信息 →</Link></article></div>
    </section>
  </div>;
}

function HomePage() {
  return <Shell>
    <HeroExperience />
    <CourseDifference />
    <LandingStory />
  </Shell>;
}

const courseWeekPlan = [
  ["WEEK 01", "立项与总纲", "把题材、目标、人物与100分钟结构真正立住。"],
  ["WEEK 02", "剧本与分镜", "让剧本进入可以拍、可以算成本的镜头方案。"],
  ["WEEK 03", "视觉资产与样片", "建立人物场景资产，并跑通第一条生产线。"],
  ["WEEK 04", "25分钟里程碑", "开始批量制作，完成第一阶段成片验收。"],
  ["WEEK 05", "50分钟里程碑", "持续生产，同时形成首轮发行素材。"],
  ["WEEK 06", "75分钟与发行", "成片继续增长，发布、数据与返修同步推进。"],
  ["WEEK 07", "100分钟与优化", "补齐全剧，围绕反馈完成关键返修。"],
  ["WEEK 08", "成片交付与复盘", "完成项目包、发行记录与个人生产复盘。"],
] as const;


function CoursePlanBrowser({ course, selected, onSelect, error = "", showHeader = true }: { course: PublicCoursePayload | null; selected: number; onSelect: (day: number) => void; error?: string; showHeader?: boolean }) {
  const selectedWeek = Math.ceil(selected / 5);
  const weekDays = course?.days.filter((day) => day.week === selectedWeek) ?? [];
  const weekHours = weekDays.reduce((total, day) => total + day.productionHours + day.operationsHours, 0);
  const active = course?.days.find((day) => day.day === selected);
  return <>
    {showHeader && <header className="detail-section-head course-workspace-head">
      <div><p className="detail-page-index">8 WEEKS / 40 DAYS</p><h2 id="course-browser-title">选中哪周，<span>只看这一周。</span></h2></div>
      <p>每周5个教学日，选择周次，了解每天的课程主题与学习方向。</p>
    </header>}
    {error && <div className="error-panel">课程读取失败：{error}</div>}
    {!course && !error && <div className="loading-panel">正在读取课程版本…</div>}
    {course && <>
      <div className="week-map" role="tablist" aria-label="八周课程筛选">{courseWeekPlan.map(([week, title], index) => <button key={week} type="button" role="tab" aria-selected={selectedWeek === index + 1} onClick={() => onSelect(index * 5 + 1)} className={selectedWeek === index + 1 ? "active" : ""}><small>{week}</small><strong>{title}</strong><span>第 {index + 1} 周</span></button>)}</div>
      <div className="course-week-intro" aria-live="polite"><div><span>{courseWeekPlan[selectedWeek - 1][0]}</span><strong>{courseWeekPlan[selectedWeek - 1][1]}</strong></div><p>{courseWeekPlan[selectedWeek - 1][2]}</p><dl><div><dt>教学日</dt><dd>5天</dd></div><div><dt>本周课时</dt><dd>{weekHours}课时</dd></div></dl></div>
      <div className="course-layout">
        <div className="day-list" aria-label={`第${selectedWeek}周课程目录`}>{weekDays.map((day) => <button key={day.code} type="button" onClick={() => onSelect(day.day)} className={selected === day.day ? "active" : ""}><span>{day.code}</span><div><strong>{day.title}</strong><small>制作 {day.productionHours}课时 · 发行运营 {day.operationsHours}课时</small></div><i>→</i></button>)}</div>
        {active && <article className="lesson-detail"><header><span>{active.code} · WEEK {String(active.week).padStart(2, "0")}</span><span className="lesson-day-badge">第 {active.day} 天</span></header><h2>{active.title}</h2><p className="lesson-objective">{active.summary}</p><div className="lesson-ratio"><span style={{ flex: active.productionHours || 1 }}>制作 {active.productionHours} 课时</span>{active.operationsHours > 0 && <span style={{ flex: active.operationsHours }}>发行运营 {active.operationsHours} 课时</span>}</div><dl className="lesson-rules"><div><dt>课程说明</dt><dd>本页仅展示课程方向。完整课时、实操指导与配套资料，报名缴费后在学习空间查看。</dd></div></dl></article>}
      </div>
    </>}
  </>;
}

function CoursePage() {
  const [course, setCourse] = useState<PublicCoursePayload | null>(null);
  const [selected, setSelected] = useState(1);
  const [error, setError] = useState("");
  useEffect(() => { api<{ ok: boolean } & PublicCoursePayload>("/api/course/public").then(setCourse).catch((value) => setError(value.message)); }, []);
  return <Shell>
    <div className="marketing-detail-page course-plan-page">
    <section className="course-plan-hero section-pad" aria-labelledby="course-plan-title">
      <div className="detail-hero-copy">
        <p className="detail-page-index">COURSE PLAN / 课程计划</p>
        <h1 id="course-plan-title"><span>40天，</span><strong>课程跟着项目走。</strong></h1>
        <p>从立项、剧本、人物场景到完整成片与发行复盘，每周只围绕同一部属于你的短剧推进。</p>
      </div>
      <dl className="course-plan-facts">
        <div><dt>教学日</dt><dd>{course?.totals.days ?? 40}</dd></div>
        <div><dt>总课时</dt><dd>{course?.totals.hours ?? 160}</dd></div>
        <div><dt>制作课时</dt><dd>{course?.totals.productionHours ?? 112}</dd></div>
        <div><dt>发行运营课时</dt><dd>{course?.totals.operationsHours ?? 48}</dd></div>
      </dl>
    </section>
    <section className="section-pad course-workspace" aria-labelledby="course-browser-title"><CoursePlanBrowser course={course} selected={selected} onSelect={setSelected} error={error} /></section>
    </div>
  </Shell>;
}

function LeadForm({ compact = false, onSaved }: { compact?: boolean; onSaved?: (lead: Lead) => void }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setMessage(""); setSuccess(false);
    const form = new FormData(event.currentTarget);
    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await api<{ lead: Lead; notice: string }>("/api/leads", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify({
          name: form.get("name"), contactType: form.get("contactType"), contact: form.get("contact"),
          schedule: form.get("schedule"), convenientTime: form.get("convenientTime"), question: form.get("question"),
          consent: form.get("consent") === "on", source: `${window.location.pathname}${window.location.search}`,
        }),
      });
      setSuccess(true); setMessage(`${result.notice} 编号：${result.lead.id}`); onSaved?.(result.lead); event.currentTarget.reset();
    } catch (value) { setMessage(value instanceof Error ? value.message : "保存失败，请重试。"); }
    finally { setBusy(false); }
  };
  return <form className={compact ? "lead-form compact" : "lead-form"} onSubmit={submit}>
    <div className="form-row"><label><span>怎么称呼你 <i>选填</i></span><input name="name" maxLength={40} placeholder="昵称也可以" /></label><label><span>想参加的时段 <i>选填</i></span><select name="schedule" defaultValue="undecided"><option value="undecided">还没想好</option><option value="morning">上午</option><option value="afternoon">下午</option><option value="evening">晚上</option></select></label></div>
    <div className="form-row contact-row"><label><span>联系方式</span><select name="contactType" aria-label="联系方式类型" defaultValue="wechat"><option value="wechat">微信</option><option value="phone">电话</option></select></label><label className="grow"><span className="sr-only">联系方式内容</span><input name="contact" required maxLength={80} placeholder="仅用于本次课程咨询" autoComplete="off" /></label></div>
    <label><span>方便联系时间 <i>选填，不等于已预约</i></span><input name="convenientTime" maxLength={80} placeholder="例如：工作日 19:00 后" /></label>
    <label><span>最想先问什么 <i>选填</i></span><textarea name="question" maxLength={600} rows={compact ? 3 : 4} placeholder="可以说说你的题材、基础、时间或预算顾虑" /></label>
    <label className="consent"><input name="consent" type="checkbox" required /><span>我确认以上信息仅用于本次课程咨询与人工跟进；当前数据进入隔离演示库，不会自动外发。</span></label>
    <button className="button primary full" disabled={busy} type="submit">{busy ? "正在保存…" : "保存咨询意向"}</button>
    {message && <p className={success ? "form-message success" : "form-message error"} role="status">{message}</p>}
  </form>;
}

function ContactPage({ status }: { status: StatusPayload | null }) {
  return <Shell>
    <section className="page-hero section-pad contact-hero"><p className="eyebrow">还有问题？直接问我</p><h1>在线咨询老师<br />或留下联系方式。</h1><p>关于课程、班次和报名，都可以直接问。老师的回复会显示在你的咨询对话里。</p></section>
    <section className="contact-grid section-pad">
      <article className="contact-method featured"><div className="method-number">01</div><p className="eyebrow">首选方式</p><h2>网页直接咨询</h2><IntegrationPill integration={status?.integrations.xingxun} /><p>直接与繁星短剧训练营的咨询老师沟通，无需先注册学员账号。可以先留言，稍后回来查看回复。</p><button className="button primary" type="button" onClick={() => window.dispatchEvent(new Event('duanju:open-support'))}>在线咨询老师</button><small>咨询记录仅用于本次课程咨询与人工跟进，不会自动开通课程权限。</small></article>
      <article className="contact-method"><div className="method-number">02</div><p className="eyebrow">站外沟通</p><h2>微信扫码咨询</h2><div className="qr-placeholder"><span>QR</span><strong>二维码待配置</strong><small>未生成假码</small></div><p>用户尚未提供本项目可公开的微信二维码与微信号。打开或复制二维码不会自动算作“已添加微信”。</p></article>
      <article className="contact-method form-card"><div className="method-number">03</div><p className="eyebrow">可实际保存</p><h2>留下联系方式</h2><p>提交成功后生成真实演示线索编号，可在管理端查看和跟进。</p><LeadForm compact /></article>
    </section>
  </Shell>;
}

function IntegrationPill({ integration }: { integration?: Integration }) {
  if (!integration) return <span className="integration-pill neutral">状态读取中</span>;
  return <span className={`integration-pill ${integration.status}`}>{statusNames[integration.status]}</span>;
}

function UserAvatar({ profile, large = false }: { profile?: PublicProfile | null; large?: boolean }) {
  const name = profile?.displayName || profile?.username || "陆炽";
  const className = `avatar user-avatar${large ? " large" : ""}`;
  return profile?.avatarUrl
    ? <img className={className} src={profile.avatarUrl} alt={`${name}的头像`} />
    : <span className={className} aria-label={`${name}的文字头像`}>{name.slice(0, 1)}</span>;
}

const internalRoleOptions: (userId: string, roles: Role[]) => { role: Role; label: string; href: string }[] = (userId, roles) => roles.map((role) => ({
    role,
    label: roleNames[role],
    href: role === "admissions" ? "/admissions" : portalViewPath(role, userId, "overview"),
  }));

const internalNavGroups: Record<Role, PortalNavGroup[]> = {
  teacher: [
    { label: "教学", items: [{ id: "overview", label: "教学总览" }, { id: "outline", label: "教学大纲" }, { id: "course", label: "教学计划" }, { id: "today", label: "今日课程" }] },
    { label: "任务", items: [{ id: "tasks", label: "任务管理" }, { id: "pending", label: "待审提交" }, { id: "feedback", label: "版本反馈" }] },
    { label: "项目", items: [{ id: "scripts", label: "剧本示范" }, { id: "dramas", label: "短剧示范" }, { id: "projects", label: "学员项目" }, { id: "files", label: "成片与文件" }, { id: "costs", label: "成本概览" }] },
    { label: "设置", items: [{ id: "cache", label: "本地缓存" }] },
  ],
  student: [
    { label: "我的短剧", items: [{ id: "overview", label: "项目总览" }, { id: "scripts", label: "剧本创作" }, { id: "dramas", label: "短剧制作" }] },
    { label: "学习与制作", items: [{ id: "today", label: "今日学习" }, { id: "tasks", label: "任务与提交" }, { id: "feedback", label: "版本与反馈" }] },
    { label: "项目资产", items: [{ id: "files", label: "剧集与文件" }, { id: "costs", label: "成本台账" }] },
    { label: "学习支持", items: [{ id: "materials", label: "课程资料" }] },
    { label: "设置", items: [{ id: "cache", label: "本地缓存" }] },
  ],
  admissions: [
    { label: "招生运营", items: [{ id: "overview", label: "招生数据" }] },
    { label: "权限管理", items: [{ id: "members", label: "成员管理" }] },
  ],
};

function InternalRoleMenu({ role, profile }: { role: Role; profile?: PublicProfile | null }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const session = readLoginSession();
  const options = session ? internalRoleOptions(session.user.id, session.availablePortals) : [];
  const canSwitch = options.length > 1;
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (!canSwitch) return <span className="internal-role-static">{roleNames[role]}</span>;
  return <div ref={menuRef} className={`internal-role-menu${open ? " is-open" : ""}`}>
    <button type="button" role="combobox" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      <span>{roleNames[role]}</span>
      <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true"><path d="M3.14 6.16a.5.5 0 0 1 .7-.02L7.5 9.56l3.66-3.42a.5.5 0 1 1 .68.73l-4 3.75a.5.5 0 0 1-.68 0l-4-3.75a.5.5 0 0 1-.02-.71Z" fill="currentColor" /></svg>
    </button>
    {open && <div className="internal-role-popover" role="listbox" aria-label="切换工作端">
      {options.map((option) => <Link key={option.role} href={option.href} className={option.role === role ? "active" : ""} onClick={() => setOpen(false)} ariaCurrent={option.role === role ? "page" : undefined}>
        <span>{option.label}</span>{option.role === role && <b aria-hidden="true">✓</b>}
      </Link>)}
    </div>}
  </div>;
}

const accountIconPaths = {
  bell: "M15 17H9m9-1V11a6 6 0 1 0-12 0v5l-2 2h16l-2-2zm-4 5a2 2 0 0 1-4 0",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 9a7 7 0 0 1 14 0",
  receipt: "M7 3h10a2 2 0 0 1 2 2v16l-3-1.5-2 1-2-1-2 1-2-1L5 21V5a2 2 0 0 1 2-2zm2 5h6m-6 4h6m-6 4h4",
  cog: "M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7zm7-3.5-1.7-.98.06-1.04 1.81-.76-1.5-2.6-1.86.53-.83-.63-.23-1.91H10.2l-.23 1.9-.83.64-1.86-.53-1.5 2.6 1.81.76.06 1.04L5 12l.98 2.77 1.96-.07.73.74-.44 1.89 2.7 1.43.67-1.84 1.07-.02.7 1.86 2.67-1.44-.42-1.88.73-.75 1.96.08L19 12z",
  help: "M12 17h.01M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4m8.9-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z",
  logout: "M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3m7-3 5-5-5-5m5 5H9",
} as const;

function AccountIcon({ type }: { type: keyof typeof accountIconPaths }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={accountIconPaths[type]} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function InternalAccountMenu({ profile }: { profile?: PublicProfile | null }) {
  const [open, setOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const name = profile?.displayName || profile?.username || "繁星账号";
  const label = name.slice(0, 1) || "星";
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);
  const logout = async () => {
    setLogoutConfirmOpen(false);
    const session = readLoginSession();
    const logoutRequest = api("/api/auth/logout", { method: "POST", body: JSON.stringify({ refreshToken: session?.refreshToken || "" }) }, false);
    clearLoginSession();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
    try {
      await logoutRequest;
    } catch {
      // The explicit local logout still completes if the identity service is temporarily unavailable.
    }
  };
  return <>
    <div ref={boxRef} className={`internal-account${open ? " is-open" : ""}`}>
    <button type="button" className="internal-account-trigger" aria-label={`当前账号：${name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      <span>{profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : label}</span>
    </button>
    <div className="internal-account-menu" role="menu" aria-label="账号菜单">
      <div className="internal-account-head">
        <span className="internal-account-avatar">{profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : label}</span>
        <div><strong>{name}</strong><small>{profile ? `繁星 ID ${profile.displayId}` : "账号信息读取中"}</small></div>
      </div>
      <i className="internal-account-divider top" />
      <div className="internal-account-body">
        <div className="internal-account-section primary">
          <button type="button" disabled><span><AccountIcon type="bell" /></span><b>我的通知</b></button>
          <Link href={publicStudentPath} onClick={() => setOpen(false)}><span><AccountIcon type="user" /></span><b>个人主页</b></Link>
          <button type="button" disabled><span><AccountIcon type="receipt" /></span><b>消费账单</b></button>
          <button type="button" disabled><span><AccountIcon type="cog" /></span><b>账户管理</b></button>
        </div>
        <i className="internal-account-divider" />
        <div className="internal-account-section secondary">
          <Link href="/plan" onClick={() => setOpen(false)}><span><AccountIcon type="help" /></span><b>使用教程</b></Link>
          <button type="button" onClick={() => { setOpen(false); setLogoutConfirmOpen(true); }}><span><AccountIcon type="logout" /></span><b>登出账号</b></button>
        </div>
      </div>
      </div>
    </div>
    <FanxingActionConfirmDialog
      open={logoutConfirmOpen}
      title="确认退出登录"
      description="退出后将清除当前登录状态。是否继续退出？"
      confirmLabel="退出登录"
      tone="danger"
      onClose={() => setLogoutConfirmOpen(false)}
      onConfirm={() => void logout()}
    />
  </>;
}

function InternalHeader({ role, profile }: { role: Role; profile?: PublicProfile | null }) {
  return <header className="internal-os-header">
    <div className="internal-os-header-left">
      <Link href="/" className="internal-os-brand"><BrandLockup /><span className="internal-os-brand-version">{roleNames[role]} {portalVersions[role]}</span></Link>
      <InternalRoleMenu role={role} profile={profile} />
    </div>
    <nav className="internal-os-header-right" aria-label="工作台辅助导航">
      <a href={publicStudentPath} target="_blank" rel="noopener noreferrer">创作者空间</a>
      <InternalAccountMenu profile={profile} />
    </nav>
  </header>;
}

function InternalSidebar({ role, userId, activeView = "overview", onSelectView }: { role: Role; userId?: string; activeView?: PortalView; onSelectView?: (view: PortalView) => void }) {
  return <aside className="internal-os-sidebar">
    <div className="internal-os-sidebar-inner">
      <nav className="internal-os-sidebar-navigation" aria-label={`${roleNames[role]}导航`}>
        {internalNavGroups[role].map((group) => <div className="internal-os-nav-group" key={group.label}>
          <span className="internal-os-nav-heading">{group.label}</span>
          {group.items.map((item) => {
            if (onSelectView && (role === "admissions" || userId)) {
              const href = role === "admissions"
                ? (item.id === "overview" ? "/admissions" : `/admissions/${item.id}`)
                : portalViewPath(role, userId!, item.id);
              return <a href={href} key={item.id} className={`internal-os-sidebar-entry${activeView === item.id ? " primary" : ""}`} aria-current={activeView === item.id ? "page" : undefined} onClick={(event) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onSelectView(item.id);
              }}>{item.label}</a>;
            }
            return <a key={item.id} className={`internal-os-sidebar-entry${item.id === "overview" ? " primary" : ""}`} href={item.id === "overview" ? "#overview" : "#workspace"}>{item.label}</a>;
          })}
        </div>)}
      </nav>
      <footer>© 2026 繁星短剧训练营</footer>
    </div>
  </aside>;
}

function useSystemMetrics(enabled: boolean) {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let timer = 0;
    const load = async () => {
      try {
        const value = await api<{ ok: true } & SystemMetrics>("/api/system/metrics");
        if (!stopped) setMetrics(value);
      } catch {
        // Keep the last verified sample instead of fabricating changing values.
      }
    };
    void load();
    timer = window.setInterval(load, 2_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [enabled]);
  return metrics;
}

function ResourceMetric({ label, value, percent }: { label: string; value: string; percent: number }) {
  return <span className={`internal-resource-metric ${label.toLowerCase()}`} style={{ "--metric-progress": `${Math.max(0, Math.min(100, percent))}%` } as React.CSSProperties}>
    <i aria-hidden="true" /><span>{label}</span><b>{value}</b>
  </span>;
}

function InternalStatusBar({ role, manualWorkspace = false }: { role: Role; manualWorkspace?: boolean }) {
  const [workflow, setWorkflow] = useState<DemonstrationWorkflowStatus | null>(null);
  useEffect(() => {
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<DemonstrationWorkflowStatus>).detail;
      setWorkflow(detail?.available ? detail : null);
    };
    window.addEventListener(workflowStatusEvent, receive);
    return () => window.removeEventListener(workflowStatusEvent, receive);
  }, []);
  useEffect(() => {
    if (!workflow?.open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') window.dispatchEvent(new CustomEvent(workflowToggleEvent, { detail: { contentId: workflow.contentId, open: false } }));
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [workflow]);
  const showResources = !manualWorkspace && (role === "teacher" || role === "student");
  const metrics = useSystemMetrics(showResources);
  const value = (key: keyof SystemMetrics) => typeof metrics?.[key] === "number" ? Number(metrics[key]) : 0;
  const consoleUrl = `https://${window.location.hostname}:8858`;
  return <footer className="internal-os-statusbar">
    <div className="internal-os-status-left">{manualWorkspace && workflow?.available ? <button type="button" className="internal-workflow-toggle" aria-expanded={workflow.open} aria-label={workflow.open ? '收起视频工作流' : '展开视频工作流'} title={workflow.open ? '收起视频工作流' : '展开视频工作流'} onClick={() => window.dispatchEvent(new CustomEvent(workflowToggleEvent, { detail: { contentId: workflow.contentId } }))}><StatusDrawerIcon /><b>视频工作流</b></button> : <StatusDrawerIcon />}<span>{roleNames[role]} · 内容建设中</span></div>
    <div className="internal-os-status-center">工程类型：<b>短剧实训</b><span>项目：个人100分钟短剧</span></div>
    {showResources && <div className="internal-os-resources" title={metrics?.sampledAt ? `采样于 ${formatTime(metrics.sampledAt)}` : "正在读取当前资源状态"}>
      <ResourceMetric label="CPU" value={metrics ? `${value("cpu")}%` : "--"} percent={value("cpu")} />
      <ResourceMetric label="RAM" value={metrics ? `${value("ram")}%` : "--"} percent={value("ram")} />
      <ResourceMetric label="GPU" value={metrics ? `${value("gpu")}%` : "--"} percent={value("gpu")} />
      <ResourceMetric label="VRAM" value={metrics ? `${value("vram")}%` : "--"} percent={value("vram")} />
      <ResourceMetric label="TEMP" value={metrics ? `${value("temp")}°` : "--"} percent={value("temp")} />
      <a className="internal-compute-console" href={consoleUrl} target="_blank" rel="noreferrer"><i aria-hidden="true" />算力控制台</a>
    </div>}
  </footer>;
}

function InternalShell({ role, userId, title, subtitle, profile, activeView, onSelectView, hideWorkspaceHead = false, children }: { role: Role; userId?: string; title: string; subtitle: string; profile?: PublicProfile | null; activeView?: PortalView; onSelectView?: (view: PortalView) => void; hideWorkspaceHead?: boolean; children: ReactNode }) {
  useEffect(() => {
    const previousTitle = document.title;
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    document.title = `繁星短剧训练营${roleNames[role]} ${portalVersions[role]}`;
    document.documentElement.dataset.internalBoot = "true";
    if (themeColor) themeColor.content = "#0f0f0f";
    document.documentElement.classList.add("internal-os-document");
    document.body.classList.add("internal-os-document");
    return () => {
      document.title = previousTitle;
      delete document.documentElement.dataset.internalBoot;
      if (themeColor) themeColor.content = "#f5f0e6";
      document.documentElement.classList.remove("internal-os-document");
      document.body.classList.remove("internal-os-document");
    };
  }, [role]);
  useEffect(() => { document.querySelector(".internal-os-main")?.scrollTo({ top: 0 }); }, [activeView]);
  return <div className={`internal-os-shell role-${role}`}>
    <InternalHeader role={role} profile={profile} />
    <InternalSidebar role={role} userId={userId} activeView={activeView} onSelectView={onSelectView} />
    <main className="internal-os-main">
      {!hideWorkspaceHead && <section id="overview" className="workspace-head section-pad"><div><p className="eyebrow">{roleNames[role]}</p><h1>{title}</h1><p>{subtitle}</p></div>{profile && <div className="workspace-person"><UserAvatar profile={profile} /><span><strong>{profile.displayName}</strong><small>繁星 ID {profile.displayId}</small></span></div>}</section>}
      <section id="workspace" className="workspace section-pad">{children}</section>
    </main>
    <InternalStatusBar role={role} manualWorkspace={activeView === "scripts" || activeView === "dramas"} />
  </div>;
}

function TrainingCachePanel({ userId, role }: { userId: string; role: "teacher" | "student" }) {
  const [info, setInfo] = useState<Awaited<ReturnType<typeof getTrainingCacheInfo>> | null>(null);
  const [courseInfo, setCourseInfo] = useState<Awaited<ReturnType<typeof getCourseCacheInfo>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [message, setMessage] = useState("");
  const refresh = useCallback(() => {
    void getTrainingCacheInfo(userId, role).then(setInfo);
    void getCourseCacheInfo(userId, role).then(setCourseInfo);
  }, [userId, role]);
  useEffect(refresh, [refresh]);
  const action = async (work: () => Promise<unknown>, success: string) => {
    setBusy(true); setMessage("");
    try { await work(); setMessage(success); refresh(); }
    catch (error) { setMessage(error instanceof Error ? error.message : "缓存设置失败，请重试。"); }
    finally { setBusy(false); }
  };
  const localCacheUrl = window.location.hostname === "192.168.1.22" && window.location.port === "8891"
    ? `http://127.0.0.1:8891${window.location.pathname}${window.location.search}${window.location.hash}`
    : null;
  const totalBytes = (info?.bytes || 0) + (courseInfo?.bytes || 0);
  const clear = () => {
    setClearConfirmOpen(false);
    void action(async () => {
      await clearTrainingCache(userId, role);
    }, "当前账号的本地缓存已清空；未上传草稿和服务端原文件已保留。");
  };
  const clearSummary = role === "teacher" ? `${courseInfo?.entries ?? 0} 份课程、${info?.entries ?? 0} 个示范文件` : `${courseInfo?.entries ?? 0} 份课程`;
  return <section className="panel training-cache-panel">
    <header className="panel-head"><div><span>{role === "student" ? "学生端 · 当前账号" : "教师端 · 当前账号"}</span><h2>本地超级缓存</h2></div><span className="data-badge">自动启用</span></header>
    <p>课程{role === "teacher" ? "和教学示范文件" : ""}打开后会自动缓存在当前浏览器，刷新时仍先核验账号权限和内容版本。磁盘副本按账号、工程、剧本和短剧分别保存；不选择目录也能正常使用。</p>
    <div className="training-cache-location"><div><small>本端缓存位置</small><strong>{info?.directoryName || "浏览器自动管理"}</strong><span>{info?.directoryName ? info.directoryReady ? "按账号、工程和内容类型分目录保存" : "目录权限需重新授权；浏览器缓存继续使用" : "按账号隔离；剧本和短剧按工程分别保存"}</span></div><div className="training-cache-actions">{info?.directoryName && !info.directoryReady && <button type="button" className="training-cache-reauthorize" disabled={busy || !info.supported} onClick={() => void action(async () => { await restoreTrainingCacheDirectory(userId, role); }, "缓存目录权限已恢复，现有缓存已同步。")}>重新授权</button>}<button type="button" className="training-cache-choose" disabled={busy || !info?.supported} onClick={() => void action(async () => { await pickTrainingCacheDirectory(userId, role); }, "目录读写已验证，本端现有缓存已同步。")}>选择目录</button></div></div>
    {info && !info.supported && <p className="portal-boundary-note">{window.isSecureContext ? "当前浏览器不支持选择磁盘目录；自动缓存不受影响。" : "与 OS 一样，选择磁盘目录需要安全地址；当前内网 HTTP 地址不能弹出目录选择。自动缓存不受影响。"}{localCacheUrl && <>这台已配置本机映射的 Mac 可<a href={localCacheUrl}>从本机安全地址打开当前页面</a>后选择；首次切换需重新登录。</>}</p>}
    {info?.directoryError && <p className="form-message" role="alert">磁盘副本写入失败：{info.directoryError}；浏览器缓存仍可使用。</p>}
    <div className="training-cache-foot"><span>当前账号：课程 {courseInfo?.entries ?? 0} 份{role === "teacher" ? ` · 示范文件 ${info?.entries ?? 0} 个` : ""} · {(totalBytes / 1024 / 1024).toFixed(1)} MB</span><button type="button" className="training-cache-clear" disabled={busy} onClick={() => setClearConfirmOpen(true)}>清空本地缓存</button></div>
    {message && <p className="form-message" role="status">{message}</p>}
    <p className="portal-boundary-note">缓存只用于本机加速，不替代服务端保存或备份；清除浏览器站点数据可能移除缓存。浏览器只提供所选文件夹名称；如需更换保存位置，可重新选择目录。</p>
    <FanxingActionConfirmDialog
      open={clearConfirmOpen}
      title="清空本地缓存"
      description={`将清空当前账号的${clearSummary}缓存${info?.directoryName ? "，包括所选目录中的副本" : ""}。其他打开的工作台需要重新载入；未上传草稿和服务端原文件会保留。是否继续？`}
      confirmLabel="确认清空"
      tone="danger"
      onClose={() => setClearConfirmOpen(false)}
      onConfirm={clear}
    />
  </section>;
}

function AdmissionsPage() {
  const view: "overview" | "members" = window.location.pathname === "/admissions/members" ? "members" : "overview";
  const [data, setData] = useState<AdmissionsData | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const load = useCallback(() => api<AdmissionsData>("/api/admissions/leads").then((value) => {
    setData(value);
    setSelected((current) => current && value.leads.some((lead) => lead.id === current) ? current : value.leads[0]?.id || null);
  }).catch(() => undefined), []);
  useEffect(() => { void load(); }, [load]);
  const lead = data?.leads.find((item) => item.id === selected);
  const followUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); if (!lead) return;
    const form = new FormData(event.currentTarget);
    try { await api(`/api/admissions/leads/${encodeURIComponent(lead.id)}/interactions`, { method: "POST", body: JSON.stringify({ note: form.get("note"), nextAction: form.get("nextAction"), contactStatus: form.get("contactStatus") }) }); setMessage("跟进记录已持久化。"); event.currentTarget.reset(); await load(); }
    catch (value) { setMessage(value instanceof Error ? value.message : "保存失败"); }
  };
  const selectView = (nextView: PortalView) => {
    const href = nextView === "members" ? "/admissions/members" : "/admissions";
    if (window.location.pathname === href) return;
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return <InternalShell
    role="admissions"
    title={view === "members" ? "成员与角色，一处管理。" : "把每一次咨询，变成可跟进的事实。"}
    subtitle={view === "members" ? "从繁星注册用户中精确查找账号，再授予教师或学生权限；每次访问仍由服务端实时校验。" : "来源、联系状态、报名状态与资金状态分开，所有动作都在同一条线索中持续记录。"}
    profile={data?.user}
    activeView={view}
    onSelectView={selectView}
    hideWorkspaceHead
  >
    {view === "members" ? <AdminMembersPanel /> : !data ? <div className="loading-panel">正在读取招生数据…</div> : <>
      <div className="metric-grid"><Metric label="咨询线索" value={String(data.metrics.leads)} note="有效留资，非访问量" /><Metric label="待联系" value={String(data.metrics.pending)} note="有待办才计数" /><Metric label="已入班" value={String(data.metrics.enrolled)} note="暂无真实入班" /><Metric label="净实收" value="—" note="未接真实付款，不显示 0 元" /></div>
      <div className="admissions-layout"><section className="panel"><header className="panel-head"><div><span>线索工作台</span><h2>最近咨询</h2></div><span className="data-badge">开发数据</span></header>{data.leads.length === 0 ? <div className="empty-state"><strong>还没有咨询</strong><p>前台留资成功后，会以真实线索编号出现在这里。</p><Link href="/ask" className="text-link">前往提交咨询线索 →</Link></div> : <div className="lead-list">{data.leads.map((item) => <button type="button" key={item.id} className={selected === item.id ? "active" : ""} onClick={() => setSelected(item.id)}><span className="avatar">{item.name.slice(0, 1)}</span><div><strong>{item.name}</strong><small>{item.id} · {item.maskedContact}</small></div><span className={`lead-status ${item.contactStatus}`}>{contactStatusNames[item.contactStatus] || item.contactStatus}</span></button>)}</div>}</section>
      <section className="panel detail-panel">{lead ? <><header className="panel-head"><div><span>线索详情</span><h2>{lead.name}</h2></div><span className="data-badge">联系方式已脱敏</span></header><dl className="detail-grid"><div><dt>线索编号</dt><dd>{lead.id}</dd></div><div><dt>首次来源</dt><dd>{lead.source || "未知"}</dd></div><div><dt>联系方式</dt><dd>{lead.contactType === "wechat" ? "微信" : "电话"} · {lead.maskedContact}</dd></div><div><dt>意向时段</dt><dd>{lead.schedule}</dd></div><div><dt>报名 / 资金</dt><dd>未申请 / 未付</dd></div><div><dt>跟进次数</dt><dd>{lead.interactionCount}</dd></div></dl><div className="question-box"><small>初始问题</small><p>{lead.question || "没有填写问题"}</p></div><form className="inline-form" onSubmit={followUp}><h3>记录一次真实操作</h3><label><span>联系状态</span><select name="contactStatus" defaultValue={lead.contactStatus}>{Object.entries(contactStatusNames).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label><label><span>跟进摘要</span><textarea name="note" required maxLength={800} rows={3} placeholder="只写必要摘要，不复制完整聊天" /></label><label><span>下一步</span><input name="nextAction" maxLength={240} placeholder="例如：周三晚回访（不是已预约）" /></label><button className="button primary" type="submit">保存跟进</button>{message && <p className="form-message success">{message}</p>}</form></> : <div className="empty-state"><strong>选择一条线索</strong><p>查看来源和记录下一次动作。</p></div>}</section></div>
      <section className="panel insight-panel"><div><p className="eyebrow">效果与改进</p><h2>样本不足，不提前下结论。</h2></div><div><strong>当前观察</strong><p>{data.metrics.leads ? `已有 ${data.metrics.leads} 条开发线索，可用于验证流程，不能推导真实转化。` : "暂无线索；未知来源保持未知，二维码点击不会算作已加微信。"}</p></div><div><strong>下一次复查</strong><p>接入客服会话引用和正式招生人员后，再建立一致观察窗口。</p></div></section>
    </>}
  </InternalShell>;
}

function AdminMembersPanel() {
  const [members, setMembers] = useState<CourseMember[]>([]);
  const [candidate, setCandidate] = useState<CourseMember | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<MemberFilter>("all");
  const [cohortDrafts, setCohortDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [message, setMessage] = useState("");
  const [pendingRevoke, setPendingRevoke] = useState<{ member: CourseMember; role: CourseMemberRole } | null>(null);
  const loadMembers = useCallback(async () => {
    try {
      const result = await api<{ ok: true; members: CourseMember[] }>("/api/admin/course-members");
      setMembers(result.members);
      setMessage("");
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "成员列表读取失败");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void loadMembers(); }, [loadMembers]);
  const search = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const exactQuery = query.trim();
    if (!exactQuery) return;
    setSearching(true);
    setCandidate(null);
    setMessage("");
    try {
      const result = await api<{ ok: true; found: boolean; member?: CourseMember }>(`/api/admin/identity-users/search?query=${encodeURIComponent(exactQuery)}`);
      if (!result.found || !result.member) setMessage("没有找到对应的繁星注册用户，请核对完整 ID 或手机号。");
      else setCandidate(result.member);
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "繁星用户查找失败");
    } finally {
      setSearching(false);
    }
  };
  const updateRole = async (member: CourseMember, role: CourseMemberRole, enabled: boolean, cohortNo?: number) => {
    const key = `${member.user.id}:${role}`;
    setBusyKey(key);
    setMessage("");
    try {
      const result = await api<{ ok: true; member: CourseMember }>(`/api/admin/course-members/${encodeURIComponent(member.user.id)}/roles`, {
        method: "PATCH",
        body: JSON.stringify({ role, enabled, ...(role === "student" && enabled ? { cohortNo } : {}) }),
      });
      if (candidate?.user.id === result.member.user.id) setCandidate(result.member);
      if (result.member.cohortNo) setCohortDrafts((current) => ({ ...current, [result.member.user.id]: String(result.member.cohortNo) }));
      await loadMembers();
      const name = result.member.user.displayName || "该账号";
      if (role === "student" && enabled) setMessage(`${name}已归入第 ${result.member.cohortNo} 期学员。`);
      else setMessage(`${name}已${enabled ? "获得" : "撤销"}${role === "teacher" ? "教师" : "学生"}权限。`);
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "角色更新失败");
    } finally {
      setBusyKey("");
      setPendingRevoke(null);
    }
  };
  const roleAction = (member: CourseMember, role: CourseMemberRole) => {
    const enabled = member.roles.includes(role);
    if (enabled) setPendingRevoke({ member, role });
    else if (role === "student") {
      const cohortNo = Number(cohortDrafts[member.user.id] || member.cohortNo || 1);
      if (!Number.isInteger(cohortNo) || cohortNo < 1 || cohortNo > 999) {
        setMessage("请先填写 1—999 之间的学员期数。");
        return;
      }
      void updateRole(member, role, true, cohortNo);
    } else void updateRole(member, role, true);
  };
  const RoleButtons = ({ member }: { member: CourseMember }) => {
    const teacherEnabled = member.roles.includes("teacher");
    const studentEnabled = member.roles.includes("student");
    const draft = cohortDrafts[member.user.id] ?? String(member.cohortNo || 1);
    const parsedDraft = Number(draft);
    const cohortChanged = studentEnabled && Number.isInteger(parsedDraft) && parsedDraft !== member.cohortNo;
    return <div className="member-role-actions" aria-label={`${member.user.displayName}的角色`}>
      <button type="button" className={teacherEnabled ? "active" : ""} aria-pressed={teacherEnabled} disabled={Boolean(busyKey)} onClick={() => roleAction(member, "teacher")}>
        <span aria-hidden="true">{teacherEnabled ? "✓" : "+"}</span>{busyKey === `${member.user.id}:teacher` ? "处理中…" : "教师"}
      </button>
      <label className="member-cohort-input"><span>第</span><input
        type="number"
        min="1"
        max="999"
        value={draft}
        disabled={Boolean(busyKey)}
        aria-label={`${member.user.displayName}的学员期数`}
        onChange={(event) => setCohortDrafts((current) => ({ ...current, [member.user.id]: event.target.value }))}
      /><span>期</span></label>
      {cohortChanged && <button type="button" disabled={Boolean(busyKey)} onClick={() => void updateRole(member, "student", true, parsedDraft)}>保存期数</button>}
      <button type="button" className={studentEnabled ? "active" : ""} aria-pressed={studentEnabled} disabled={Boolean(busyKey)} onClick={() => roleAction(member, "student")}>
        <span aria-hidden="true">{studentEnabled ? "✓" : "+"}</span>{busyKey === `${member.user.id}:student` ? "处理中…" : "学生"}
      </button>
    </div>;
  };
  const superAdmins = members.filter((member) => member.superAdmin);
  const teachers = members.filter((member) => member.roles.includes("teacher"));
  const students = members.filter((member) => member.roles.includes("student"));
  const cohorts = [...new Set(students.map((member) => member.cohortNo).filter((value): value is number => Number.isInteger(value)))].sort((a, b) => a - b);
  const unassignedStudents = students.filter((member) => !member.cohortNo);
  const cohortFilterActive = filter === "cohorts" || filter === "unassigned" || filter.startsWith("cohort:");
  const filterCards: { filter: MemberFilter; label: string; count: number; note: string; active: boolean }[] = [
    { filter: "superAdmin", label: "超级管理员", count: superAdmins.length, note: "查看超级管理员", active: filter === "superAdmin" },
    { filter: "teacher", label: "教师", count: teachers.length, note: "查看教师成员", active: filter === "teacher" },
    { filter: "student", label: "学生", count: students.length, note: "查看全部学员", active: filter === "student" },
    { filter: "cohorts", label: "学员期数", count: cohorts.length, note: "按第 X 期查看学员", active: cohortFilterActive },
  ];
  const cohortGroups = [
    ...cohorts.map((cohortNo) => ({ label: `第 ${cohortNo} 期`, members: students.filter((member) => member.cohortNo === cohortNo) })),
    ...(unassignedStudents.length ? [{ label: "未分期", members: unassignedStudents }] : []),
  ];
  const filterOptions: { value: MemberFilter; label: string }[] = [
    { value: "all", label: `全部 ${members.length}` },
    { value: "superAdmin", label: `超管 ${superAdmins.length}` },
    { value: "teacher", label: `教师 ${teachers.length}` },
    { value: "student", label: `学生 ${students.length}` },
    { value: "cohorts", label: "按期查看" },
    ...cohorts.map((cohortNo) => ({ value: `cohort:${cohortNo}` as const, label: `第 ${cohortNo} 期` })),
    ...(unassignedStudents.length ? [{ value: "unassigned" as const, label: `未分期 ${unassignedStudents.length}` }] : []),
  ];
  const visibleMembers = members.filter((member) => {
    if (filter === "all") return true;
    if (filter === "superAdmin") return member.superAdmin;
    if (filter === "cohorts") return member.roles.includes("student");
    if (filter === "teacher" || filter === "student") return member.roles.includes(filter);
    if (filter === "unassigned") return member.roles.includes("student") && !member.cohortNo;
    return member.roles.includes("student") && member.cohortNo === Number(filter.slice("cohort:".length));
  });
  const listTitle = filter === "all" ? "超管、教师与分期学员"
    : filter === "superAdmin" ? "超级管理员"
    : filter === "teacher" ? "教师"
    : filter === "student" ? "全部学员"
    : filter === "cohorts" ? "按期查看学员"
    : filter === "unassigned" ? "未分期学员"
    : `第 ${filter.slice("cohort:".length)} 期学员`;
  const renderMembers = (items: CourseMember[]) => <div className="member-list">
    {items.map((member) => <article key={member.user.id}><MemberIdentity member={member} /><RoleButtons member={member} /></article>)}
  </div>;
  return <div className="member-management">
    <div className="metric-grid member-metrics" role="group" aria-label="成员分类">
      {filterCards.map((card) => <button
        key={card.filter}
        type="button"
        className="metric member-filter-card"
        aria-label={card.label}
        aria-pressed={card.active}
        aria-controls="member-results"
        disabled={loading}
        onClick={() => setFilter(filter === card.filter ? "all" : card.filter)}
      ><span>{card.label}</span><strong>{loading ? "—" : String(card.count)}</strong><small>{card.note}</small></button>)}
    </div>
    <section className="panel member-search-panel">
      <header className="panel-head"><div><span>添加成员</span><h2>查找繁星注册用户</h2></div><span className="data-badge">精确查找</span></header>
      <form className="member-search-form" onSubmit={search}>
        <label><span className="sr-only">繁星 ID 或手机号</span><input value={query} onChange={(event) => setQuery(event.target.value)} maxLength={80} autoComplete="off" placeholder="输入完整繁星 ID 或手机号" /></label>
        <button className="button primary" type="submit" disabled={searching || !query.trim()}>{searching ? "查找中…" : "查找用户"}</button>
      </form>
      <p className="member-search-note">不会浏览或导入繁星用户目录；只有精确匹配到的注册账号才能授权。</p>
      {candidate && <article className="member-candidate"><MemberIdentity member={candidate} /><RoleButtons member={candidate} /></article>}
    </section>
    <section className="panel member-list-panel" id="member-results" aria-labelledby="member-results-title" aria-busy={loading}>
      <header className="panel-head member-list-head"><div><span>训练营成员</span><h2 id="member-results-title" aria-live="polite">{listTitle}</h2></div><div className="member-filters" role="group" aria-label="筛选成员">
        {filterOptions.map((option) => <button type="button" key={option.value} className={filter === option.value ? "active" : ""} aria-pressed={filter === option.value} aria-controls="member-results" disabled={loading} onClick={() => setFilter(option.value)}>{option.label}</button>)}
      </div></header>
      {loading ? <div className="loading-panel">正在读取成员…</div> : visibleMembers.length === 0 ? <div className="empty-state"><strong>当前分类还没有成员</strong><p>在上方精确查找繁星注册用户并授予角色。</p></div>
        : filter === "cohorts" ? <div className="member-cohort-groups">{cohortGroups.map((group) => <section className="member-cohort-group" key={group.label} aria-label={group.label}>
          <header><h3>{group.label}</h3><span>{group.members.length} 位学员</span></header>
          {renderMembers(group.members)}
        </section>)}</div>
        : renderMembers(visibleMembers)}
    </section>
    {message && <p className="member-management-message" role="status">{message}</p>}
    <FanxingActionConfirmDialog
      open={Boolean(pendingRevoke)}
      title={`撤销${pendingRevoke?.role === "teacher" ? "教师" : "学生"}权限`}
      description={`撤销后，${pendingRevoke?.member.user.displayName || "该账号"}将立即无法进入对应工作端。是否继续？`}
      confirmLabel="确认撤销"
      tone="danger"
      onClose={() => setPendingRevoke(null)}
      onConfirm={() => pendingRevoke && void updateRole(pendingRevoke.member, pendingRevoke.role, false)}
    />
  </div>;
}

function MemberIdentity({ member }: { member: CourseMember }) {
  const name = member.user.displayName || member.user.username || "繁星用户";
  const hasIdentity = member.superAdmin || member.roles.length > 0;
  return <div className="member-identity">
    <UserAvatar profile={member.user} />
    <div><strong>{name}</strong><small>{member.user.displayId ? `繁星 ID ${member.user.displayId}` : "繁星注册用户"}</small></div>
    <div className="member-role-tags">
      {!hasIdentity && <span>待授权</span>}
      {member.superAdmin && <span className="super-admin">超级管理员</span>}
      {member.roles.includes("teacher") && <span className="teacher">教师</span>}
      {member.roles.includes("student") && <span className="student">学生</span>}
      {member.roles.includes("student") && <span className="cohort">{member.cohortNo ? `第 ${member.cohortNo} 期` : "未分期"}</span>}
    </div>
  </div>;
}

function Metric({ label, value, note }: { label: string; value: string; note: string }) {
  return <article className="metric"><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

function LegacyTeacherPage({ userId }: { userId: string }) {
  const [course, setCourse] = useState<CoursePayload | null>(null);
  const [data, setData] = useState<{ user: PublicProfile; assignments: Assignment[]; submissions: Submission[] } | null>(null);
  const [message, setMessage] = useState("");
  useEffect(() => { api<{ ok: boolean } & CoursePayload>("/api/course").then(setCourse).catch(() => undefined); }, []);
  const load = useCallback(() => api<{ user: PublicProfile; assignments: Assignment[]; submissions: Submission[] }>(`/api/teacher/${encodeURIComponent(userId)}/dashboard`).then(setData).catch(() => undefined), [userId]);
  useEffect(() => { void load(); }, [load]);
  const createAssignment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api(`/api/teacher/${encodeURIComponent(userId)}/assignments`, { method: "POST", body: JSON.stringify({ lessonCode: form.get("lessonCode"), title: form.get("title"), requirements: form.get("requirements") }) }); setMessage("任务已发布到学生空间。"); event.currentTarget.reset(); await load(); }
    catch (value) { setMessage(value instanceof Error ? value.message : "发布失败"); }
  };
  const review = async (event: FormEvent<HTMLFormElement>, submissionId: string) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api(`/api/teacher/${encodeURIComponent(userId)}/submissions/${encodeURIComponent(submissionId)}/reviews`, { method: "POST", body: JSON.stringify({ decision: form.get("decision"), timecode: form.get("timecode"), comment: form.get("comment") }) }); setMessage("反馈已绑定到该提交版本。"); await load(); }
    catch (value) { setMessage(value instanceof Error ? value.message : "反馈失败"); }
  };
  const pending = data?.submissions.filter((submission) => submission.status === "pending_review") || [];
  return <InternalShell role="teacher" title="今天教什么，也今天把项目往前推。" subtitle="课程、任务、提交和反馈使用同一份事实；D01—D03 为完整课，其余为待展开骨架。" profile={data?.user}>
    {!data ? <div className="loading-panel">正在读取教师工作台…</div> : <>
      <div className="metric-grid"><Metric label="课程版本" value="V1" note="40天结构化种子" /><Metric label="今日课程" value="D01" note="个人项目立项" /><Metric label="待审提交" value={String(pending.length)} note="按确定版本审阅" /><Metric label="风险提醒" value="0" note="规则待试班校准" /></div>
      <div className="teacher-layout"><section className="panel today-panel"><header className="panel-head"><div><span>今日教学</span><h2>{course?.days[0].title || "D01 读取中"}</h2></div><span className="status ready">完整课</span></header><p>{course?.days[0].objective}</p><div className="today-sessions">{course?.days[0].sessions?.map(([teacher, student], index) => <div key={teacher}><span>{index + 1}</span><p><strong>{teacher}</strong><small>{student}</small></p></div>)}</div><Link href="/plan" className="text-link">打开完整课程页 →</Link></section>
      <section className="panel"><header className="panel-head"><div><span>发布任务</span><h2>同步到学生空间</h2></div><span className="data-badge">当前班期</span></header><form className="inline-form" onSubmit={createAssignment}><label><span>教学日</span><select name="lessonCode" defaultValue="D01">{course?.days.map((day) => <option key={day.code}>{day.code}</option>)}</select></label><label><span>任务标题</span><input name="title" required maxLength={120} placeholder="例如：提交选题 V2" /></label><label><span>要求与验收依据</span><textarea name="requirements" required maxLength={1200} rows={4} placeholder="说明交什么、怎样验收，不写保证性结果" /></label><button className="button primary" type="submit">发布任务</button></form></section></div>
      <section className="panel review-panel"><header className="panel-head"><div><span>待审与历史</span><h2>提交版本审阅</h2></div><span className="data-badge">{data.submissions.length} 个版本</span></header>{data.submissions.length === 0 ? <div className="empty-state"><strong>还没有学生提交</strong><p>切换学生端提交一个版本后，这里会出现待审项。</p></div> : <div className="submission-grid">{data.submissions.map((submission) => <article key={submission.id}><header><span>{submission.lessonCode} · V{submission.version}</span><span className={`submission-status ${submission.status}`}>{submission.status === "pending_review" ? "待审" : submission.status === "revision" ? "需返修" : "已通过"}</span></header><h3>{submission.assignmentTitle}</h3><p>{submission.note}</p><small>附件说明：{submission.attachmentLabel || "无"} · {formatTime(submission.createdAt)}</small>{submission.review && <blockquote><strong>{submission.review.decision === "approved" ? "通过" : "返修"}{submission.review.timecode ? ` · ${submission.review.timecode}` : ""}</strong>{submission.review.comment}</blockquote>}{submission.status === "pending_review" && <form onSubmit={(event) => review(event, submission.id)}><div className="form-row"><select name="decision" defaultValue="revision"><option value="revision">要求返修</option><option value="approved">通过</option></select><input name="timecode" maxLength={24} placeholder="时间点（选填）" /></div><textarea name="comment" required maxLength={1200} rows={3} placeholder="写给这个版本的具体反馈" /><button className="button primary small" type="submit">保存反馈</button></form>}</article>)}</div>}{message && <p className="form-message success">{message}</p>}</section>
    </>}
  </InternalShell>;
}

function LegacyStudentPage({ status, userId }: { status: StatusPayload | null; userId: string }) {
  const [data, setData] = useState<{ user: PublicProfile; project: { title: string; stage: string; targetDurationMs: number; acceptedDurationMs: number }; assignments: Assignment[]; submissions: Submission[]; costs: Cost[]; knownCostFen: number } | null>(null);
  const [message, setMessage] = useState("");
  const load = useCallback(() => api<typeof data & { ok: boolean }>(`/api/student/${encodeURIComponent(userId)}/dashboard`).then((value) => setData(value)).catch(() => undefined), [userId]);
  useEffect(() => { void load(); }, [load]);
  const submitVersion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api(`/api/student/${encodeURIComponent(userId)}/submissions`, { method: "POST", body: JSON.stringify({ assignmentId: form.get("assignmentId"), note: form.get("note"), attachmentLabel: form.get("attachmentLabel") }) }); setMessage("新版本已保存并进入待审；附件当前只是说明，尚未上传到 COS。"); event.currentTarget.reset(); await load(); }
    catch (value) { setMessage(value instanceof Error ? value.message : "提交失败"); }
  };
  const addCost = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api(`/api/student/${encodeURIComponent(userId)}/costs`, { method: "POST", body: JSON.stringify({ category: form.get("category"), tool: form.get("tool"), amount: form.get("amount"), currency: form.get("currency"), accountingBasis: "project_usage", note: form.get("note") }) }); setMessage("成本记录已保存；未知费用不会按 0 计算。"); event.currentTarget.reset(); await load(); }
    catch (value) { setMessage(value instanceof Error ? value.message : "保存失败"); }
  };
  return <InternalShell role="student" title="这里不是听课列表，是我的剧。" subtitle="项目、任务、版本、反馈、成本和文件角色围绕同一个个人项目组织。" profile={data?.user}>
    {!data ? <div className="loading-panel">正在读取个人项目…</div> : <>
      <section className="student-project-card"><div className="project-identity"><UserAvatar profile={data.user} large /><div><small>{data.user.displayName} · 繁星 ID {data.user.displayId}</small><h2>{data.project.title}</h2><p>阶段：{data.project.stage} · 一人一项目 · 目标 100 分钟</p></div></div><div className="progress-ring" style={{ "--progress": `${Math.max(0.8, data.project.acceptedDurationMs / data.project.targetDurationMs * 100)}%` } as React.CSSProperties}><strong>{(data.project.acceptedDurationMs / 60_000).toFixed(1)}</strong><span>/ 100 分钟</span></div><div className="project-stats"><div><span>已验收</span><strong>{(data.project.acceptedDurationMs / 60_000).toFixed(1)} 分钟</strong></div><div><span>待审版本</span><strong>{data.submissions.filter((item) => item.status === "pending_review").length}</strong></div><div><span>返修版本</span><strong>{data.submissions.filter((item) => item.status === "revision").length}</strong></div><div><span>已记录成本</span><strong>{money(data.knownCostFen)}</strong></div></div></section>
      <div className="student-layout"><section className="panel"><header className="panel-head"><div><span>今天做什么</span><h2>已发布任务</h2></div><span className="data-badge">{data.assignments.length} 项</span></header><div className="task-list">{data.assignments.map((assignment) => <article key={assignment.id}><span>{assignment.lesson_code}</span><div><h3>{assignment.title}</h3><p>{assignment.requirements}</p></div></article>)}</div><form className="inline-form separated" onSubmit={submitVersion}><h3>提交一个确定版本</h3><label><span>对应任务</span><select name="assignmentId">{data.assignments.map((assignment) => <option value={assignment.id} key={assignment.id}>{assignment.lesson_code} · {assignment.title}</option>)}</select></label><label><span>版本说明</span><textarea name="note" required maxLength={1600} rows={4} placeholder="写明这版改了什么、还有什么未知" /></label><label><span>附件说明 <i>当前不上传文件</i></span><input name="attachmentLabel" maxLength={160} placeholder="例如：立项书-v1.pdf（仅说明）" /></label><button className="button primary" type="submit">提交版本</button></form></section>
      <section className="panel"><header className="panel-head"><div><span>最近反馈</span><h2>按版本追溯</h2></div></header>{data.submissions.length === 0 ? <div className="empty-state"><strong>还没有提交</strong><p>提交后状态先是“待审”，不会直接显示通过。</p></div> : <div className="feedback-list">{data.submissions.map((submission) => <article key={submission.id}><header><strong>{submission.lessonCode} · V{submission.version}</strong><span className={`submission-status ${submission.status}`}>{submission.status === "pending_review" ? "待审" : submission.status === "revision" ? "需返修" : "已通过"}</span></header><p>{submission.note}</p>{submission.review ? <blockquote><small>教师反馈{submission.review.timecode ? ` · ${submission.review.timecode}` : ""}</small>{submission.review.comment}</blockquote> : <small>老师尚未反馈</small>}</article>)}</div>}</section></div>
      <div className="student-layout lower"><section className="panel"><header className="panel-head"><div><span>制作成本</span><h2>项目耗用台账</h2></div><span className="data-badge">不含学费与 COS</span></header><div className="cost-list">{data.costs.length === 0 ? <p>还没有成本记录；未知费用不会默认记作 0。</p> : data.costs.map((cost) => <div key={cost.id}><span><strong>{cost.tool}</strong><small>{cost.category} · {formatTime(cost.created_at)}</small></span><b>{money(cost.amount_fen, cost.currency)}</b></div>)}</div><form className="cost-form" onSubmit={addCost}><select name="category" defaultValue="image"><option value="text">文本 / 剧本</option><option value="image">图片生成</option><option value="video">视频生成</option><option value="audio">声音</option><option value="editing">编辑工具</option><option value="material">合法素材</option></select><input name="tool" required maxLength={80} placeholder="平台 / 工具" /><div className="form-row"><input name="amount" type="number" min="0" max="1000000" step="0.01" placeholder="金额；留空=待核" /><select name="currency" defaultValue="CNY"><option>CNY</option><option>USD</option></select></div><input name="note" maxLength={500} placeholder="本项目耗用说明" /><button className="button ghost small" type="submit">添加成本</button></form></section>
      <section className="panel"><header className="panel-head"><div><span>剧集与文件</span><h2>预览 / 母版分离</h2></div><IntegrationPill integration={status?.integrations.cos} /></header><div className="file-slots"><article><span>PREVIEW</span><h3>在线预览</h3><p>用于课堂审片与时间点反馈。</p><button disabled type="button">预览待上传</button></article><article><span>MASTER</span><h3>原始母版</h3><p>私有留档与按权限下载，不作网页默认播放源。</p><button disabled type="button">服务端授权待接入</button></article></div><p className="boundary-box">桶：<code>duanju-school-1335253334</code> · 地域：<code>ap-beijing</code><br />已确认资源目标，但没有长期密钥进入前端，也没有把“选择文件”伪装成上传成功。</p></section></div>
      {message && <p className="floating-message" role="status">{message}</p>}
    </>}
  </InternalShell>;
}

function TeacherOutline({ userId, caseId, week = 1, day, hour }: { userId: string; caseId?: TeachingCaseId; week?: number; day?: number; hour?: number }) {
  const [cases, setCases] = useState<TeachingCaseSummary[]>([]);
  const [loaded, setLoaded] = useState<CoursePayload | null>(null);
  const [error, setError] = useState("");
  const [catalogError, setCatalogError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setCatalogError("");
    api<{ cases: TeachingCaseSummary[] }>("/api/course/cases", { signal: controller.signal }).then((data) => setCases(data.cases)).catch((reason) => {
      if (!controller.signal.aborted) setCatalogError(reason instanceof Error ? reason.message : "案例读取失败");
    });
    return () => controller.abort();
  }, [retry]);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    if (caseId) cachedCourse<CoursePayload>(userId, "teacher", caseId, (etag) => authenticatedFetch(`/api/course?case=${caseId}`, {
      cache: "no-store",
      signal: AbortSignal.timeout(15_000),
      headers: etag ? { "If-None-Match": etag } : {},
    }), () => verifiedLoginSession?.user.id === userId && readLoginSession()?.user.id === userId).then((data) => {
      if (!controller.signal.aborted) setLoaded(data);
    }).catch((reason) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "课程读取失败"); });
    return () => controller.abort();
  }, [userId, caseId, retry]);
  // 切换时立即隐藏上一案例；迟到的请求不得把A内容覆盖到B选择上。
  const course = caseId && loaded?.caseId === caseId && !error ? loaded : null;
  const selectedCase = course?.teachingCase;
  const selectedWeek = course?.weeks?.[week - 1];
  const weekDays = course?.days.filter((item) => item.week === week) ?? [];
  const weekHours = weekDays.reduce((total, item) => total + item.productionHours + item.operationsHours, 0);
  const active = day ? weekDays.find((item) => item.day === day) : undefined;
  const selectedSession = active && hour ? active.sessions?.[hour - 1] : undefined;
  const selectedSpec = active && hour ? active.hourSpecs?.[hour - 1] : undefined;
  const pending = "课时资料未读取成功，请刷新后重试。";
  return <section className="course-workspace teacher-course-workspace teacher-outline-workspace" aria-label="教学大纲">
    <div className="teacher-case-selector" role="group" aria-label="案例选择">{cases.map((item) => <button type="button" key={item.id} aria-pressed={caseId === item.id} className={caseId === item.id ? "active" : ""} onClick={() => navigateTeacherOutline(userId, item.id, week, day, hour)}><strong>{item.label}</strong><small>《{item.title}》</small></button>)}</div>
    {(error || catalogError) && <div className="error-panel" role="alert">课程读取失败：{error || catalogError} <button type="button" onClick={() => setRetry((value) => value + 1)}>重试</button></div>}
    {!caseId && cases.length > 0 && <p className="teacher-case-prompt">先选择本班使用的教学案例，再查看对应的周次、教学日和课时。</p>}
    {((caseId && !course && !error) || (!cases.length && !catalogError)) && <p className="teacher-case-prompt" role="status">正在读取案例课程…</p>}
    {course && caseId && <>
      {selectedCase && <details key={caseId} className="teacher-case-guide"><summary>《{selectedCase.title}》 · 案例设定与50集规划</summary><p>{selectedCase.logline}</p><p>{selectedCase.format}。{selectedCase.status}</p><p>核心看点：{selectedCase.promise}</p><p>全剧结局：{selectedCase.ending}</p><h3>人物与关系</h3><ul>{selectedCase.characters.map(([name, description]) => <li key={name}><strong>{name}</strong> · {description}</li>)}</ul><h3>主要场景与道具</h3><ul>{[...selectedCase.locations, ...selectedCase.props].map((item) => <li key={item}>{item}</li>)}</ul><h3>全剧分集规划</h3><ol>{selectedCase.episodes.map((episode) => <li key={episode.id}><strong>{episode.id} · {episode.title}</strong><p>{episode.action}。集尾：{episode.hook}。</p></li>)}</ol></details>}
      <div className="week-map" role="group" aria-label="八周教学大纲">{course.weeks?.map((item) => <button key={item.week} type="button" aria-pressed={week === item.week} onClick={() => navigateTeacherOutline(userId, caseId, item.week)} className={week === item.week ? "active" : ""}><small>WEEK {String(item.week).padStart(2, "0")}</small><strong>{item.title}</strong></button>)}</div>
      <div className="teacher-outline-week-note" aria-live="polite"><span>{selectedWeek?.summary}</span><small>5 天 · {weekHours} 课时</small></div>
      <div className="teacher-outline-day-tabs" role="group" aria-label={`第${week}周教学日选择`}>{weekDays.map((item) => <button key={item.code} type="button" aria-pressed={day === item.day} className={day === item.day ? "active" : ""} onClick={() => navigateTeacherOutline(userId, caseId, week, item.day)}><small>{item.code}</small><strong>{item.title}</strong></button>)}</div>
      {active && <article className="lesson-detail teacher-outline-detail" aria-live="polite"><header><h2>{active.code} · {active.title}</h2><span>{active.productionHours} 制作课时{active.operationsHours > 0 ? ` · ${active.operationsHours} 发行运营课时` : ""}</span></header>
        <details key={`${caseId}-${active.code}-${hour || "day"}`} className="teacher-outline-day-summary" open={!hour}>
          <summary>当天概况<span>目标 / 准备 / 提交 / 验收</span></summary>
          <p className="lesson-objective">{active.objective}</p>
          <p className="teacher-lesson-preparation"><span>课前准备：{active.prerequisite}</span><span>{active.milestone} · 教案已细化，试班后校准</span></p>
          <dl className="lesson-rules"><div><dt>当天提交</dt><dd>{active.deliverable}</dd></div><div><dt>验收标准</dt><dd>{active.acceptance}</dd></div><div><dt>课外继续</dt><dd>{active.homework}</dd></div></dl>
        </details>
        <section className="teacher-outline-hours" aria-label="今天的课包含什么"><div className="teacher-outline-hour-tabs" role="group" aria-label={`${active.code}课时选择`}>{Array.from({ length: active.productionHours + active.operationsHours }, (_, index) => <button key={index} type="button" aria-pressed={hour === index + 1} className={hour === index + 1 ? "active" : ""} onClick={() => navigateTeacherOutline(userId, caseId, week, active.day, index + 1)}><small>第 {index + 1} 课时{index >= active.productionHours ? " · 发行运营" : ""}</small><strong>{active.sessions?.[index]?.[0] || "课时内容待展开"}</strong></button>)}</div>
          {hour && <section key={`${caseId}-${active.code}-${hour}`} className="teacher-outline-hour" aria-label={`第${hour}课时详细课程规范`}><header><h3>H{hour} · {selectedSession?.[0] || "课时资料未读取"}</h3><span>{hour <= active.productionHours ? "制作" : "发行运营"}{selectedSpec ? ` · ${selectedSpec.durationMinutes} 分钟` : ""}</span></header><dl>
            <div><dt>课程</dt><dd>{selectedSession?.[0] || pending}{selectedSpec && <details className="teacher-lesson-resource teacher-lesson-agenda"><summary>45分钟授课安排</summary><ol>{selectedSpec.agenda.map((step, index) => {
              const start = selectedSpec.agenda.slice(0, index).reduce((minutes, item) => minutes + item.minutes, 0);
              return <li key={step.label}><strong>{start}—{start + step.minutes} 分钟 · {step.label}</strong><span>{step.detail}</span></li>;
            })}</ol></details>}</dd></div>
            <div><dt>教学目标</dt><dd>{selectedSpec?.goal || pending}</dd></div>
            <div><dt>教学内容</dt><dd>{selectedSpec?.content || pending}{selectedSpec && <p className="teacher-lesson-demo"><strong>教师示范：</strong>{selectedSpec.demo}</p>}</dd></div>
            <div><dt>课堂任务</dt><dd>{selectedSession?.[1] || pending}</dd></div>
            <div><dt>课程资料</dt><dd>{selectedSpec ? <><details className="teacher-lesson-resource"><summary>{selectedSpec.resource.title} · 查看模板与案例底稿</summary><ul>{selectedSpec.resource.fields.map((field) => <li key={field}>{field}</li>)}</ul><p className="teacher-case-example"><strong>课堂示例：</strong>{selectedSpec.resource.example}</p>{selectedSpec.resource.steps && <ol>{selectedSpec.resource.steps.map((step) => <li key={step}>{step}</li>)}</ol>}</details>{selectedSpec.references.length > 0 && <ul className="teacher-lesson-references">{selectedSpec.references.map((reference) => <li key={reference.url}><a href={reference.url} target="_blank" rel="noopener noreferrer">{reference.title} ↗</a></li>)}</ul>}</> : pending}</dd></div>
            <div><dt>学生应完成</dt><dd>{selectedSpec?.result || pending}</dd></div>
            <div><dt>教师检查点</dt><dd>{selectedSpec?.check || pending}{selectedSpec && <p className="teacher-lesson-demo"><strong>常见问题与纠正：</strong>{selectedSpec.mistake}</p>}</dd></div>
          </dl></section>}
        </section>
      </article>}
    </>}
  </section>;
}

function useTeachingDate() {
  const readDate = () => {
    const parts = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" }).formatToParts(new Date());
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || "";
    return { iso: `${get("year")}-${get("month")}-${get("day")}`, label: `${get("year")}年${get("month")}月${get("day")}日`, weekday: get("weekday") };
  };
  const [date, setDate] = useState(readDate);
  useEffect(() => {
    const update = () => setDate(readDate());
    const timer = window.setInterval(update, 30_000);
    document.addEventListener("visibilitychange", update);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", update); };
  }, []);
  return date;
}

type TeacherDashboard = { user: PublicProfile; assignments: Assignment[]; submissions: Submission[]; teachingCohorts?: { id: TeachingCohortId; cohortNo: number | null }[] };

function TeacherPage({ userId, view, todayCohort = "morning", todaySession = 1, planCohort = "morning", planWeek, planDay, outlineCase, outlineWeek, outlineDay, outlineHour }: { userId: string; view: PortalView; todayCohort?: TeachingCohortId; todaySession?: number; planCohort?: TeachingCohortId; planWeek?: number; planDay?: number; outlineCase?: TeachingCaseId; outlineWeek?: number; outlineDay?: number; outlineHour?: number }) {
  const [course, setCourse] = useState<CoursePayload | null>(null);
  const [courseError, setCourseError] = useState("");
  const [data, setData] = useState<TeacherDashboard | null>(null);
  const teachingDate = useTeachingDate();
  const [student, setStudent] = useState<StudentDashboard | null>(null);
  const [message, setMessage] = useState("");
  const [pendingOrder, setPendingOrder] = useState<"newest" | "oldest">("newest");
  const [feedbackStatus, setFeedbackStatus] = useState<"all" | "revision" | "approved">("all");
  const [feedbackDay, setFeedbackDay] = useState("all");
  const [feedbackTask, setFeedbackTask] = useState("all");
  const [projectDetail, setProjectDetail] = useState(false);
  useEffect(() => {
    if (view === "outline" || view === "scripts" || view === "dramas" || view === "cache" || course) return;
    let mounted = true;
    cachedCourse<CoursePayload>(userId, "teacher", "a", (etag) => authenticatedFetch("/api/course", {
      headers: etag ? { "If-None-Match": etag } : {},
    }), () => verifiedLoginSession?.user.id === userId && readLoginSession()?.user.id === userId).then((value) => { if (mounted) setCourse(value); }).catch((value) => {
      if (mounted) setCourseError(value instanceof Error ? value.message : "课程读取失败");
    });
    return () => { mounted = false; };
  }, [view, course, userId]);
  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      api<TeacherDashboard>(`/api/teacher/${encodeURIComponent(userId)}/dashboard`),
      api<StudentDashboard>(`/api/student/${encodeURIComponent(userId)}/dashboard`),
    ]);
    if (results[0].status === "fulfilled") setData(results[0].value);
    if (results[1].status === "fulfilled") setStudent(results[1].value);
  }, [userId]);
  useEffect(() => { if (view !== "cache") void load(); }, [load, view]);
  const createAssignment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api(`/api/teacher/${encodeURIComponent(userId)}/assignments`, { method: "POST", body: JSON.stringify({ lessonCode: form.get("lessonCode"), title: form.get("title"), requirements: form.get("requirements") }) });
      setMessage("任务已发布到学生空间。"); event.currentTarget.reset(); await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "发布失败"); }
  };
  const review = async (event: FormEvent<HTMLFormElement>, submissionId: string) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api(`/api/teacher/${encodeURIComponent(userId)}/submissions/${encodeURIComponent(submissionId)}/reviews`, { method: "POST", body: JSON.stringify({ decision: form.get("decision"), timecode: form.get("timecode"), comment: form.get("comment") }) });
      setMessage("反馈已绑定到该提交版本。"); await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "反馈失败"); }
  };
  const pending = data?.submissions.filter((item) => item.status === "pending_review") || [];
  const reviewed = data?.submissions.filter((item) => item.status !== "pending_review") || [];
  const visibleFeedback = reviewed.filter((item) =>
    (feedbackStatus === "all" || item.status === feedbackStatus)
    && (feedbackDay === "all" || item.lessonCode === feedbackDay)
    && (feedbackTask === "all" || item.assignmentId === feedbackTask)
  );
  const today = course?.days[0];
  const teachingCohorts = [
    { id: "morning", label: "上午班", time: "09:00—12:00" },
    { id: "afternoon", label: "下午班", time: "14:00—17:00" },
    { id: "evening", label: "晚间班", time: "19:00—22:00" },
  ] as const;
  const cohortLabel = (id: TeachingCohortId) => {
    const number = data?.teachingCohorts?.find((item) => item.id === id)?.cohortNo;
    return typeof number === "number" && Number.isInteger(number) && number > 0 ? `第 ${number} 期学员` : "学员期数待绑定";
  };
  const activeCohort = teachingCohorts.find((cohort) => cohort.id === (view === "today" ? todayCohort : planCohort))!;
  const activeSession = today?.sessions?.[todaySession - 1];
  const planWeekDays = planWeek ? course?.days.filter((day) => day.week === planWeek) ?? [] : [];
  const selectedPlanDay = planDay ? planWeekDays.find((day) => day.day === planDay) : undefined;
  const titles: Record<PortalView, string> = {
    overview: "教学总览", outline: "教学大纲", today: "今日课程", course: "教学计划", tasks: "任务管理", pending: "待审提交",
    feedback: "版本反馈", projects: "学员项目", files: "成片与文件", costs: "成本概览", materials: "课程资料", scripts: "剧本示范", dramas: "短剧示范", members: "成员管理", cache: "本地缓存",
  };
  const renderSubmission = (item: Submission) => <article key={item.id} className="portal-version-card">
    <header><div><small>{student?.user.displayName || "当前学员"} · {item.lessonCode} · V{item.version} · {formatTime(item.createdAt)}</small><h3>{item.assignmentTitle}</h3></div><span className={`submission-status ${item.status}`}>{item.status === "pending_review" ? "待审" : item.status === "revision" ? "需返修" : "已通过"}</span></header>
    <p>{item.note}</p><small>附件说明：{item.attachmentLabel || "无"}（当前未接入真实文件上传）</small>
    {item.review && <blockquote><strong>{item.review.decision === "approved" ? "通过" : "要求返修"} · {formatTime(item.review.createdAt)}{item.review.timecode ? ` · ${item.review.timecode}` : ""}</strong><p>{item.review.comment}</p></blockquote>}
    {item.status === "pending_review" && <form className="portal-review-form" onSubmit={(event) => review(event, item.id)}><div className="form-row"><select name="decision" defaultValue="revision"><option value="revision">要求返修</option><option value="approved">通过</option></select><input name="timecode" maxLength={24} placeholder="时间点（选填）" /></div><textarea name="comment" required maxLength={1200} rows={3} placeholder="写给这个确定版本的具体反馈" /><button className="button primary small" type="submit">保存反馈并移出待审</button></form>}
  </article>;
  const go = (next: PortalView) => navigatePortalView("teacher", userId, next);
  return <InternalShell role="teacher" userId={userId} title={titles[view]} subtitle="课程、任务、项目和每次版本反馈，围绕同一个学员项目持续推进。" profile={data?.user} activeView={view} onSelectView={go} hideWorkspaceHead>
    {view === "cache" ? <TrainingCachePanel userId={userId} role="teacher" /> : view === "outline" ? <TeacherOutline userId={userId} caseId={outlineCase} week={outlineWeek} day={outlineDay} hour={outlineHour} /> : view === "scripts" || view === "dramas" ? <TeacherDemonstrations key={view} userId={userId} kind={view} /> : !data ? <div className="loading-panel">正在读取教师工作台…</div> : <>
      {view === "overview" && <>
        <div className="metric-grid"><Metric label="当前教学日" value="D01" note="试班课程种子" /><Metric label="今日课程" value={today?.title || "读取中"} note={today?.status === "expanded" ? "完整教案" : "课程骨架"} /><Metric label="待审提交" value={String(pending.length)} note="按确定版本处理" /><Metric label="异常提醒" value="—" note="风险规则尚未建立" /></div>
        <div className="portal-dashboard-grid">
          <section className="panel"><header className="panel-head"><div><span>今天讲什么</span><h2>{today?.title || "D01"}</h2></div></header><p>{today?.objective || "正在读取课程目标"}</p><div className="portal-mini-list">{today?.sessions?.slice(0, 4).map(([teacher], index) => <span key={index}>{index + 1}. {teacher}</span>)}</div><button className="portal-text-button" type="button" onClick={() => go("today")}>查看完整教案 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>现在等我处理</span><h2>待审队列</h2></div><span className="data-badge">{pending.length} 个版本</span></header>{pending.length ? <div className="portal-mini-list">{pending.slice(0, 3).map((item) => <span key={item.id}>{student?.user.displayName || "当前学员"} · {item.lessonCode} · V{item.version} · {item.assignmentTitle}</span>)}</div> : <p>当前没有待审版本。</p>}<button className="portal-text-button" type="button" onClick={() => go("pending")}>进入待审提交 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>快捷操作</span><h2>管理今天的任务</h2></div></header><p>已发布 {data.assignments.length} 项任务。新建、查看和后续修改都从任务管理进入。</p><button className="portal-text-button" type="button" onClick={() => go("tasks")}>新建或查看任务 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>项目进度</span><h2>当前接入学员</h2></div></header>{student ? <p>{student.user.displayName} · {student.project.stage} · 已验收 {(student.project.acceptedDurationMs / 60_000).toFixed(1)} / 100 分钟</p> : <p>学员项目数据读取中。</p>}<small>目前仅接入当前开发学员，不推算班级平均值或风险人数。</small><button className="portal-text-button" type="button" onClick={() => go("projects")}>查看学员项目 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>最近做过什么</span><h2>最近反馈</h2></div></header>{reviewed.length ? <div className="portal-mini-list">{reviewed.slice(0, 3).map((item) => <span key={item.id}>{item.lessonCode} · V{item.version} · {item.status === "approved" ? "已通过" : "要求返修"}</span>)}</div> : <p>还没有已完成的审批记录。</p>}<button className="portal-text-button" type="button" onClick={() => go("feedback")}>查看反馈历史 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>项目风险</span><h2>暂不虚报“0 位异常”</h2></div></header><p>落后、返修过多和长期未提交需要班期、截止时间与规则支撑；这些条件尚未接入。</p></section>
        </div>
      </>}
      {view === "today" && <div className="portal-cohort-view teacher-course-workspace teacher-today">
        <div className="week-map teacher-cohort-map" role="tablist" aria-label="选择上课班次">
          {teachingCohorts.map((cohort) => <button type="button" role="tab" id={`teacher-cohort-tab-${cohort.id}`} aria-controls="teacher-cohort-panel" aria-selected={todayCohort === cohort.id} className={todayCohort === cohort.id ? "active" : ""} key={cohort.id} onClick={() => navigateTeacherToday(userId, cohort.id, 1)}>
            <span className="teacher-cohort-heading"><strong>{cohort.label}</strong><span className="teacher-cohort-time">{cohort.time}</span><span className="teacher-cohort-intake">{cohortLabel(cohort.id)}</span></span>
            <span className="teacher-cohort-calendar"><time dateTime={teachingDate.iso}>今日 · {teachingDate.label}</time><span>{teachingDate.weekday}</span></span>
          </button>)}
        </div>
        <section className="teacher-today-content" role="tabpanel" id="teacher-cohort-panel" aria-labelledby={`teacher-cohort-tab-${activeCohort.id}`} data-cohort={activeCohort.id}>
          <div className="course-week-intro teacher-today-intro"><div><span>{activeCohort.label} · {cohortLabel(activeCohort.id)} · {today?.code || "D01"}</span><strong>{today?.title || "课程读取中"}</strong></div><p>{today?.objective}</p><span className="status ready">{today?.status === "expanded" ? "完整课" : "待展开"}</span></div>
          <div className="teacher-today-session-tabs" role="tablist" aria-label={`${activeCohort.label}课时选择`}>{today?.sessions?.map(([teacher], index) => <button type="button" role="tab" id={`teacher-session-tab-${index + 1}`} aria-controls="teacher-session-panel" aria-selected={todaySession === index + 1} className={todaySession === index + 1 ? "active" : ""} key={`${activeCohort.id}-${index}`} onClick={() => navigateTeacherToday(userId, activeCohort.id, index + 1)}><small>第 {index + 1} 课时</small><strong>{teacher}</strong></button>)}</div>
          {activeSession && <div className="teacher-today-session-detail" role="tabpanel" id="teacher-session-panel" aria-labelledby={`teacher-session-tab-${todaySession}`}><dl className="lesson-rules"><div><dt>教师示范</dt><dd>{activeSession[0]}</dd></div><div><dt>项目推进</dt><dd>{activeSession[1]}</dd></div></dl></div>}
          <dl className="lesson-rules teacher-today-rules"><div><dt>今日产出</dt><dd>{today?.deliverable}</dd></div><div><dt>验收依据</dt><dd>{today?.acceptance}</dd></div><div><dt>课后任务</dt><dd>{today?.homework}</dd></div></dl>
          <div className="teacher-today-assignments"><h3>对应已发布任务</h3><div className="portal-mini-list">{data.assignments.filter((item) => item.lesson_code === "D01").map((item) => <span key={item.id}>{item.title} · {item.requirements}</span>)}</div></div>
        </section>
      </div>}
      {view === "course" && <div className="teacher-plan">
        <div className="portal-cohort-tabs" role="tablist" aria-label="选择教学计划班次">
          {teachingCohorts.map((cohort) => <button type="button" role="tab" id={`teacher-plan-tab-${cohort.id}`} aria-controls="teacher-plan-panel" aria-selected={planCohort === cohort.id} className={planCohort === cohort.id ? "active" : ""} key={cohort.id} onClick={() => navigateTeacherPlan(userId, cohort.id)}><strong>{cohort.label}</strong><small>{cohort.time}</small></button>)}
        </div>
        <div role="tabpanel" id="teacher-plan-panel" aria-labelledby={`teacher-plan-tab-${activeCohort.id}`} data-cohort={activeCohort.id}>
          {courseError ? <div className="error-panel">课程读取失败：{courseError}</div> : !course ? <div className="loading-panel">正在读取课程…</div> : <>
            <div className="teacher-plan-week-tabs" role="group" aria-label={`${activeCohort.label}周次选择`}>{courseWeekPlan.map(([, title], index) => <button type="button" key={index} aria-pressed={planWeek === index + 1} className={planWeek === index + 1 ? "active" : ""} onClick={() => navigateTeacherPlan(userId, activeCohort.id, index + 1)}><small>第 {index + 1} 周</small><strong>{title}</strong></button>)}</div>
            {planWeek && <div className="teacher-plan-day-tabs" role="group" aria-label={`第${planWeek}周教学日选择`}>{planWeekDays.map((day) => <button type="button" key={day.code} aria-pressed={planDay === day.day} className={planDay === day.day ? "active" : ""} onClick={() => navigateTeacherPlan(userId, activeCohort.id, planWeek, day.day)}><small>{day.code}</small><strong>{day.title}</strong></button>)}</div>}
            {selectedPlanDay && <section className="teacher-plan-selected-day" aria-label={`${selectedPlanDay.code}课程`}>
              <header><span>{activeCohort.label} · {activeCohort.time} · {selectedPlanDay.code}</span><em>{selectedPlanDay.status === "expanded" ? "完整教案" : "课程骨架"}</em></header>
              <h2>{selectedPlanDay.title}</h2>
              <p>{selectedPlanDay.objective}</p>
              {selectedPlanDay.sessions?.length ? <ol className="teacher-plan-session-grid">{selectedPlanDay.sessions.map(([teacher, learner], index) => <li key={index}><small>第 {index + 1} 课时</small><strong>{teacher}</strong><span>项目推进：{learner}</span></li>)}</ol> : <p className="teacher-plan-outline-note">本教学日目前是课程骨架，详细课时内容待展开。</p>}
              <dl className="lesson-rules"><div><dt>当天提交</dt><dd>{selectedPlanDay.deliverable}</dd></div><div><dt>验收标准</dt><dd>{selectedPlanDay.acceptance}</dd></div><div><dt>课外继续</dt><dd>{selectedPlanDay.homework}</dd></div></dl>
            </section>}
          </>}
        </div>
      </div>}
      {view === "tasks" && <div className="portal-dashboard-grid">
        <section className="panel"><header className="panel-head"><div><span>任务管理</span><h2>新建并发布任务</h2></div></header><form className="inline-form" onSubmit={createAssignment}><label><span>教学日</span><select name="lessonCode" defaultValue="D01">{course?.days.map((day) => <option key={day.code}>{day.code}</option>)}</select></label><label><span>任务标题</span><input name="title" required maxLength={120} placeholder="例如：提交选题 V2" /></label><label><span>要求与验收依据</span><textarea name="requirements" required maxLength={1200} rows={5} placeholder="说明交什么、怎样验收" /></label><button className="button primary" type="submit">发布任务</button></form><p className="portal-boundary-note">草稿、截止时间和已发布任务修改尚无后端接口，当前只提供真实发布，不伪装为已保存。</p></section>
        <section className="panel"><header className="panel-head"><div><span>已发布</span><h2>任务列表</h2></div><span className="data-badge">{data.assignments.length} 项</span></header><div className="portal-mini-list">{data.assignments.map((item) => {
          const versions = data.submissions.filter((submission) => submission.assignmentId === item.id);
          const latest = versions.sort((a, b) => b.version - a.version)[0];
          return <span key={item.id}><b>{item.lesson_code} · {item.title}</b><small>{item.requirements}</small><small>{versions.length} 个提交版本 · {latest ? `最新 V${latest.version} ${latest.status === "pending_review" ? "待审" : latest.status === "revision" ? "需返修" : "已通过"}` : "尚未提交"}</small></span>;
        })}</div><p className="portal-boundary-note">当前只有一名接入学员，尚不能计算真实班级完成率。</p></section>
      </div>}
      {view === "pending" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>只处理现在待审的版本</span><h2>待审提交</h2></div><span className="data-badge">{pending.length} 个版本</span></header><div className="portal-toolbar"><label>排序 <select value={pendingOrder} onChange={(event) => setPendingOrder(event.target.value as "newest" | "oldest")}><option value="newest">最近提交</option><option value="oldest">最早提交</option></select></label><small>当前仅接入 {student?.user.displayName || "一名学员"} 的项目；附件为说明文字，非已上传文件。</small></div>{pending.length ? <div className="portal-review-list">{[...pending].sort((a, b) => pendingOrder === "newest" ? b.createdAt.localeCompare(a.createdAt) : a.createdAt.localeCompare(b.createdAt)).map(renderSubmission)}</div> : <div className="empty-state"><strong>待审队列已清空</strong><p>学生提交新版本后会进入这里；处理后自动移出。</p></div>}</section>}
      {view === "feedback" && <section className="panel portal-wide-panel">
        <header className="panel-head"><div><span>已完成的审批证据链</span><h2>版本反馈</h2></div><span className="data-badge">{reviewed.length} 条记录</span></header>
        <div className="portal-toolbar portal-feedback-filters">
          <label>教学日 <select value={feedbackDay} onChange={(event) => setFeedbackDay(event.target.value)}><option value="all">全部教学日</option>{[...new Set(reviewed.map((item) => item.lessonCode))].map((day) => <option key={day} value={day}>{day}</option>)}</select></label>
          <label>任务 <select value={feedbackTask} onChange={(event) => setFeedbackTask(event.target.value)}><option value="all">全部任务</option>{data.assignments.map((assignment) => <option key={assignment.id} value={assignment.id}>{assignment.title}</option>)}</select></label>
          <label>状态 <select value={feedbackStatus} onChange={(event) => setFeedbackStatus(event.target.value as "all" | "revision" | "approved")}><option value="all">全部已处理</option><option value="revision">要求返修</option><option value="approved">已通过</option></select></label>
        </div>
        <p className="portal-boundary-note">目前只有一名接入学员；待审版本只在“待审提交”处理，不混入历史记录。</p>
        {visibleFeedback.length ? <div className="portal-review-list">{visibleFeedback.map(renderSubmission)}</div> : <div className="empty-state"><strong>暂无符合条件的反馈</strong><p>老师完成一次审批后，版本和意见会在这里保留。</p></div>}
      </section>}
      {view === "projects" && <section className="panel portal-wide-panel">
        <header className="panel-head"><div><span>一人一剧</span><h2>{projectDetail ? `${student?.user.displayName || "学员"}的项目空间 · 教师视角` : "学员项目"}</h2></div><span className="data-badge">当前接入 1 个项目</span></header>
        {student ? projectDetail ? <>
          <button className="portal-text-button portal-back-button" type="button" onClick={() => setProjectDetail(false)}>← 返回学员项目</button>
          <div className="portal-project-row"><UserAvatar profile={student.user} /><strong>{student.user.displayName}</strong><span>{student.project.title}</span><span>{student.project.stage}</span><span>{(student.project.acceptedDurationMs / 60_000).toFixed(1)} / 100 分钟</span><span>{pending.length} 待审</span><span>{money(student.knownCostFen)}</span></div>
          <div className="portal-detail-grid">
            <div><small>项目概况</small><p>个人项目 · {student.project.stage} · 目标 100 分钟 · 已验收 {(student.project.acceptedDurationMs / 60_000).toFixed(1)} 分钟</p></div>
            <div><small>任务</small><p>已发布 {student.assignments.length} 项，待审 {pending.length} 个版本。</p></div>
            <div><small>成本</small><p>已知人民币支出 {money(student.knownCostFen)}；其他币种与待核费用单列。</p></div>
          </div>
          <div className="portal-dashboard-grid">
            <div className="portal-project-section"><h3>任务与提交</h3><div className="portal-mini-list">{student.assignments.map((assignment) => <span key={assignment.id}>{assignment.lesson_code} · {assignment.title} · {student.submissions.filter((item) => item.assignmentId === assignment.id).length} 个版本</span>)}</div></div>
            <div className="portal-project-section"><h3>老师反馈</h3><div className="portal-mini-list">{student.submissions.filter((item) => item.review).map((item) => <span key={item.id}>{item.lessonCode} · V{item.version} · {item.review?.comment}</span>)}{!student.submissions.some((item) => item.review) && <span>暂无反馈。</span>}</div></div>
            <div className="portal-project-section"><h3>剧集与文件</h3><p>剧集、预览和母版尚未上传。附件栏目前只是学生填写的说明文字。</p></div>
            <div className="portal-project-section"><h3>版本时间线</h3><div className="portal-mini-list">{student.submissions.map((item) => <span key={item.id}>{formatTime(item.createdAt)} · {item.lessonCode} · V{item.version} · {item.status === "pending_review" ? "待审" : item.status === "revision" ? "需返修" : "已通过"}</span>)}</div></div>
          </div>
          <button className="portal-text-button" type="button" onClick={() => go("pending")}>前往待审提交 →</button>
        </> : <>
          <button className="portal-project-row portal-project-button" type="button" onClick={() => setProjectDetail(true)} aria-label={`查看${student.user.displayName}的项目空间`}><UserAvatar profile={student.user} /><strong>{student.user.displayName}</strong><span>{student.project.title}</span><span>{student.project.stage}</span><span>{(student.project.acceptedDurationMs / 60_000).toFixed(1)} / 100 分钟</span><span>{pending.length} 待审</span><span>{money(student.knownCostFen)}</span></button>
          <p className="portal-boundary-note">点击学员行查看同一项目的任务、版本、反馈、文件与成本。多学员名单和风险规则尚未接入；这里只展示当前真实开发账号。</p>
        </> : <div className="empty-state"><strong>项目数据读取中</strong></div>}
      </section>}
      {view === "files" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>按作品结构管理</span><h2>成片与文件</h2></div></header><p>剧本、分集、分镜、素材、预览版和原始母版将按学员项目与剧集归档，不混作普通网盘。</p><div className="portal-detail-grid"><div><small>当前提交的附件说明</small><div className="portal-mini-list">{data.submissions.filter((item) => item.attachmentLabel).map((item) => <span key={item.id}>{item.lessonCode} · V{item.version} · {item.attachmentLabel}（未实际上传）</span>)}{!data.submissions.some((item) => item.attachmentLabel) && <span>暂无附件说明</span>}</div></div><div><small>在线预览 PREVIEW</small><p>轻量课堂审片源尚未上传。</p></div><div><small>原始母版 MASTER</small><p>私有留档与授权下载尚未接入；不默认在线播放。</p></div></div></section>}
      {view === "costs" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>第三方生成费用属于学员项目成本</span><h2>成本概览</h2></div><span className="data-badge">教学平台与基础 COS 不计入</span></header>{student ? <><div className="portal-stat-grid"><Metric label="当前项目已知人民币支出" value={money(student.knownCostFen)} note="美元与未知金额单独列示" /><Metric label="费用记录" value={String(student.costs.length)} note="仅当前接入项目" /><Metric label="异常支出" value="—" note="阈值规则尚未建立" /></div><div className="portal-mini-list">{student.costs.map((cost) => <span key={cost.id}>{formatTime(cost.created_at)} · {cost.category} · {cost.tool} · {money(cost.amount_fen, cost.currency)}</span>)}</div></> : <p>项目成本读取中。</p>}</section>}
      {message && <p className="floating-message" role="status">{message}</p>}
    </>}
  </InternalShell>;
}

function StudentPage({ status, userId, view }: { status: StatusPayload | null; userId: string; view: PortalView }) {
  const [data, setData] = useState<StudentDashboard | null>(null);
  const [course, setCourse] = useState<CoursePayload | null>(null);
  const [message, setMessage] = useState("");
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const isCreationWorkspace = view === "scripts" || view === "dramas";
  const load = useCallback(() => api<StudentDashboard>(`/api/student/${encodeURIComponent(userId)}/dashboard`).then(setData).catch(() => undefined), [userId]);
  useEffect(() => { if (view !== "cache") void load(); }, [load, view]);
  useEffect(() => {
    if (isCreationWorkspace || view === "cache" || course) return;
    let mounted = true;
    cachedCourse<CoursePayload>(userId, "student", "a", (etag) => authenticatedFetch("/api/course", {
      headers: etag ? { "If-None-Match": etag } : {},
    }), () => verifiedLoginSession?.user.id === userId && readLoginSession()?.user.id === userId).then((value) => { if (mounted) setCourse(value); }).catch(() => undefined);
    return () => { mounted = false; };
  }, [isCreationWorkspace, course, userId, view]);
  const submitVersion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try {
      await api(`/api/student/${encodeURIComponent(userId)}/submissions`, { method: "POST", body: JSON.stringify({ assignmentId: form.get("assignmentId"), note: form.get("note"), attachmentLabel: form.get("attachmentLabel") }) });
      setMessage("新版本已保存并进入待审；附件当前只是说明，尚未上传到 COS。"); event.currentTarget.reset(); await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "提交失败"); }
  };
  const addCost = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try {
      await api(`/api/student/${encodeURIComponent(userId)}/costs`, { method: "POST", body: JSON.stringify({ category: form.get("category"), tool: form.get("tool"), amount: form.get("amount"), currency: form.get("currency"), accountingBasis: "project_usage", note: form.get("note") }) });
      setMessage("项目制作成本已记录；未知费用不会按 0 计算。"); event.currentTarget.reset(); await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "保存失败"); }
  };
  const setCreatorVisibility = async (category: CreatorCategory, visibility: CreatorVisibility[CreatorCategory]) => {
    try {
      await api(`/api/student/${encodeURIComponent(userId)}/creator-visibility`, { method: "PATCH", body: JSON.stringify({ category, visibility }) });
      setMessage("公开范围已更新；公开个人主页只读取明确设为“公开”的内容。");
      await load();
    } catch (value) { setMessage(value instanceof Error ? value.message : "公开范围更新失败"); }
  };
  const titles: Record<PortalView, string> = {
    overview: "项目总览", outline: "教学大纲", today: "今日学习", course: "课程计划", tasks: "任务与提交", pending: "待审提交",
    feedback: "版本与反馈", projects: "学员项目", files: "剧集与文件", costs: "成本台账", materials: "课程资料", scripts: "剧本创作", dramas: "短剧制作", members: "成员管理", cache: "本地缓存",
  };
  const stages = ["立项", "剧本", "分集", "分镜", "角色/场景", "镜头生产", "剪辑", "成片", "发行运营"];
  const today = course?.days[0];
  const currentAssignment = data?.assignments.find((item) => item.id === selectedAssignment) || data?.assignments[0];
  const latestReview = data?.submissions.filter((item) => item.review).sort((a, b) => (b.review?.createdAt || "").localeCompare(a.review?.createdAt || ""))[0];
  const latestSubmissionFor = (assignmentId: string) => data?.submissions.filter((item) => item.assignmentId === assignmentId).sort((a, b) => b.version - a.version)[0];
  const toDoCount = data?.assignments.filter((assignment) => latestSubmissionFor(assignment.id)?.status !== "approved").length || 0;
  const revisionCount = data?.assignments.filter((assignment) => latestSubmissionFor(assignment.id)?.status === "revision").length || 0;
  const taskStatus = (assignmentId: string) => {
    const latest = latestSubmissionFor(assignmentId);
    return latest?.status === "approved" ? "已通过" : latest?.status === "revision" ? "需返修" : latest?.status === "pending_review" ? "待老师审阅" : "待提交";
  };
  const knownCostByCategory = data?.costs.reduce<Record<string, number>>((totals, cost) => {
    if (cost.amount_fen !== null && cost.currency === "CNY") totals[cost.category] = (totals[cost.category] || 0) + cost.amount_fen;
    return totals;
  }, {}) || {};
  const knownOtherCostFen = Math.max(0, (data?.knownCostFen || 0) - (knownCostByCategory.image || 0) - (knownCostByCategory.video || 0) - (knownCostByCategory.audio || 0));
  const go = (next: PortalView) => navigatePortalView("student", userId, next);
  return <InternalShell role="student" userId={userId} title={titles[view]} subtitle="你的剧、任务、版本、反馈、文件和成本，都在同一个个人项目里。" profile={data?.user} activeView={view} onSelectView={go} hideWorkspaceHead={isCreationWorkspace}>
    {view === "cache" ? <TrainingCachePanel userId={userId} role="student" /> : isCreationWorkspace ? <section className="student-empty-workspace" aria-label={titles[view]} /> : !data ? <div className="loading-panel">正在读取个人项目…</div> : <>
      {view === "overview" && <>
        <section className="student-project-card"><div className="project-identity"><UserAvatar profile={data.user} large /><div><small>{data.user.displayName} · 繁星 ID {data.user.displayId}</small><h2>{data.project.title}</h2><p>阶段：{data.project.stage} · 一人一剧 · 目标 100 分钟</p></div></div><div className="progress-ring" style={{ "--progress": `${Math.max(0.8, data.project.acceptedDurationMs / data.project.targetDurationMs * 100)}%` } as React.CSSProperties}><strong>{(data.project.acceptedDurationMs / 60_000).toFixed(1)}</strong><span>/ 100 分钟</span></div></section>
        <div className="portal-stat-grid"><Metric label="成片进度" value={`${(data.project.acceptedDurationMs / 60_000).toFixed(1)} / 100`} note="已验收分钟" /><Metric label="当前阶段" value={data.project.stage} note="个人项目" /><Metric label="当前教学日" value="D01" note="试班课程种子" /><Metric label="待完成任务" value={String(toDoCount)} note="未通过的任务" /><Metric label="待修改版本" value={String(revisionCount)} note="老师要求返修" /><Metric label="已记录人民币成本" value={money(data.knownCostFen)} note="美元另列，不含教学基础存储" /></div>
        <div className="portal-dashboard-grid">
          <section className="panel"><header className="panel-head"><div><span>今天只聚焦眼前的事</span><h2>今天要做什么</h2></div></header>{currentAssignment ? <p>{currentAssignment.lesson_code} · {currentAssignment.title}<br />{currentAssignment.requirements}</p> : <p>老师尚未发布任务。</p>}<button className="portal-text-button" type="button" onClick={() => go("tasks")}>查看任务并提交版本 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>不用翻历史</span><h2>最新反馈</h2></div></header>{latestReview ? <><strong>{latestReview.lessonCode} · V{latestReview.version} · {latestReview.status === "approved" ? "已通过" : "要求返修"}</strong><p>{latestReview.review?.comment}</p></> : <p>老师还没有留下反馈。</p>}<button className="portal-text-button" type="button" onClick={() => go("feedback")}>查看版本与反馈 →</button></section>
          <section className="panel"><header className="panel-head"><div><span>我的剧走到哪里</span><h2>项目进度</h2></div></header><div className="portal-stage-line">{stages.map((stage) => <span className={stage === data.project.stage ? "active" : ""} key={stage}>{stage}</span>)}</div></section>
          <section className="panel"><header className="panel-head"><div><span>第三方生成支出</span><h2>项目成本</h2></div></header><strong className="portal-large-value">{money(data.knownCostFen)}</strong><p>这里只汇总已知人民币金额；美元及待核费用在台账单列。教学平台与基础 COS 存储不计入个人项目制作成本。</p><button className="portal-text-button" type="button" onClick={() => go("costs")}>打开成本台账 →</button></section>
        </div>
        <section className="panel portal-wide-panel creator-privacy-panel"><header className="panel-head"><div><span>公开主页展示设置</span><h2>创作者空间</h2></div><a href={`/u/${userId}`} target="_blank" rel="noopener noreferrer" className="portal-text-button">预览公开主页 ↗</a></header><p>只有设为“公开”的分类才会进入公开主页。成本、完整剧本、原始母版、教师内部批改及联系方式不提供公开开关，始终不从此处输出。</p><div className="creator-privacy-grid">{([
          ["project", "项目与进度"], ["activity", "创作活跃日历与年度报告"], ["timeline", "已通过的创作历程"], ["works", "作品封面"], ["highlights", "精选创作"], ["reviews", "精选老师点评与反馈次数"], ["capabilities", "创作能力与年度报告"],
        ] as [CreatorCategory, string][]).map(([category, label]) => <label key={category}><span>{label}</span><select aria-label={`${label}可见范围`} value={data.creatorVisibility[category]} onChange={(event) => void setCreatorVisibility(category, event.target.value as CreatorVisibility[CreatorCategory])}><option value="private">仅自己</option><option value="teacher">教师可见</option><option value="public">公开</option></select></label>)}</div><p className="portal-boundary-note">当前是开发阶段的 UUID 直达模式，尚未设置身份权限；请勿在此模式下录入或公开敏感资料。精选素材和点评发布入口未接入，开关不会自动公开内部文件或评语。</p></section>
      </>}
      {view === "today" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>D01 · 当前示例教学日</span><h2>{today?.title || "今日课程读取中"}</h2></div><span className="status ready">{today?.status === "expanded" ? "完整课" : "待展开"}</span></header><p>{today?.objective}</p><div className="portal-lesson-grid">{today?.sessions?.map(([teacher, learner], index) => <article key={index}><b>第 {index + 1} 课时</b><strong>{teacher}</strong><small>自己要做：{learner}</small></article>)}</div><div className="portal-detail-grid"><div><small>今日产出</small><p>{today?.deliverable}</p></div><div><small>完成标准</small><p>{today?.acceptance}</p></div><div><small>课后推进</small><p>{today?.homework}</p></div></div><button className="portal-text-button" type="button" onClick={() => go("tasks")}>进入任务与提交 →</button></section>}
      {view === "tasks" && <div className="portal-dashboard-grid">
        <section className="panel"><header className="panel-head"><div><span>老师发布的任务</span><h2>任务要求</h2></div><span className="data-badge">{data.assignments.length} 项</span></header><div className="portal-task-choices">{data.assignments.map((assignment) => <button type="button" className={currentAssignment?.id === assignment.id ? "active" : ""} key={assignment.id} onClick={() => setSelectedAssignment(assignment.id)}><b>{assignment.lesson_code} · {assignment.title}</b><small>{taskStatus(assignment.id)}</small></button>)}</div>{currentAssignment && <div className="portal-task-detail"><small>任务要求与验收依据</small><p>{currentAssignment.requirements}</p><small>截止时间：老师尚未设置</small></div>}</section>
        <section className="panel"><header className="panel-head"><div><span>持续迭代，不只交一次</span><h2>提交项目版本</h2></div></header>{data.assignments.length ? <form className="inline-form" onSubmit={submitVersion}><label><span>对应任务</span><select name="assignmentId" value={currentAssignment?.id || ""} onChange={(event) => setSelectedAssignment(event.target.value)}>{data.assignments.map((assignment) => <option value={assignment.id} key={assignment.id}>{assignment.lesson_code} · {assignment.title}</option>)}</select></label><label><span>版本说明</span><textarea name="note" required maxLength={1600} rows={5} placeholder="写明这版完成了什么、改了什么" /></label><label><span>附件说明 <i>当前不上传文件</i></span><input name="attachmentLabel" maxLength={160} placeholder="例如：项目立项书-v2.docx（仅说明）" /></label><button className="button primary" type="submit">提交新版本</button></form> : <p>请等待老师发布任务。</p>}<p className="portal-boundary-note">真实文件上传、截止时间和附件下载尚未接入；填写附件名称不会上传文件。</p></section>
        <section className="panel portal-full-span"><header className="panel-head"><div><span>这项任务的演进</span><h2>我的提交</h2></div></header><div className="portal-mini-list">{data.submissions.filter((item) => item.assignmentId === currentAssignment?.id).map((item) => <span key={item.id}>V{item.version} · {item.status === "pending_review" ? "待审" : item.status === "revision" ? "需返修" : "已通过"} · {formatTime(item.createdAt)} · {item.note}</span>)}{!data.submissions.some((item) => item.assignmentId === currentAssignment?.id) && <span>还没有提交这个任务的版本。</span>}</div></section>
      </div>}
      {view === "feedback" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>自己的项目版本进化史</span><h2>版本与反馈</h2></div><span className="data-badge">{data.submissions.length} 个版本</span></header>{data.submissions.length ? <div className="portal-version-timeline">{data.submissions.map((item) => <article key={item.id}><div className="portal-version-marker">V{item.version}</div><div><small>{item.lessonCode} · {item.assignmentTitle} · {formatTime(item.createdAt)}</small><h3>{item.status === "pending_review" ? "待老师审阅" : item.status === "revision" ? "需要返修" : "已通过"}</h3><p>{item.note}</p>{item.review && <blockquote><small>教师反馈{item.review.timecode ? ` · ${item.review.timecode}` : ""}</small><p>{item.review.comment}</p></blockquote>}</div></article>)}</div> : <div className="empty-state"><strong>还没有版本记录</strong><p>提交 V1 后，每次提交、返修和通过会留在这里。</p></div>}</section>}
      {view === "files" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>{data.project.title}</span><h2>剧集与文件</h2></div><IntegrationPill integration={status?.integrations.cos} /></header><p>按剧集组织剧本、分镜、图片、视频、声音、剪辑工程、在线预览与原始母版。目前尚未建立实际剧集或上传文件。</p><div className="portal-file-tree"><strong>我的短剧</strong><div>剧本 / 分集 / 分镜</div><div>角色与场景 / 图片 / 视频 / 配音 / 音乐</div><div>EP01…EPn · 预览版 / 母版 / 最终交付</div></div><div className="file-slots"><article><span>PREVIEW</span><h3>在线预览</h3><p>课堂审片用轻量预览源，尚未上传。</p><button disabled type="button">预览待上传</button></article><article><span>MASTER</span><h3>原始母版</h3><p>私有留档与授权下载，不默认在线播放。</p><button disabled type="button">服务端授权待接入</button></article></div><p className="portal-boundary-note">教学基础 COS 存储属于繁星运营成本，不混入学员第三方生成费用。</p></section>}
      {view === "costs" && <div className="portal-dashboard-grid">
        <section className="panel portal-full-span">
          <header className="panel-head"><div><span>只记自己的项目制作支出</span><h2>成本台账</h2></div><span className="data-badge">不含学费与教学基础 COS</span></header>
          <div className="portal-stat-grid">
            <Metric label="累计已知人民币支出" value={money(data.knownCostFen)} note="美元与待核金额单独列示" />
            {["image", "video", "audio", "other"].map((category) => <Metric key={category} label={({ image: "图片", video: "视频", audio: "声音", other: "其他" } as Record<string, string>)[category]} value={money(category === "other" ? knownOtherCostFen : knownCostByCategory[category] || 0)} note="当前已记录的人民币" />)}
          </div>
          {data.costs.some((cost) => cost.amount_fen === null || cost.currency !== "CNY") && <p className="portal-boundary-note">美元和待核费用不与人民币直接相加；以下保留各笔原币种记录。</p>}
          <div className="portal-mini-list">{data.costs.map((cost) => <span key={cost.id}>{formatTime(cost.created_at)} · {cost.category} · {cost.tool} · {cost.note || "未填写用途"} · {money(cost.amount_fen, cost.currency)}</span>)}{data.costs.length === 0 && <span>还没有成本记录。</span>}</div>
        </section>
        <section className="panel portal-full-span"><header className="panel-head"><div><span>记录一次真实耗用</span><h2>添加项目成本</h2></div></header><form className="portal-cost-form" onSubmit={addCost}><select name="category" defaultValue="image"><option value="text">文本 / 剧本</option><option value="image">图片生成</option><option value="video">视频生成</option><option value="audio">声音</option><option value="editing">编辑工具</option><option value="material">合法素材</option></select><input name="tool" required maxLength={80} placeholder="平台 / 工具" /><input name="amount" type="number" min="0" max="1000000" step="0.01" placeholder="金额；留空=待核" /><select name="currency" defaultValue="CNY"><option>CNY</option><option>USD</option></select><input name="note" maxLength={500} placeholder="用途，例如 EP01 镜头生成" /><button className="button primary" type="submit">添加成本</button></form></section>
      </div>}
      {view === "materials" && <section className="panel portal-wide-panel"><header className="panel-head"><div><span>教学支持</span><h2>课程资料</h2></div></header><p>已展开的教学日可查看目标、步骤、产出和验收要求；模板、案例与文件下载尚未接入，不显示虚假的下载按钮。</p><div className="portal-course-days">{course?.days.filter((day) => day.status === "expanded").map((day) => <article key={day.code}><b>{day.code}</b><div><strong>{day.title}</strong><small>{day.deliverable}</small></div><span>完整课</span></article>)}</div><Link className="portal-text-button" href="/plan">查看完整课程计划 →</Link></section>}
      {message && <p className="floating-message" role="status">{message}</p>}
    </>}
  </InternalShell>;
}

function TrainingProfileFooter() {
  return <footer className="training-profile-footer"><div><span>Copyright © 2026 DUANJU.SCHOOL. 繁星短剧训练营</span><span>一人一剧 · 项目制教学 · 完整短剧生产</span></div></footer>;
}

function TrainingProfileHeader() {
  return <header className="training-profile-header">
    <div className="training-profile-header-left">
      <Link href="/" className="training-profile-brand">
        <BrandLockup />
      </Link>
      <nav className="training-profile-nav" aria-label="主导航">
        <Link href="/">首页</Link>
        <Link href="/plan">课程计划</Link>
        <Link href="/teach">教学方式</Link>
        <Link href="/work">项目成果</Link>
        <Link href="/me">关于老师</Link>
        <Link href="/faq">常见问题</Link>
      </nav>
    </div>
    <div className="training-profile-header-right">
      <Link href="/ask" className="training-profile-contact">咨询方式</Link>
    </div>
  </header>;
}

function ProjectResultCard({ project, index, onSelect }: { project: ProjectResult; index: number; onSelect: (project: ProjectResult) => void }) {
  return <button className="result-card" type="button" onClick={() => onSelect(project)} aria-label={`查看《${project.title}》简介`}>
    <div className="result-cover">
      <img src={project.coverUrl} alt={`${project.title}封面`} loading={index < 18 ? "eager" : "lazy"} decoding="async" />
      <span>{project.category}</span>
    </div>
    <h3>{project.title}</h3>
    <p><strong>{project.durationMinutes}</strong> 分钟</p>
  </button>;
}

function TrainingProfileWorks({ visible }: { visible: boolean }) {
  const [filter, setFilter] = useState("全部");
  const [selectedProject, setSelectedProject] = useState<ProjectResult | null>(null);
  const projects = (projectResultsCatalog.projects as ProjectResult[]).slice(0, 6);
  const shown = visible && ["全部", "短剧"].includes(filter) ? projects : [];
  useEffect(() => {
    document.body.classList.toggle("overlay-open", Boolean(selectedProject));
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedProject(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("overlay-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedProject]);
  return <section className="yycp-works yycp-worksProfile creator-section" aria-label="我的作品">
    <div className="creator-section-head"><div><span>04 / PORTFOLIO</span><h2 className="yycp-sectionTitle">我的作品</h2></div><p>只展示选择公开的项目封面；封面不代表成片已完成。</p></div>
    <div className="yycp-sectionRule" />
    <div className="creator-filter" role="group" aria-label="作品类型">{["全部", "短剧", "剧照", "预告片", "片段", "海报"].map((option) => <button key={option} type="button" className={filter === option ? "active" : ""} aria-pressed={filter === option} onClick={() => setFilter(option)}>{option}</button>)}</div>
    {shown.length ? <div className="results-grid" aria-live="polite">
      {shown.map((project, index) => <ProjectResultCard key={project.id} project={project} index={index} onSelect={setSelectedProject} />)}
    </div> : <div className="creator-empty">{!visible ? "作者尚未公开作品。" : `暂未公开${filter === "全部" ? "作品" : filter}。`}</div>}
    {selectedProject ? <ProjectWorkDetail project={selectedProject} onClose={() => setSelectedProject(null)} onWatch={() => {
      setSelectedProject(null);
      window.open("https://v.fanxing.io/#download", "_blank", "noopener,noreferrer");
    }} /> : null}
  </section>;
}

function CreatorCapabilityArchive({ capabilities }: { capabilities: PublicCreatorData["capabilities"] }) {
  // Developer-only visual fixture. Do not put these example counts into the public API or annual report.
  const previewCards = [
    { title: "剧本创作", lines: [["完成任务", "16"], ["通过版本", "31"], ["参与剧集", "60集"]] },
    { title: "分镜设计", lines: [["完成分镜", "328镜"]] },
    { title: "AI图像", lines: [["正式采用素材", "486张"]] },
    { title: "AI视频", lines: [["验收镜头", "264个"]] },
    { title: "剪辑", lines: [["完成成片", "103.4分钟"]] },
    { title: "项目经验", lines: [["完整项目", "1部"], ["进行中", "1部"]] },
  ];
  return <section className="creator-section creator-capability-archive" aria-label="创作能力档案">
    <div className="creator-section-head"><div><span>01 / CREATIVE RECORD</span><h2>创作能力档案</h2></div><p>开发预览 · 数值暂用示例</p></div>
    {capabilities ? <><div className="creator-capability-grid">{previewCards.map((card) => <article key={card.title} className="creator-capability-card">
      <h3>{card.title}</h3><div className="creator-capability-lines">{card.lines.map(([label, value]) => <p key={label}><span>{label}</span><strong>{value}</strong></p>)}</div>
    </article>)}</div><p className="creator-capability-source">当前是能力档案的开发演示数据，后续接入真实项目记录；不计入年度创作报告。</p></> : <div className="creator-empty">作者暂未公开创作能力档案。</div>}
  </section>;
}

// 仅给 ID 1 的开发预览页补足时间轴；真实审核记录始终来自公开 API。
const previewJourney = [
  { date: "2026-09-21", icon: "🟢", title: "D12 · 第一阶段剧本通过老师审核", detail: "完成 EP01—EP03 第二版", status: "已通过" },
  { date: "2026-09-19", icon: "🟡", title: "D10 · 提交人物关系 V3", detail: "第三次调整男女主人物关系", status: "已提交" },
  { date: "2026-09-16", icon: "🟢", title: "D07 · 人物小传通过", detail: "", status: "已通过" },
  { date: "2026-09-12", icon: "🟢", title: "D03 · 项目立项完成", detail: "", status: "已完成" },
  { date: "2026-09-10", icon: "🚀", title: "我的第一部100分钟短剧正式开始", detail: "", status: "开始创作" },
] as const;

function PublicStudentPage({ userId }: { userId: string }) {
  const [data, setData] = useState<PublicCreatorData | null>(null);
  const [error, setError] = useState("");
  const [shareToast, setShareToast] = useState("");
  const [showAnnualShare, setShowAnnualShare] = useState(false);
  useEffect(() => {
    setError("");
    api<PublicCreatorData>(`/api/u/${encodeURIComponent(userId)}`)
      .then(setData)
      .catch((value) => setError(value instanceof Error ? value.message : "个人空间读取失败"));
  }, [userId]);
  useEffect(() => {
    document.title = "个人主页 · 繁星短剧训练营";
    document.documentElement.classList.add("training-profile-document");
    document.body.classList.add("training-profile-document");
    return () => {
      document.title = "繁星短剧训练营";
      document.documentElement.classList.remove("training-profile-document");
      document.body.classList.remove("training-profile-document");
    };
  }, []);
  useEffect(() => {
    if (!shareToast) return;
    const timer = window.setTimeout(() => setShareToast(""), 1800);
    return () => window.clearTimeout(timer);
  }, [shareToast]);
  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareToast("已复制网址到剪贴板");
    } catch {
      setShareToast("复制失败，请手动复制");
    }
  };
  const publicProject = data?.project;
  const acceptedMinutes = publicProject ? publicProject.acceptedDurationMs / 60_000 : 0;
  const progress = publicProject?.targetDurationMs ? Math.min(100, publicProject.acceptedDurationMs / publicProject.targetDurationMs * 100) : 0;
  const stages = ["立项", "人物", "故事梗概", "剧本", "分镜", "角色场景", "镜头制作", "剪辑", "成片", "发行"];
  const stageIndex = Math.max(0, stages.findIndex((stage) => publicProject?.stage?.includes(stage)));
  const showPreviewJourney = data?.user.displayId === "1" && data.visibility.timeline === "public" && data.timeline.length === 0;
  if (error) return <NotFoundPage />;
  return <div className="training-profile-page" data-user-uuid={userId}>
    <TrainingProfileHeader />
    <main className="training-profile-main">
      {!data ? <div className="training-profile-loading">正在读取公开个人空间…</div> : <>
        <div className="yycp-hero">
          <div className="yycp-heroOverlay">
            <div aria-hidden="true" />
            <div className="yycp-heroCalendarSlot"><PublicActivityCalendar activity={data.activity} visible={data.visibility.activity === "public"} /></div>
          </div>
        </div>
        <div className="yycp-content">
          <aside className="yycp-profileCard" aria-label="学生信息卡片">
            <button type="button" className="yycp-shareBtn" aria-label="分享主页" title="分享主页" onClick={() => void shareProfile()}>
              <ShareArrowIcon width="36" height="36" />
            </button>
            {shareToast ? <div className="yycp-shareToast" role="status" aria-live="polite">{shareToast}</div> : null}
            <div className="yycp-avatar" aria-hidden="true">
              {data.user.avatarUrl ? <img className="yycp-avatarImg" src={data.user.avatarUrl} alt="" /> : <span>{data.user.displayName.slice(0, 1)}</span>}
            </div>
            <h1 className="yycp-name">{data.user.displayName}</h1>
            <p className="yycp-bio">{data.user.bio || "正在把自己的故事，做成一部完整短剧。"}</p>
            <p className="yycp-trainingMeta">繁星 ID {data.user.displayId}{publicProject ? ` · ${publicProject.stage}` : ""}</p>
            <div className="creator-profile-note">一人一剧 · 用项目记录每一步创作</div>
            <section className="creator-annual-report" aria-label="年度创作报告">
              <span className="creator-annual-eyebrow">{data.annualReport?.year ?? new Date().getFullYear()} / CREATION REPORT</span>
              <h2>年度创作报告</h2>
              {data.annualReport ? <>
                <p>每一次提交与验收，都留下真实记录。</p>
                <div className="creator-annual-metrics">
                  <div><strong>{data.annualReport.activeSubmissionDays}</strong><span>有提交的创作日</span></div>
                  <div><strong>{data.annualReport.submissionVersions}</strong><span>提交版本</span></div>
                  <div><strong>{data.annualReport.approvedMilestones}</strong><span>通过里程碑</span></div>
                  <div><strong>{data.annualReport.teacherFeedbacks ?? "—"}</strong><span>教师反馈{data.annualReport.teacherFeedbacks === null ? " · 未公开" : ""}</span></div>
                </div>
                <p className="creator-annual-footnote">创作日按版本提交日期统计；镜头、成片等指标待验收数据接入后显示。</p>
                <button type="button" className="creator-annual-share" onClick={() => setShowAnnualShare(true)}>生成年度分享卡 <span aria-hidden="true">↗</span></button>
              </> : <p>作者暂未公开年度创作数据。</p>}
            </section>
          </aside>
          <div className="yycp-contentMain">
            <CreatorCapabilityArchive capabilities={data.capabilities} />
            <section className="creator-section creator-current"><div className="creator-section-head"><div><span>02 / CURRENT PROJECT</span><h2>正在制作</h2></div><span className="creator-status">{publicProject?.stage || "创作中"}</span></div>{publicProject ? <><div className="creator-current-main"><div><span className="creator-kicker">个人短剧项目 · 目标 100 分钟</span><h3>《{publicProject.title}》</h3><p>从立项、人物、剧本，到分镜、制作与发行，持续推进自己的作品。</p></div><div className="creator-minute"><strong>{acceptedMinutes.toFixed(1)}</strong><span>/ 100 分钟</span></div></div><div className="creator-progress-track"><i style={{ width: `${progress}%` }} /></div><div className="creator-stage-flow">{stages.map((stage, index) => <span key={stage} className={index < stageIndex ? "done" : index === stageIndex ? "current" : ""}>{index < stageIndex ? "✓ " : index === stageIndex ? "● " : "○ "}{stage}</span>)}</div><div className="creator-project-tail"><p className="creator-project-note">只显示已公开的项目进度；剧本、成本、原始文件仍在内部学生端。</p><a href="#creator-journey">查看项目历程 ↘</a></div></> : <div className="creator-empty">作者暂未公开正在制作的项目详情。</div>}</section>
            <section id="creator-journey" className="creator-section">
              <div className="creator-section-head"><div><span>03 / JOURNEY</span><h2>创作历程</h2></div><p>{showPreviewJourney ? "开发预览 · 以下为虚拟历程" : "仅记录公开的教师审核通过节点。"}</p></div>
              {showPreviewJourney ? <ol className="creator-timeline">{previewJourney.map((item) => <li key={item.date}>
                <time dateTime={item.date}>{item.date.slice(5).replace("-", ".")}</time>
                <div><strong>{item.icon} {item.title}</strong>{item.detail && <p>{item.detail}</p>}</div>
                <span>{item.status}</span>
              </li>)}</ol> : data.timeline.length ? <ol className="creator-timeline">{data.timeline.map((item) => <li key={`${item.lessonCode}-${item.version}-${item.date}`}><time dateTime={item.date}>{new Date(item.date).toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" })}</time><div><strong>{item.lessonCode} · {item.title}</strong><p>V{item.version} 已通过教师审核</p></div><span>已通过</span></li>)}</ol> : <div className="creator-empty">还没有公开的审核通过记录。每一个被公开的里程碑，都会留在这里。</div>}
            </section>
            <TrainingProfileWorks visible={data.visibility.works === "public"} />
            <section className="creator-section"><div className="creator-section-head"><div><span>05 / SELECTED WORK</span><h2>精选创作</h2></div><p>阶段成果可以先于完整成片出现。</p></div><div className="creator-empty creator-empty-grid"><span>人物设定</span><span>场景设计</span><span>分镜作品</span><span>精选镜头 / 预告片</span><p>当前没有主动公开的阶段素材；内部项目文件不会自动显示在这里。</p></div></section>
            <section className="creator-section"><div className="creator-section-head"><div><span>06 / REVIEW</span><h2>老师点评与里程碑</h2></div><p>内部批改不会自动公开。</p></div><div className="creator-empty">精选老师点评尚未发布。只有经过单独确认的公开点评才会在这里出现；内部返修意见始终留在教学端。</div>{data.timeline.length ? <div className="creator-approved"><strong>已公开的审核节点</strong>{data.timeline.map((item) => <span key={`${item.lessonCode}-${item.version}-${item.date}`}>✓ {item.lessonCode} · {item.title}</span>)}</div> : null}</section>
            <section className="creator-section creator-cta"><div><span>DUANJU.SCHOOL</span><h2>{data.user.displayName} 正在完成自己的短剧。<br />你也可以从第一天开始。</h2><p>一人一剧，从立项到完整成片，跟着项目进度学习创作。</p></div><Link href="/" className="creator-cta-link">了解课程 <span aria-hidden="true">↗</span></Link></section>
          </div>
        </div>
      </>}
    </main>
    <TrainingProfileFooter />
    {showAnnualShare && data?.annualReport ? <AnnualReportShare report={data.annualReport} name={data.user.displayName} onClose={() => setShowAnnualShare(false)} /> : null}
  </div>;
}

function NotFoundPage() {
  return <Shell><section className="not-found"><span>404</span><h1>这个页面还没有进入课程。</h1><p>检查地址，或返回招生首页。</p><Link href="/" className="button primary">返回首页</Link></section></Shell>;
}

function BlankPage({ title }: { title: string }) {
  return <Shell><section className="blank-page"><h1 className="sr-only">{title}</h1></section></Shell>;
}

const teachingMilestones = ["立项", "剧本", "视觉资产", "样片", "25 min", "50 min", "75 min", "100 min", "发行", "复盘"];

function TeachingStudentInterface() {
  return <article className="method-student-ui" aria-label="学生个人项目空间概念界面">
    <header><div><span>我的短剧</span><h3>《雾港回声》</h3></div><small>学生空间 · 概念界面</small></header>
    <div className="method-progress"><strong>27.4</strong><span>/ 100 min</span><i><b /></i></div>
    <dl>
      <div><dt>当前完成</dt><dd>27.4分钟</dd></div>
      <div><dt>当前阶段</dt><dd>批量生产</dd></div>
      <div><dt>下一里程碑</dt><dd>50分钟</dd></div>
      <div><dt>待返修</dt><dd>3项</dd></div>
      <div><dt>项目成本</dt><dd>¥1,286.40</dd></div>
    </dl>
    <footer><span>DAY 18</span><p>完成镜头 26—31 的返修与声音替换</p><b>→</b></footer>
  </article>;
}

function TeachingReviewInterface() {
  const students = [
    ["周*", "样片待审", "02:41"],
    ["林**", "返修中", "镜头 08"],
    ["陈*", "进度落后", "18.6 min"],
    ["叶**", "成本提醒", "¥428 / min"],
    ["沈*", "达到里程碑", "50 min"],
  ];
  return <div className="method-review-system">
    <article className="method-teacher-ui">
      <header><div><span>教师端</span><h3>项目进度与待审队列</h3></div><small>概念界面</small></header>
      <div className="teacher-ui-summary"><span><strong>05</strong> 今日关注</span><span><strong>12</strong> 待审版本</span><span><strong>03</strong> 里程碑验收</span></div>
      <ul>{students.map(([name, status, detail], index) => <li key={name}><i>{String(index + 1).padStart(2, "0")}</i><strong>{name}</strong><span>{status}</span><small>{detail}</small><b>→</b></li>)}</ul>
    </article>
    <article className="method-student-task-ui">
      <header><span>学生端</span><small>镜头 08 · V03</small></header>
      <h3>今天继续推进自己的成片</h3>
      <div className="task-ui-shot"><span>00:27</span><i /><i /><i /></div>
      <blockquote><small>教师反馈</small><p>人物视线与上一镜不连续，保留动作节奏，重新生成后半段并提交 V04。</p></blockquote>
      <footer><span>返修内容已同步</span><b>提交新版本 →</b></footer>
    </article>
  </div>;
}

function TeachingMethodPage() {
  const dailyLessons = [
    ["01", "第1课时", "方法与原理", "今天为什么这样做。"],
    ["02", "第2课时", "案例与教师示范", "把方法真正做给你看。"],
    ["03", "第3课时", "项目实操", "推进你自己的100分钟短剧。"],
    ["04", "第4课时", "审片与返修", "看作品、找问题、给修改要求。"],
  ];
  const costSteps = ["分镜先行", "静态资产先确认", "样片跑通生产线", "关键镜头投入较高生成成本", "持续记录项目成本"];
  return <Shell>
    <div className="marketing-detail-page teaching-method-page">
      <section className="detail-page-hero section-pad" aria-labelledby="teaching-method-title">
        <div className="detail-hero-copy">
          <p className="detail-page-index">TEACHING METHOD / 教学方式</p>
          <h1 id="teaching-method-title"><span>不是进群听课。</span><strong>进入自己的短剧项目。</strong></h1>
          <p>从第一天立项，到100分钟完整成片，再到发行运营，课程围绕每个人自己的短剧项目推进。</p>
        </div>
        <ol className="detail-hero-route" aria-label="教学方式主线">
          {["一人一剧", "项目里程碑", "每日课堂", "教师带项目", "成本控制", "发行闭环"].map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span><strong>{item}</strong></li>)}
        </ol>
      </section>

      <section className="detail-page-section method-project section-pad" aria-labelledby="method-project-title">
        <div className="detail-section-copy">
          <p className="detail-page-index">01 / ONE PERSON, ONE PROJECT</p>
          <h2 id="method-project-title">每个人，<span>都有自己的</span><span>100分钟短剧。</span></h2>
          <p>不采用线上多人协作完成同一部作品，每位学员独立选择题材、建立人物与场景、完成剧本、分镜、镜头生产、剪辑和发行。</p>
        </div>
        <TeachingStudentInterface />
      </section>

      <section className="detail-page-section method-milestones section-pad" aria-labelledby="method-milestones-title">
        <header className="detail-section-head"><div><p className="detail-page-index">02 / PROJECT MILESTONES</p><h2 id="method-milestones-title">课程走到哪里，<span>作品就做到哪里。</span></h2></div><p>每一天的新知识，都立即作用到自己的项目上，而不是学完一堆软件以后再开始做作品。</p></header>
        <ol>{teachingMilestones.map((item, index) => <li className={index >= 4 ? "is-production" : ""} key={item}><span>{String(index + 1).padStart(2, "0")}</span><i /><strong>{item}</strong></li>)}</ol>
      </section>

      <section className="detail-page-section method-day section-pad" aria-labelledby="method-day-title">
        <header className="detail-section-head"><div><p className="detail-page-index">03 / A DAY IN CLASS</p><h2 id="method-day-title">每天4课时，<span>不是连续听3小时。</span></h2></div><p>讲清方法、现场示范、推进项目、审片返修，每一天都留下可检查的项目进度。</p></header>
        <div className="daily-lesson-grid">{dailyLessons.map(([number, period, title, copy]) => <article key={number}><span>{number}</span><small>{period}</small><h3>{title}</h3><p>{copy}</p></article>)}</div>
      </section>

      <section className="detail-page-section method-review section-pad" aria-labelledby="method-review-title">
        <header className="detail-section-head"><div><p className="detail-page-index">04 / CONTINUOUS REVIEW</p><h2 id="method-review-title">你的项目，<span>会一直有人盯进度。</span></h2></div><p>教师不是只负责“上课”，而是持续参与项目审核、返修和里程碑验收。</p></header>
        <TeachingReviewInterface />
      </section>

      <section className="detail-page-section method-cost section-pad" aria-labelledby="method-cost-title">
        <div className="detail-section-copy">
          <p className="detail-page-index">05 / COST CONTROL</p>
          <h2 id="method-cost-title">先把镜头想清楚，<span>再花生成费用。</span></h2>
          <ol>{costSteps.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>)}</ol>
        </div>
        <article className="method-cost-ledger">
          <header><span>项目成本台账</span><small>《雾港回声》 · DAY 18</small></header>
          <div className="cost-ledger-chart"><i style={{ height: "42%" }} /><i style={{ height: "68%" }} /><i style={{ height: "34%" }} /><i style={{ height: "83%" }} /><i style={{ height: "57%" }} /><i style={{ height: "76%" }} /><i style={{ height: "49%" }} /><i style={{ height: "63%" }} /></div>
          <dl><div><dt>成片分钟数</dt><dd>27.4 min</dd></div><div><dt>生成支出</dt><dd>¥1,286.40</dd></div><div><dt>每分钟成本</dt><dd>¥46.95</dd></div><div><dt>废片率</dt><dd>18.2%</dd></div><div><dt>预计剩余预算</dt><dd>持续测算</dd></div></dl>
        </article>
      </section>

      <section className="method-ratio section-pad" aria-labelledby="method-ratio-title">
        <div><p className="detail-page-index">06 / PRODUCTION & RELEASE</p><h2 id="method-ratio-title">70%制作，<span>30%发行运营。</span></h2><p>先把作品真正做出来，再让作品进入真实发行、包装、数据观察与复盘。</p></div>
        <div className="ratio-graphic" aria-label="70%制作与30%发行运营"><span><strong>70</strong><small>制作</small></span><span><strong>30</strong><small>发行运营</small></span></div>
      </section>
    </div>
  </Shell>;
}

const instructorPhases = [
  ["01", "影视制作", "影视后期、栏目包装、广告与商业影像制作，后来进入真人短剧与内容项目。", "/showcase/night.webp"],
  ["02", "教学与专业建设", "长期动画与影视教学、专业负责人、课程体系和学生项目经验。", "/showcase/future.webp"],
  ["03", "AI短剧与创作系统", "AI漫剧承制、自主AI内容项目、短剧生产流程，以及繁星OS、JUBEN等创作系统实践。", "/showcase/fantasy.webp"],
] as const;

const instructorPractice = [
  ["01", "剧本与模型研究", "持续研究故事结构、人物关系与创作辅助能力。"],
  ["02", "JUBEN剧本系统", "把剧本工作拆成可追踪、可迭代的创作过程。"],
  ["03", "繁星OS", "围绕真实内容项目搭建创作与生产工作空间。"],
  ["04", "AI短剧生产流程", "持续验证从剧本、资产、镜头到成片的完整链路。"],
  ["05", "模型 / LoRA / 工作流研究", "跟进新工具，同时把重点放回作品质量与生产效率。"],
] as const;

function AboutTeacherPage() {
  const experience = [
    ["2002—2005", "影视后期 / 商业影像"],
    ["2005—2014", "动画专业负责人 / 教师"],
    ["2014—2015", "影视在线课程"],
    ["2025—至今", "真人短剧 / AI漫剧 / AIGC内容与系统"],
  ];
  return <Shell>
    <div className="marketing-detail-page about-teacher-page">
      <section className="detail-page-hero about-hero section-pad" aria-labelledby="about-teacher-title">
        <div className="detail-hero-copy">
          <p className="detail-page-index">ABOUT THE INSTRUCTOR / 关于老师</p>
          <h1 id="about-teacher-title">不是从AI工具<span>开始的。</span></h1>
          <p>从影视制作、动画教学，到AI短剧与内容生产，我一直做的都是同一件事：把故事变成可以被看见的作品。</p>
        </div>
        <div className="about-hero-proof" aria-label="三段专业实践"><span><i>01</i><strong>制作</strong><small>把故事做成作品</small></span><span><i>02</i><strong>教学</strong><small>把经验变成方法</small></span><span><i>03</i><strong>系统</strong><small>把流程持续跑通</small></span></div>
      </section>

      <section className="detail-page-section instructor-phases section-pad" aria-labelledby="instructor-phases-title">
        <header className="detail-section-head"><div><p className="detail-page-index">01 / THREE PHASES</p><h2 id="instructor-phases-title">不是普通的<span>AI工具讲师。</span></h2></div><p>影视制作、长期教学和正在发生的AI内容实践，共同决定了这门课怎样教。</p></header>
        <div className="instructor-phase-grid">{instructorPhases.map(([number, title, copy, image]) => <article key={number}><div><img src={image} alt="" /><span>{number}</span></div><h3>{title}</h3><p>{copy}</p></article>)}</div>
      </section>

      <section className="instructor-rationale section-pad" aria-labelledby="instructor-rationale-title">
        <p className="detail-page-index">02 / WHY THIS COURSE</p>
        <h2 id="instructor-rationale-title">这套课，是从<span>真实制作倒推出来的。</span></h2>
        <div><p>以前教影视，学生需要理解构图、镜头、叙事和剪辑。</p><p>AI改变了生产工具，却没有改变影视作品成立的基本逻辑。</p><p>所以课程不会从某个软件按钮开始，而是从“你要完成一部什么样的短剧”开始。</p></div>
      </section>

      <section className="detail-page-section instructor-now section-pad" aria-labelledby="instructor-now-title">
        <header className="detail-section-head"><div><p className="detail-page-index">03 / STILL MAKING</p><h2 id="instructor-now-title">课程之外，<span>我自己也在继续做。</span></h2></div><p>课程不是一套停止更新的PPT，而是跟着真实生产、工具变化和项目复盘持续迭代。</p></header>
        <div className="instructor-practice-grid">{instructorPractice.map(([number, title, copy]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p><i>→</i></article>)}</div>
      </section>

      <section className="instructor-boundary section-pad" aria-labelledby="instructor-boundary-title">
        <div><p className="detail-page-index">04 / TEACHING BOUNDARY</p><h2 id="instructor-boundary-title">我负责带你做，<span>但不会替你做。</span></h2></div>
        <div className="boundary-copy"><p>我会讲方法、做示范、审片、指出问题、给返修方案，并帮助你跑通生产线，但最终的剧本、镜头、成片和项目必须由你自己完成。</p><blockquote>因为真正值钱的，不是最后拿到一部片，而是你自己已经完整做过一部。</blockquote></div>
      </section>

      <section className="detail-page-section instructor-experience section-pad" aria-labelledby="instructor-experience-title">
        <header><p className="detail-page-index">05 / EXPERIENCE</p><h2 id="instructor-experience-title">经历</h2><p>履历不是页面主体，但它解释了这套方法从哪里来。</p></header>
        <ol>{experience.map(([years, title], index) => <li key={years}><span>{String(index + 1).padStart(2, "0")}</span><strong>{years}</strong><p>{title}</p></li>)}</ol>
      </section>
    </div>
  </Shell>;
}

type FaqChatMessage = {
  id: number;
  role: "user" | "assistant";
  text: string;
  href?: string;
  linkLabel?: string;
};

const faqAssistantMatches = [
  { words: ["零基础", "基础", "不会", "能学"], group: faqGroups[0], item: homepageFaqs[0] },
  { words: ["电脑", "配置", "设备"], group: faqGroups[0], item: homepageFaqs[3] },
  { words: ["每天", "投入", "课后", "时间"], group: faqGroups[1], item: homepageFaqs[2] },
  { words: ["缺课", "请假", "补课"], group: faqGroups[1], item: homepageFaqs[5] },
  { words: ["100分钟", "完成", "保证"], group: faqGroups[2], item: homepageFaqs[1] },
  { words: ["赚钱", "接单", "收入"], group: faqGroups[2], item: homepageFaqs[7] },
  { words: ["工具", "费用", "多少钱", "成本"], group: faqGroups[3], item: homepageFaqs[4] },
  { words: ["版权", "归属", "作品"], group: faqGroups[3], item: homepageFaqs[6] },
] as const;

function FaqAssistantHero() {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<FaqChatMessage[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const messageIdRef = useRef(0);
  const isChatting = messages.length > 0;

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    const styles = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 24;
    const padding = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0);
    const minimumHeight = lineHeight + padding;
    const maximumHeight = lineHeight * 4 + padding;
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minimumHeight), maximumHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maximumHeight ? "auto" : "hidden";
    contentRef.current?.style.setProperty("--home-composer-height", `${nextHeight}px`);
  }, [isChatting, query]);

  useEffect(() => {
    const conversation = conversationRef.current;
    if (!conversation) return;
    conversation.scrollTo({ top: conversation.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    const normalized = value.toLocaleLowerCase("zh-CN");
    const match = faqAssistantMatches.find(({ words, item }) =>
      words.some((word) => normalized.includes(word.toLocaleLowerCase("zh-CN")))
      || item[0].toLocaleLowerCase("zh-CN").includes(normalized),
    );
    const userMessage: FaqChatMessage = { id: ++messageIdRef.current, role: "user", text: value };
    const assistantMessage: FaqChatMessage = match ? {
      id: ++messageIdRef.current,
      role: "assistant",
      text: match.item[1],
      href: `#${match.group.id}`,
      linkLabel: `查看${match.group.title}`,
    } : {
      id: ++messageIdRef.current,
      role: "assistant",
      text: "这个问题暂时没有匹配到公开的课程资料。你可以补充课程、项目或费用关键词，也可以直接向老师咨询。",
      href: "/ask",
      linkLabel: "直接咨询老师",
    };
    setMessages((current) => [...current, userMessage, assistantMessage]);
    setQuery("");
  };

  return <section className={`home-assistant-hero${isChatting ? " is-chatting" : ""}`} aria-labelledby="home-assistant-title">
    <div className="home-assistant-hero__content site-frame" ref={contentRef}>
      <h1 id="home-assistant-title">有什么可以帮你的？</h1>
      <div className="home-assistant-conversation" ref={conversationRef} aria-live="polite">
        {messages.map((message) => <article className={`home-assistant-message is-${message.role}`} key={message.id}>
          <span>{message.role === "assistant" ? "繁星短剧训练营" : "你"}</span>
          <p>{message.text}</p>
          {message.href ? <a href={message.href}>{message.linkLabel}<strong aria-hidden="true">↗</strong></a> : null}
        </article>)}
      </div>
      <form className="home-assistant-prompt" onSubmit={handleSubmit}>
        <label className="sr-only" htmlFor="faq-training-question">向繁星短剧训练营提问</label>
        <textarea
          id="faq-training-question"
          name="question"
          ref={textareaRef}
          rows={1}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={isChatting ? "继续询问课程与项目……" : "问问课程、项目、工具与费用……"}
        />
        <button type="submit" disabled={!query.trim()} aria-label="提交问题">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 15V5M6 9l4-4 4 4" /></svg>
        </button>
      </form>
      <nav className="home-assistant-quick-links" aria-label="常见问题快捷入口">
        {faqGroups.map((group) => <a href={`#${group.id}`} key={group.id}>{group.title}</a>)}
        <a href="#faq-answers">更多</a>
      </nav>
      <p className="home-assistant-hero__scope">仅回答与繁星短剧训练营相关的问题和讨论</p>
    </div>
  </section>;
}

function FaqPage() {
  const jumpToGroup = (id: string) => {
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const visibleCount = faqGroups.reduce((count, group) => count + group.items.length, 0);

  return <Shell>
    <div className="faq-page">
      <FaqAssistantHero />

      <section className="faq-directory section-pad" aria-labelledby="faq-directory-title">
        <div className="faq-directory-heading">
          <div>
            <p className="faq-page-index">QUESTION CATEGORIES / 问题分类</p>
            <h2 id="faq-directory-title">先找到问题所在的环节。</h2>
          </div>
          <p>四类问题一行展开，点击即可直接查看对应答案。</p>
        </div>
        <div className="faq-category-row">
          {faqGroups.map((group) => <button key={group.id} type="button" onClick={() => jumpToGroup(group.id)}>
            <span>{group.index}</span>
            <strong>{group.title}</strong>
            <small>{group.summary}</small>
            <i aria-hidden="true">→</i>
          </button>)}
        </div>
      </section>

      <section className="faq-answers section-pad" id="faq-answers" aria-label="常见问题答案">
        <div className="faq-results-status" aria-live="polite">
          <span>全部问题</span>
          <strong>{visibleCount} 个回答</strong>
        </div>
        <div className="faq-answer-groups">
          {faqGroups.map((group) => <section className="faq-answer-group" id={group.id} key={group.id} aria-labelledby={`${group.id}-title`}>
            <header>
              <span>{group.index}</span>
              <div><h2 id={`${group.id}-title`}>{group.title}</h2><p>{group.summary}</p></div>
            </header>
            <div className="faq-answer-list">
              {group.items.map(([question, answer]) => <details key={question}>
                <summary><strong>{question}</strong><i aria-hidden="true">＋</i></summary>
                <p>{answer}</p>
              </details>)}
            </div>
          </section>)}
        </div>
      </section>
    </div>
  </Shell>;
}

const projectResultCategories = [
  "全部",
  "都市情感",
  "逆袭爽剧",
  "家庭成长",
  "古装穿越",
  "悬疑奇幻",
  "职场商战",
] as const;

type ProjectIconProps = SVGProps<SVGSVGElement>;

function ProjectArrowIcon(props: ProjectIconProps) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="M5 12h13M14 7l5 5-5 5" /></svg>;
}

function ProjectCloseIcon(props: ProjectIconProps) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="m6 6 12 12M18 6 6 18" /></svg>;
}

function ProjectDetailTitle({ title }: { title: string }) {
  const semanticBreak = title.search(/[，：；？！,:;!?]/);
  if (semanticBreak < 0 || semanticBreak === title.length - 1) return <>{title}</>;
  return <><span className="detail-title-clause">{title.slice(0, semanticBreak + 1)}</span><br />{title.slice(semanticBreak + 1)}</>;
}

function ProjectWorkDetail({ project, onClose, onWatch }: { project: ProjectResult; onClose: () => void; onWatch: () => void }) {
  return (
    <div className="detail-backdrop" role="presentation" onMouseDown={(event: MouseEvent<HTMLDivElement>) => event.target === event.currentTarget && onClose()}>
      <section className="detail-panel" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <button className="detail-close" type="button" onClick={onClose} aria-label="关闭项目简介"><ProjectCloseIcon /></button>
        <div className="detail-cover"><img src={project.coverUrl} alt={`《${project.title}》封面`} /></div>
        <div className="detail-copy">
          <span className="type-pill">{project.category}</span>
          <p className="detail-eyebrow">{project.tags.slice(0, 2).join(" · ") || "完整短剧项目"}</p>
          <h2 id="detail-title"><ProjectDetailTitle title={project.title} /></h2>
          <p className="detail-meta">约 {project.durationMinutes} 分钟</p>
          <p className="detail-summary">{project.description}</p>
          <div className="detail-note"><i /> 当前页面展示课程项目简介，完整成片请前往繁星视频 App 观看。</div>
          <button className="primary-button" type="button" onClick={onWatch}>去繁星视频 App 里观看 <ProjectArrowIcon /></button>
        </div>
      </section>
    </div>
  );
}

function ProjectResultsPage() {
  const projects = projectResultsCatalog.projects as ProjectResult[];
  const [activeCategory, setActiveCategory] = useState<(typeof projectResultCategories)[number]>("全部");
  const [selectedProject, setSelectedProject] = useState<ProjectResult | null>(null);
  const categoryCounts = useMemo(
    () => Object.fromEntries(
      projectResultCategories.map((category) => [
        category,
        category === "全部"
          ? projects.length
          : projects.filter((project) => project.category === category).length,
      ]),
    ) as Record<(typeof projectResultCategories)[number], number>,
    [projects],
  );
  const visibleProjects = useMemo(
    () => activeCategory === "全部"
      ? projects
      : projects.filter((project) => project.category === activeCategory),
    [activeCategory, projects],
  );

  useEffect(() => {
    document.body.classList.toggle("overlay-open", Boolean(selectedProject));
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedProject(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("overlay-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedProject]);

  const openFanxingVideo = () => {
    setSelectedProject(null);
    window.open("https://v.fanxing.io/#download", "_blank", "noopener,noreferrer");
  };

  return <Shell>
    <div className="project-results-page">
      <section className="project-results-hero section-pad" aria-labelledby="project-results-title">
        <p className="results-index">PROJECT RESULTS / 项目成果</p>
        <h1 id="project-results-title">完整短剧项目</h1>
        <p className="results-hero-copy">从题材、剧本、人物资产到完整成片，看看课程真正要完成的项目形态。</p>
        <div className="results-hero-meta" aria-label="项目成果概览">
          <span><strong>{projects.length}</strong> 个项目</span>
          <span><strong>6</strong> 个题材方向</span>
          <span><strong>100—120</strong> 分钟</span>
        </div>
      </section>

      <section className="project-results-library section-pad" aria-label="项目成果列表">
        <h2 className="sr-only">按题材查看</h2>
        <div className="results-filters" role="group" aria-label="按题材筛选项目成果">
          {projectResultCategories.map((category) => <button
            key={category}
            type="button"
            className={activeCategory === category ? "is-active" : ""}
            aria-pressed={activeCategory === category}
            onClick={() => setActiveCategory(category)}
          >
            <span>{category}</span>
            <small>{categoryCounts[category]}</small>
          </button>)}
        </div>

        <div className="results-grid" aria-live="polite">
          {visibleProjects.map((project, index) => <ProjectResultCard key={project.id} project={project} index={index} onSelect={setSelectedProject} />)}
        </div>

        <p className="results-data-note">分钟数为首版展示数据，正式对外前将按项目验收后的实际成片时长更新。</p>
      </section>
      {selectedProject ? <ProjectWorkDetail project={selectedProject} onClose={() => setSelectedProject(null)} onWatch={openFanxingVideo} /> : null}
    </div>
  </Shell>;
}

function InternalAccessGate({ role, userId, children }: { role: Role; userId?: string; children: ReactNode }) {
  const [state, setState] = useState<"checking" | "denied" | "allowed">(() => sessionCanEnter(verifiedLoginSession, role, userId) ? "allowed" : "checking");
  const [message, setMessage] = useState("正在核验繁星账号与课程权限…");
  const [loginOpen, setLoginOpen] = useState(false);

  const acceptSession = useCallback((session: LoginSession) => {
    setLoginOpen(false);
    if (!session.availablePortals.includes(role)) {
      setState("denied");
      setMessage(`当前账号没有${roleNames[role]}权限。`);
      return;
    }
    if (userId && session.user.id.toLowerCase() !== userId.toLowerCase()) {
      setState("denied");
      setMessage("当前登录账号与该训练营空间不一致。");
      return;
    }
    setState("allowed");
  }, [role, userId]);

  useEffect(() => {
    const controller = new AbortController();
    let retryTimer = 0;
    let retries = 0;
    const returnToLogin = () => {
      pendingProtectedPath = window.location.pathname;
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
    const verify = () => {
      const stored = readLoginSession();
      if (!stored) { returnToLogin(); return; }
      api<LoginSession>("/api/auth/session", { signal: controller.signal }).then((payload) => {
        if (controller.signal.aborted) return;
        const verified = normalizeLoginSession({ ...stored, ...payload });
        if (!verified) throw new Error("INVALID_LOGIN_SESSION");
        persistLoginSession(verified);
        acceptSession(verified);
      }).catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof ApiRequestError && error.status === 403) {
          setState("denied");
          setMessage(error.message);
        } else if (error instanceof ApiRequestError && error.status === 401) {
          clearLoginSession();
          returnToLogin();
        } else {
          // A temporary identity/API outage is not a sign-out. Keep the
          // protected workspace unmounted and retry without showing a login page.
          setState("checking");
          setMessage("身份服务暂时不可用，正在重新校验…");
          retryTimer = window.setTimeout(verify, Math.min(2_000 * 2 ** retries++, 15_000));
        }
      });
    };
    verify();
    return () => { controller.abort(); window.clearTimeout(retryTimer); };
  }, [acceptSession]);

  if (state === "allowed" && sessionCanEnter(verifiedLoginSession, role, userId)) return <>{children}</>;
  if (state === "checking") return <div className="internal-access-checking" role="status"><span aria-hidden /><span>{message}</span></div>;
  return <div className="internal-access-checking internal-access-denied">
    <section className="internal-access-notice" role="alert">
      <h2>没有访问权限</h2><p>{message}</p>
      <button type="button" onClick={() => setLoginOpen(true)}>切换账号</button>
      <button type="button" onClick={() => { window.history.replaceState({}, "", "/"); window.dispatchEvent(new PopStateEvent("popstate")); }}>返回首页</button>
    </section>
    {loginOpen ? <XingxunLoginDialog onClose={() => setLoginOpen(false)} onAuthenticated={acceptSession} /> : null}
  </div>;
}

function App() {
  const pathname = usePathname();
  const [status, setStatus] = useState<StatusPayload | null>(null);
  useEffect(() => { api<{ ok: boolean } & StatusPayload>("/api/status").then(setStatus).catch(() => undefined); }, []);
  const teacherMatch = pathname.match(/^\/teacher\/([0-9a-f-]{36})(?:\/([a-z]+))?$/i);
  const demonstrationMatch = pathname.match(/^\/teacher\/([0-9a-f-]{36})\/(scripts|dramas)\/[0-9a-f-]{36}(?:\/[a-z-]+)?$/i);
  const teacherTodayMatch = pathname.match(/^\/teacher\/([0-9a-f-]{36})\/today\/(morning|afternoon|evening)\/([1-4])$/i);
  const teacherPlanMatch = pathname.match(/^\/teacher\/([0-9a-f-]{36})\/plan\/(morning|afternoon|evening)(?:\/w([1-8])(?:\/d(0?[1-9]|[1-3][0-9]|40))?)?$/i);
  const teacherOutlineMatch = pathname.match(/^\/teacher\/([0-9a-f-]{36})\/outline(?:\/(a|b))?\/w([1-8])(?:\/d(0?[1-9]|[1-3][0-9]|40)(?:\/h([1-4]))?)?$/i);
  const studentMatch = pathname.match(/^\/student\/([0-9a-f-]{36})(?:\/([a-z]+))?$/i);
  const publicStudentMatch = pathname.match(/^\/u\/([0-9a-f-]{36})$/i);
  const admissionsMatch = pathname.match(/^\/admissions(?:\/(members))?$/i);
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => document.querySelector(hash)?.scrollIntoView({ block: "start" }));
    });
    return () => { window.cancelAnimationFrame(firstFrame); window.cancelAnimationFrame(secondFrame); };
  }, [pathname]);
  if (pathname === "/") return <HomePage />;
  if (pathname === "/plan") return <CoursePage />;
  if (pathname === "/teach") return <TeachingMethodPage />;
  if (pathname === "/work") return <ProjectResultsPage />;
  if (pathname === "/me") return <AboutTeacherPage />;
  if (pathname === "/faq") return <FaqPage />;
  if (pathname === "/ask") return <ContactPage status={status} />;
  if (admissionsMatch) return <InternalAccessGate role="admissions"><AdmissionsPage /></InternalAccessGate>;
  if (demonstrationMatch) return <InternalAccessGate role="teacher" userId={demonstrationMatch[1]}><TeacherPage userId={demonstrationMatch[1]} view={demonstrationMatch[2] as 'scripts' | 'dramas'} /></InternalAccessGate>;
  if (teacherTodayMatch) return <InternalAccessGate role="teacher" userId={teacherTodayMatch[1]}><TeacherPage userId={teacherTodayMatch[1]} view="today" todayCohort={teacherTodayMatch[2] as TeachingCohortId} todaySession={Number(teacherTodayMatch[3])} /></InternalAccessGate>;
  if (teacherPlanMatch) {
    const week = teacherPlanMatch[3] ? Number(teacherPlanMatch[3]) : undefined;
    const day = teacherPlanMatch[4] ? Number(teacherPlanMatch[4]) : undefined;
    if (!day || Math.ceil(day / 5) === week) return <InternalAccessGate role="teacher" userId={teacherPlanMatch[1]}><TeacherPage userId={teacherPlanMatch[1]} view="course" planCohort={teacherPlanMatch[2] as TeachingCohortId} planWeek={week} planDay={day} /></InternalAccessGate>;
  }
  if (teacherOutlineMatch) {
    const caseId = (teacherOutlineMatch[2]?.toLowerCase() || "a") as TeachingCaseId;
    const week = Number(teacherOutlineMatch[3]);
    const day = teacherOutlineMatch[4] ? Number(teacherOutlineMatch[4]) : undefined;
    const hour = teacherOutlineMatch[5] ? Number(teacherOutlineMatch[5]) : undefined;
    if (!day || Math.ceil(day / 5) === week) return <InternalAccessGate role="teacher" userId={teacherOutlineMatch[1]}><TeacherPage userId={teacherOutlineMatch[1]} view="outline" outlineCase={caseId} outlineWeek={week} outlineDay={day} outlineHour={hour} /></InternalAccessGate>;
  }
  if (teacherMatch) {
    const view = portalViewFromSlug("teacher", teacherMatch[2]);
    if (view) return <InternalAccessGate role="teacher" userId={teacherMatch[1]}><TeacherPage userId={teacherMatch[1]} view={view} /></InternalAccessGate>;
  }
  if (studentMatch) {
    const view = portalViewFromSlug("student", studentMatch[2]);
    if (view) return <InternalAccessGate role="student" userId={studentMatch[1]}><StudentPage status={status} userId={studentMatch[1]} view={view} /></InternalAccessGate>;
  }
  if (publicStudentMatch) return <PublicStudentPage userId={publicStudentMatch[1]} />;
  return <NotFoundPage />;
}

const root = document.getElementById("root");
if (!root) throw new Error("DUANJU.SCHOOL root element is missing");
createRoot(root).render(<App />);
